import React, { useState, useRef, useEffect } from "react"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { post, del } from "../../helpers/api_helper"
import { okara_api } from "../../utils/variables"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import { usePlatforms } from "../../contexts/PlatformsContext"
import { isBlurredField, BlurredInline, BlurUpgradeContext } from "../Report/components/BlurredField"
import ActionsFeedPanel from "./ActionsFeedPanel"
import "./AppScanner.css"
import AppsTrackingPlanModal from "../../components/Common/Modal/AppsTrackingPlanModal"
import KeywordSuggestionsDetailsModal from "../../components/Common/Modal/KeywordSuggestionsDetailsModal"
import BackButton from "../../components/Common/BackButton"
import SEO from "../../components/Common/SEO"
import { generateCanonicalUrl } from "../../utils/seoUtils"
import { PAGE_SEO } from "../../config/seoConfig"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"
import html2pdf from "html2pdf.js"
import {
  Shield,
  TrendingUp,
  Users,
  Download,
  DollarSign,
  Wallet,
  Star,
  Gamepad2,
  Globe,
  ExternalLink,
  Bot,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  BarChart3,
  PieChart,
  MessageSquare,
  Heart,
  Eye,
  Target,
  AlertTriangle,
  XCircle,
  Rss,
  Send,
  X,
  ArrowUpRight,
  ArrowDownRight,
  FileText,
  Brain,
  CircleDot,
  ShieldCheck,
  ShieldAlert,
  Zap,
  ChevronDown,
  ChevronLeft,
  Search,
  Layers,
  ThumbsUp,
  Building2,
  Clock,
  Sun,
  Calendar,
  Newspaper,
  Tag,
  Table,
  Lightbulb,
  Flag,
  TrendingDown,
  Database,
  Sparkles
} from "lucide-react"
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Pie,
  Cell,
} from "recharts"
import metalogo from "../../assets/images/meta-logo.png"

// Module-level cache that persists across component re-renders
const appDataCache = new Map()

// Module-level recently opened apps that persists across component re-renders
let recentlyOpenedApps = []

// Helper function to format numbers in K or M format
const formatNumber = num => {
  if (!num) return "0"
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1
  }).format(num)
}

// Guards downloads/revenue/review-volume metrics against the backend's
// {blurred: true, ...} redaction descriptor sent for guest/lower-tier
// viewers — that's an object, not a number, so a bare `.toFixed()` call on
// it throws instead of falling through to a null/undefined check. Renders
// the same locked-pill treatment used elsewhere in the report (BlurredInline)
// rather than a flat "N/A", which would read as missing data instead of
// gated data. "N/A" is reserved for genuinely absent values.
const formatMetricMagnitude = (value, prefix = "", { size = "xs", interactive = true } = {}) => {
  if (isBlurredField(value)) return <BlurredInline field={value} size={size} interactive={interactive} />
  if (typeof value !== "number") return "N/A"
  if (value >= 1000000) return `${prefix}${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `${prefix}${(value / 1000).toFixed(1)}K`
  return `${prefix}${value.toFixed(0)}`
}

// Same guard for a plain fixed-decimal metric (rating average, KU score,
// etc.), with an optional prefix/suffix composed only on the real-value path
// — a blurred value renders the lock pill on its own, never "$[object]".
// `size`/`interactive` default to "xs", BlurredInline's smallest pill (just
// a lock icon + "Unlock", no placeholder word) — these cards are as narrow
// as ~70px, where even the "sm" pill's placeholder text overflows; pass a
// bigger size explicitly where there's more room (e.g. the wider marketing
// strategy stat cards).
const formatMetricDecimal = (value, decimals = 2, { prefix = "", suffix = "", size = "xs", interactive = true } = {}) => {
  if (isBlurredField(value)) return <BlurredInline field={value} size={size} interactive={interactive} />
  if (typeof value !== "number") return "N/A"
  return `${prefix}${value.toFixed(decimals)}${suffix}`
}

// Plain-text variant for the markdown/PDF export, which can't render JSX —
// falls back to the backend's own message instead of a rendered lock pill.
const formatMetricDecimalText = (value, decimals = 2) => {
  if (isBlurredField(value)) return value.message || "Locked — available on a paid plan"
  return typeof value === "number" ? value.toFixed(decimals) : "N/A"
}

// StatCard component for displaying statistics
const StatCard = ({ label, value, icon: Icon, trend }) => (
  <div
    className="pdf-no-break"
    style={{
      backgroundColor: "rgba(55, 65, 81, 0.5)",
      borderRadius: "10px",
      padding: "16px",
      border: "1px solid rgba(55, 65, 81, 0.6)",
      transition: "all 0.2s ease",
      pageBreakInside: "avoid",
    }}
  >
    <div className="d-flex justify-content-between align-items-start mb-2">
      <p
        style={{
          fontSize: "11px",
          color: "#9ca3af",
          marginBottom: "4px",
          margin: 0,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          fontWeight: "500",
        }}
      >
        {label}
      </p>
      {Icon && <Icon style={{ width: "14px", height: "14px", color: "#9ca3af" }} />}
    </div>
    <div className="d-flex align-items-center gap-2">
      <p
        style={{
          fontSize: "20px",
          fontWeight: "700",
          color: "#ffffff",
          margin: "4px 0 0 0",
          letterSpacing: "-0.5px",
        }}
      >
        {value}
      </p>
      {trend && (
        <span
          style={{
            fontSize: "10px",
            padding: "2px 6px",
            borderRadius: "4px",
            backgroundColor: trend === "up" ? "rgba(16, 185, 129, 0.2)" : "rgba(239, 68, 68, 0.2)",
            color: trend === "up" ? "#10b981" : "#ef4444",
            fontWeight: "600",
          }}
        >
          {trend === "up" ? <ArrowUpRight style={{ width: "10px", height: "10px", display: "inline" }} /> : <ArrowDownRight style={{ width: "10px", height: "10px", display: "inline" }} />}
        </span>
      )}
    </div>
  </div>
)

// InfoCard component for displaying information with label, value, and subtext
const InfoCard = ({ label, value, subtext, color = "#F6255A", icon: Icon }) => (
  <div className="pdf-no-break"
    style={{ background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
      borderRadius: "12px",
      padding: "16px",
      border: "1px solid rgba(55, 65, 81, 0.6)",
      transition: "all 0.2s ease",
      pageBreakInside: "avoid",
    }}
  >
    <div className="d-flex justify-content-between align-items-start mb-2">
      <p
        style={{
          fontSize: "11px",
          color: "#9ca3af",
          marginBottom: "4px",
          margin: 0,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          fontWeight: "500",
        }}
      >
        {label}
      </p>
      {Icon && <Icon style={{ width: "14px", height: "14px", color: color || "#9ca3af" }} />}
    </div>
    <p
      style={{
        fontSize: "16px",
        fontWeight: "700",
        color: color,
        marginBottom: "4px",
        margin: "4px 0",
        letterSpacing: "-0.5px",
      }}
    >
      {value}
    </p>
    {subtext && (
      <p
        style={{
          fontSize: "12px",
          color: "#9ca3af",
          lineHeight: "1.5",
          margin: "4px 0 0 0",
        }}
      >
        {subtext}
      </p>
    )}
  </div>
)

// ActionItem component for displaying action items
const ActionItem = ({ action }) => (
  <div
    className="pdf-no-break"
    style={{
      padding: "16px",
      background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
      borderRadius: "12px",
      marginBottom: "12px",
      border: "1px solid rgba(55, 65, 81, 0.6)",
      pageBreakInside: "avoid",
      transition: "all 0.2s ease",
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: "8px",
      }}
    >
      <p
        style={{
          fontSize: "14px",
          fontWeight: "600",
          color: "#ffffff",
          marginBottom: "4px",
          margin: 0,
          lineHeight: "1.4",
          flex: 1,
        }}
      >
        {action.action}
      </p>
      <span
        style={{
          fontSize: "10px",
          padding: "4px 8px",
          borderRadius: "6px",
          backgroundColor:
            action.effort === "High"
              ? "rgba(239, 68, 68, 0.2)"
              : action.effort === "Medium"
                ? "rgba(245, 158, 11, 0.2)"
                : "rgba(16, 185, 129, 0.2)",
          color:
            action.effort === "High"
              ? "#ef4444"
              : action.effort === "Medium"
                ? "#f59e0b"
                : "#10b981",
          fontWeight: "700",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {action.effort}
      </span>
    </div>
    <div style={{ fontSize: "12px", color: "#9ca3af", display: "flex", gap: "16px", marginBottom: "6px", flexWrap: "wrap", rowGap: "4px" }}>
      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <Users style={{ width: "12px", height: "12px" }} />
        Owner: {action.owner}
      </span>
      {action.kpi && (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <Target style={{ width: "12px", height: "12px" }} />
          KPI: {action.kpi}
        </span>
      )}
    </div>
    {action.expected_impact && (
      <p
        style={{
          fontSize: "12px",
          color: "#10b981",
          margin: "8px 0 0 0",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          fontWeight: "500",
        }}
      >
        <ArrowUpRight style={{ width: "12px", height: "12px" }} />
        Impact: {action.expected_impact}
      </p>
    )}
  </div>
)

// Helper function to format marketing strategy as markdown for copying
const formatStrategyAsMarkdown = strategy => {
  let markdown = "# Digital Marketing Strategy\n\n"

  if (strategy.product_context) {
    markdown += "## Product Context\n\n"
    markdown += `- **App Name:** ${strategy.product_context.app_name}\n`
    markdown += `- **Developer:** ${strategy.product_context.developer}\n`
    if (strategy.product_context.genre?.length > 0) {
      markdown += `- **Genre:** ${strategy.product_context.genre.join(", ")}\n`
    }
    if (strategy.product_context.supported_platforms?.length > 0) {
      markdown += `- **Supported Platforms:** ${strategy.product_context.supported_platforms.join(", ")}\n`
    }
    markdown += `- **Pricing:** ${strategy.product_context.is_free ? "Free" : "Paid"}\n\n`
  }

  if (strategy.executive_summary) {
    markdown += "## Executive Summary\n\n"
    markdown += `**${strategy.executive_summary.headline}**\n\n`
    markdown += `${strategy.executive_summary.overview}\n\n`

    if (strategy.executive_summary.key_stats) {
      markdown += "### Key Statistics\n\n"
      const stats = strategy.executive_summary.key_stats
      markdown += `- Downloads: ${formatNumber(stats.total_downloads)}\n`
      markdown += `- Revenue: $${formatNumber(stats.total_revenue_usd)}\n`
      markdown += `- Rating: ${formatMetricDecimalText(stats.avg_rating, 2)}\n`
      markdown += `- Reviews: ${formatNumber(stats.total_reviews)}\n\n`
    }

    markdown += `**Market Position:** ${strategy.executive_summary.market_position}\n`
    markdown += `${strategy.executive_summary.market_position_rationale}\n\n`

    markdown += `**Urgency:** ${strategy.executive_summary.urgency}\n`
    markdown += `${strategy.executive_summary.urgency_rationale}\n\n`

    if (strategy.executive_summary.biggest_opportunity) {
      markdown += `**Biggest Opportunity:** ${strategy.executive_summary.biggest_opportunity}\n\n`
    }
    if (strategy.executive_summary.biggest_threat) {
      markdown += `**Biggest Threat:** ${strategy.executive_summary.biggest_threat}\n\n`
    }
  }

  if (strategy.market_analysis) {
    markdown += "## Market Analysis\n\n"

    if (strategy.market_analysis.performance_metrics) {
      markdown += "### Performance Metrics\n\n"
      const metrics = strategy.market_analysis.performance_metrics
      markdown += `- Revenue per Download: $${formatMetricDecimalText(metrics.revenue_per_download, 2)}\n`
      markdown += `- Review Rate: ${formatMetricDecimalText(metrics.review_rate_pct, 2)}%\n\n`
    }

    if (strategy.market_analysis.growth_analysis) {
      markdown += "### Growth Analysis\n\n"
      markdown += `- **Status:** ${strategy.market_analysis.growth_analysis.status}\n`
      markdown += `- **Momentum:** ${strategy.market_analysis.growth_analysis.momentum_signal}\n`
      markdown += `- **Explanation:** ${strategy.market_analysis.growth_analysis.momentum_explanation}\n\n`
    }

    if (strategy.market_analysis.sentiment_analysis) {
      markdown += "### Sentiment Analysis\n\n"
      markdown += `- **Overall:** ${strategy.market_analysis.sentiment_analysis.overall}\n`
      markdown += `- **Positive:** ${strategy.market_analysis.sentiment_analysis.positive_pct}%\n`
      markdown += `- **Negative:** ${strategy.market_analysis.sentiment_analysis.negative_pct}%\n`
      markdown += `- **Neutral:** ${strategy.market_analysis.sentiment_analysis.neutral_pct}%\n\n`

      if (strategy.market_analysis.sentiment_analysis.bug_complaints?.length > 0) {
        markdown += "#### Bug Complaints\n\n"
        strategy.market_analysis.sentiment_analysis.bug_complaints.forEach(bug => {
          markdown += `- **${bug.bug}**\n`
          markdown += `  - Impact: ${bug.impact}\n`
          markdown += `  - Priority: ${bug.priority}\n\n`
        })
      }
    }

    if (strategy.market_analysis.keyword_intelligence) {
      markdown += "### Keyword Intelligence\n\n"

      if (strategy.market_analysis.keyword_intelligence.top_keywords_by_download_volume?.length > 0) {
        markdown += "#### Top Keywords by Download Volume\n\n"
        strategy.market_analysis.keyword_intelligence.top_keywords_by_download_volume.forEach(kw => {
          markdown += `- **${kw.keyword}** - ${formatNumber(kw.estimated_downloads)} downloads (${kw.opportunity_score})\n`
          markdown += `  - Action: ${kw.action}\n\n`
        })
      }

      if (strategy.market_analysis.keyword_intelligence.untapped_keywords?.length > 0) {
        markdown += "#### Untapped Keywords\n\n"
        strategy.market_analysis.keyword_intelligence.untapped_keywords.forEach(kw => {
          markdown += `- **${kw.keyword}**\n`
          markdown += `  - Rationale: ${kw.rationale}\n`
          markdown += `  - Estimated Traffic: ${kw.estimated_traffic}\n\n`
        })
      }

      if (strategy.market_analysis.keyword_intelligence.keyword_gaps_vs_competitors?.length > 0) {
        markdown += "#### Keyword Gaps vs Competitors\n\n"
        strategy.market_analysis.keyword_intelligence.keyword_gaps_vs_competitors.forEach(gap => {
          markdown += `- **${gap.keyword}**\n`
          markdown += `  - Competitor Ranking: ${gap.competitor_ranking}\n`
          markdown += `  - Our Status: ${gap.our_status}\n`
          markdown += `  - Action: ${gap.action}\n\n`
        })
      }
    }

    if (strategy.market_analysis.market_opportunity_score) {
      markdown += "### Market Opportunity Score\n\n"
      markdown += `- **Overall:** ${formatMetricDecimalText(strategy.market_analysis.market_opportunity_score.overall, 1)}/100\n`
      markdown += `- **Rationale:** ${strategy.market_analysis.market_opportunity_score.rationale}\n\n`

      if (strategy.market_analysis.market_opportunity_score.subcategory_scores) {
        markdown += "#### Subcategory Scores\n\n"
        const scores = strategy.market_analysis.market_opportunity_score.subcategory_scores
        markdown += `- ASO Opportunity: ${scores.aso_opportunity}\n`
        markdown += `- Sentiment Opportunity: ${scores.sentiment_opportunity}\n`
        markdown += `- Competitor Gap: ${scores.competitor_gap_opportunity}\n`
        markdown += `- Content Opportunity: ${scores.content_opportunity}\n`
        markdown += `- Monetization Opportunity: ${scores.monetization_opportunity}\n\n`
      }
    }
  }

  if (strategy.positioning) {
    markdown += "## Positioning\n\n"
    markdown += `**Tagline:** "${strategy.positioning.tagline}"\n\n`
    markdown += `**USP:** ${strategy.positioning.usp}\n\n`

    if (strategy.positioning.target_audiences?.length > 0) {
      markdown += "### Target Audiences\n\n"
      strategy.positioning.target_audiences.forEach(audience => {
        markdown += `- **${audience.segment}** (${audience.size_estimate})\n`
        markdown += `  - ${audience.description}\n`
        markdown += `  - Primary Channel: ${audience.primary_channel}\n\n`
      })
    }

    if (strategy.positioning.competitive_advantages?.length > 0) {
      markdown += "### Competitive Advantages\n\n"
      strategy.positioning.competitive_advantages.forEach(adv => {
        markdown += `- **${adv.advantage}**\n`
        markdown += `  - ${adv.evidence}\n\n`
      })
    }
  }

  if (strategy.campaigns?.length > 0) {
    markdown += "## Recommended Campaigns\n\n"
    strategy.campaigns.forEach(campaign => {
      markdown += `### ${campaign.name}\n`
      markdown += `- **Priority:** ${campaign.priority}\n`
      markdown += `- **Goal:** ${campaign.goal}\n`
      markdown += `- **Budget:** ${campaign.budget_estimate}\n`
      markdown += `- **Timeline:** ${campaign.timeline}\n`
      markdown += `- **Core Message:** ${campaign.core_message}\n\n`

      if (campaign.channels?.length > 0) {
        markdown += "**Channels:**\n"
        campaign.channels.forEach(channel => {
          markdown += `- ${channel.channel} (${channel.role}, ${channel.budget_allocation_pct}% budget)\n`
        })
        markdown += "\n"
      }
    })
  }

  if (strategy.seo_aso_strategy) {
    markdown += "## SEO/ASO Strategy\n\n"

    if (strategy.seo_aso_strategy.current_aso_health) {
      markdown += "### Current ASO Health\n\n"
      markdown += `- **Score:** ${strategy.seo_aso_strategy.current_aso_health.score}\n`
      markdown += `- **Strengths:** ${strategy.seo_aso_strategy.current_aso_health.strengths?.join(", ")}\n`
      markdown += `- **Gaps:** ${strategy.seo_aso_strategy.current_aso_health.gaps?.join(", ")}\n\n`
    }

    if (strategy.seo_aso_strategy.title_optimization) {
      markdown += "### Title Optimization\n\n"
      markdown += `- **Current:** ${strategy.seo_aso_strategy.title_optimization.current_title}\n`
      markdown += `- **Recommended:** ${strategy.seo_aso_strategy.title_optimization.recommended_title}\n`
      markdown += `- **Rationale:** ${strategy.seo_aso_strategy.title_optimization.rationale}\n\n`
    }

    if (strategy.seo_aso_strategy.keyword_strategy?.length > 0) {
      markdown += "### Keyword Strategy\n\n"
      strategy.seo_aso_strategy.keyword_strategy.forEach(kw => {
        markdown += `- **${kw.keyword}**\n`
        markdown += `  - Monthly Volume: ${formatNumber(kw.monthly_download_volume)}\n`
        markdown += `  - Placement: ${kw.recommended_placement}\n`
        markdown += `  - Priority: ${kw.priority_rank}\n\n`
      })
    }
  }

  if (strategy.content_strategy) {
    markdown += "## Content Strategy\n\n"

    if (strategy.content_strategy.content_pillars?.length > 0) {
      markdown += "### Content Pillars\n\n"
      strategy.content_strategy.content_pillars.forEach(pillar => {
        markdown += `- **${pillar.pillar}** (${pillar.cadence})\n`
        markdown += `  - Types: ${pillar.content_types?.join(", ")}\n`
        markdown += `  - Rationale: ${pillar.rationale}\n\n`
      })
    }

    if (strategy.content_strategy.viral_hooks?.length > 0) {
      markdown += "### Viral Hooks\n\n"
      strategy.content_strategy.viral_hooks.forEach(hook => {
        markdown += `- **${hook.hook}**\n`
        markdown += `  - Mechanic: ${hook.mechanic}\n`
        markdown += `  - Platform: ${hook.platform}\n\n`
      })
    }
  }

  if (strategy.retention_and_growth) {
    markdown += "## Retention and Growth\n\n"

    if (strategy.retention_and_growth.current_retention_health) {
      markdown += "### Current Retention Health\n\n"
      markdown += `- **Assessment:** ${strategy.retention_and_growth.current_retention_health.assessment}\n`
      markdown += `- **Evidence:** ${strategy.retention_and_growth.current_retention_health.evidence}\n`
      markdown += `- **Churn Rate:** ${strategy.retention_and_growth.current_retention_health.churn_rate_estimate}\n\n`
    }

    if (strategy.retention_and_growth.churn_risks?.length > 0) {
      markdown += "### Churn Risks\n\n"
      strategy.retention_and_growth.churn_risks.forEach(risk => {
        markdown += `- **${risk.risk}** (${risk.severity})\n`
        markdown += `  - Impact: ${risk.impact}\n`
        markdown += `  - Mitigation: ${risk.mitigation}\n\n`
      })
    }
  }

  if (strategy.risk_register?.length > 0) {
    markdown += "## Risk Register\n\n"
    strategy.risk_register.forEach(risk => {
      markdown += `- **${risk.risk}**\n`
      markdown += `  - Likelihood: ${risk.likelihood}\n`
      markdown += `  - Impact: ${risk.impact}\n`
      markdown += `  - Trigger: ${risk.trigger}\n`
      markdown += `  - Mitigation: ${risk.mitigation}\n`
      markdown += `  - Contingency: ${risk.contingency}\n\n`
    })
  }

  if (strategy.success_metrics_dashboard) {
    markdown += "## Success Metrics Dashboard\n\n"
    markdown += `- **North Star Metric:** ${strategy.success_metrics_dashboard.north_star_metric}\n`
    markdown += `- **Review Cadence:** ${strategy.success_metrics_dashboard.review_cadence}\n\n`

    if (strategy.success_metrics_dashboard.primary_kpis?.length > 0) {
      markdown += "### Primary KPIs\n\n"
      strategy.success_metrics_dashboard.primary_kpis.forEach(kpi => {
        markdown += `- **${kpi.kpi}**\n`
        markdown += `  - Current: ${kpi.current_value}\n`
        markdown += `  - 30d Target: ${kpi["30_day_target"]}\n`
        markdown += `  - 90d Target: ${kpi["90_day_target"]}\n`
        markdown += `  - Data Source: ${kpi.data_source}\n\n`
      })
    }
  }

  if (strategy.data_sources_used?.length > 0) {
    markdown += "## Data Sources Used\n\n"
    strategy.data_sources_used.forEach(source => {
      markdown += `- **${source.tool}**\n`
      markdown += `  - Data Retrieved: ${source.data_retrieved}\n`
      markdown += `  - Status: ${source.status}\n\n`
    })
  }

  if (strategy.action_feed) {
    markdown += "## Action Feed\n\n"

    if (strategy.action_feed.north_star_goal) {
      markdown += `**North Star Goal:** ${strategy.action_feed.north_star_goal}\n\n`
    }

    if (strategy.action_feed.week_1?.length > 0) {
      markdown += "### Week 1\n\n"
      strategy.action_feed.week_1.forEach(action => {
        markdown += `- ${action.action} (${action.effort}) - ${action.owner}\n`
        if (action.expected_impact) {
          markdown += `  - Impact: ${action.expected_impact}\n`
        }
        if (action.kpi) {
          markdown += `  - KPI: ${action.kpi}\n`
        }
      })
      markdown += "\n"
    }

    if (strategy.action_feed.month_1?.length > 0) {
      markdown += "### Month 1\n\n"
      strategy.action_feed.month_1.forEach(action => {
        markdown += `- ${action.action} (${action.effort}) - ${action.owner}\n`
        if (action.expected_impact) {
          markdown += `  - Impact: ${action.expected_impact}\n`
        }
        if (action.kpi) {
          markdown += `  - KPI: ${action.kpi}\n`
        }
      })
      markdown += "\n"
    }

    if (strategy.action_feed.quarter_1?.length > 0) {
      markdown += "### Quarter 1\n\n"
      strategy.action_feed.quarter_1.forEach(action => {
        markdown += `- ${action.action} (${action.effort}) - ${action.owner}\n`
        if (action.expected_impact) {
          markdown += `  - Impact: ${action.expected_impact}\n`
        }
        if (action.success_metric) {
          markdown += `  - Success Metric: ${action.success_metric}\n`
        }
      })
      markdown += "\n"
    }
  }

  if (strategy.budget_allocation) {
    markdown += "## Budget Allocation\n\n"
    markdown += `**Total Budget:** ${strategy.budget_allocation.total_budget_recommendation}\n\n`
    markdown += `${strategy.budget_allocation.rationale}\n\n`

    if (strategy.budget_allocation.breakdown?.length > 0) {
      markdown += "### Breakdown\n\n"
      strategy.budget_allocation.breakdown.forEach(item => {
        markdown += `- **${item.category}** (${item.allocation_pct}%): ${item.monthly_estimate}\n`
        markdown += `  - ${item.rationale}\n\n`
      })
    }
  }

  if (strategy.social_media_intelligence) {
    markdown += "## Social Media Intelligence\n\n"
    markdown += `**Status:** ${strategy.social_media_intelligence.status}\n\n`

    if (strategy.social_media_intelligence.platform_opportunities?.length > 0) {
      markdown += "### Platform Opportunities\n\n"
      strategy.social_media_intelligence.platform_opportunities.forEach(opportunity => {
        markdown += `- **${opportunity.platform}**\n`
        markdown += `  - ${opportunity.insight}\n`
        if (opportunity.competitors_count > 0) {
          markdown += `  - Competitors: ${opportunity.good_presence} Good, ${opportunity.weak_presence} Low/Medium\n\n`
        } else {
          markdown += `  - No competitors present\n\n`
        }
      })
    }

    if (strategy.social_media_intelligence.competitor_social_presence?.length > 0) {
      markdown += "### Competitor Social Presence\n\n"
      strategy.social_media_intelligence.competitor_social_presence.slice(0, 3).forEach(competitor => {
        markdown += `- **${competitor.competitor}** (${competitor.publisher_name})\n`
        if (competitor.platforms?.length > 0) {
          competitor.platforms.forEach(platform => {
            markdown += `  - ${platform.platform}: ${platform.presence} (${formatNumber(platform.avg_followers)} followers)\n`
          })
        }
        markdown += "\n"
      })
    }

    if (strategy.social_media_intelligence.summary) {
      markdown += `**Summary:** ${strategy.social_media_intelligence.summary}\n\n`
    }
  }

  if (strategy.competitor_ad_intelligence?.length > 0) {
    markdown += "## Competitor Ad Intelligence\n\n"
    strategy.competitor_ad_intelligence.forEach(adIntel => {
      markdown += `### ${adIntel.name}\n\n`
      markdown += `- **Threat Level:** ${adIntel.threat_level}\n`
      markdown += `- **Ad Count:** ${adIntel.ad_count}\n`
      markdown += `- **Active Ads:** ${adIntel.has_active_ads ? "Yes" : "No"}\n`
      markdown += `- **Platforms:** ${adIntel.platforms?.length || 0}\n\n`

      if (adIntel.unique_strategy) {
        markdown += `**Strategy:** ${adIntel.unique_strategy}\n\n`
      }
      if (adIntel.counter_strategy) {
        markdown += `**Counter Strategy:** ${adIntel.counter_strategy}\n\n`
      }
      if (adIntel.opportunity) {
        markdown += `**Opportunity:** ${adIntel.opportunity}\n\n`
      }
    })
  }

  if (strategy.ads_fetch_report?.length > 0) {
    markdown += "## Ads Data Collection Report\n\n"
    strategy.ads_fetch_report.forEach(report => {
      markdown += `- **${report.app_name}**\n`
      markdown += `  - Ads: ${report.ad_count}\n`
      markdown += `  - Active: ${report.active_ad_count}\n`
      markdown += `  - Status: ${report.has_error ? "Error" : "Success"}\n\n`
    })
  }

  if (strategy.enterprise_intelligence_report) {
    markdown += "## Enterprise Intelligence Report\n\n"
    markdown += `**Document Type:** ${strategy.enterprise_intelligence_report.document_type}\n`
    markdown += `**Generated By:** ${strategy.enterprise_intelligence_report.generated_by}\n`
    markdown += `**Version:** ${strategy.enterprise_intelligence_report.version}\n`
    markdown += `**Generated At:** ${strategy.enterprise_intelligence_report.generated_at}\n\n`

    if (strategy.enterprise_intelligence_report.product_context) {
      markdown += "### Product Context\n\n"
      markdown += `- **Product Name:** ${strategy.enterprise_intelligence_report.product_context.product_name}\n`
      markdown += `- **Category:** ${strategy.enterprise_intelligence_report.product_context.category}\n`
      markdown += `- **Platform:** ${strategy.enterprise_intelligence_report.product_context.platform}\n`
      markdown += `- **Summary:** ${strategy.enterprise_intelligence_report.product_context.summary}\n\n`
    }

    if (strategy.enterprise_intelligence_report.competitor_intelligence?.length > 0) {
      markdown += "### Competitor Intelligence\n\n"
      strategy.enterprise_intelligence_report.competitor_intelligence.forEach(comp => {
        markdown += `- **${comp.name}**\n`
        markdown += `  - Keywords: ${comp.key_seo_keywords?.join(", ")}\n`
        if (comp.ai_insights?.length > 0) {
          markdown += `  - AI Insights: ${comp.ai_insights.join(", ")}\n`
        }
        markdown += "\n"
      })
    }

    if (strategy.enterprise_intelligence_report.final_ai_evaluation) {
      markdown += "### Final AI Evaluation\n\n"
      const eval_ = strategy.enterprise_intelligence_report.final_ai_evaluation
      markdown += `- **Enterprise Potential:** ${eval_.enterprise_potential}\n`
      markdown += `- **Market Opportunity:** ${eval_.market_opportunity}\n`
      markdown += `- **Competition Density:** ${eval_.competition_density}\n`
      markdown += `- **Investor Narrative:** ${eval_.investor_narrative}\n\n`
    }
  }

  return markdown
}

// Helper function to download strategy as PDF file
const downloadStrategyAsPDF = async (strategy, appName, setIsGeneratingPDF) => {
  try {
    setIsGeneratingPDF(true)
    toastr.info("Generating PDF, please wait...")

    const element = document.getElementById("marketing-strategy-content")
    if (!element) {
      toastr.error("Could not find strategy content to generate PDF")
      return
    }

    const fileName = `marketing_strategy_${appName?.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.pdf`

    const opt = {
      margin:       10,
      filename:     fileName,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true, logging: false },
      jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak:    { 
        mode: ['css', 'legacy'],
        avoid: ['.pdf-no-break', 'p', 'h6', 'h5', 'h4', 'h3', 'h2', 'h1', 'li']
      }
    }

    await html2pdf().set(opt).from(element).save()
    
    toastr.success("PDF downloaded successfully!")

  } catch (error) {
    console.error("Error generating PDF", error)
    toastr.error("Failed to generate PDF")
  } finally {
    setIsGeneratingPDF(false)
  }
}

// Policy fields come back as snake_case keys (e.g. "multiplayer_safety_details")
// — turn them into a readable label ("Multiplayer safety details").
const humanizeFieldName = value => {
  if (typeof value !== "string") return value
  const text = value.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim()
  return text.charAt(0).toUpperCase() + text.slice(1)
}

// Helper function to create slug from app name
const createSlug = name => {
  if (!name) return ""

  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "") // Remove special characters except spaces
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, "") // Remove leading/trailing hyphens
    .trim()
}

// Helper function to determine platform from URL using dynamic platforms data
const getPlatformInfo = (
  url,
  platforms,
  getPlatformIcon,
  getPlatformDisplayName,
  appData,
) => {
  if (!platforms.length) return null

  const lowerUrl = (url || '').toLowerCase()

  // Domain-specific aliases that avoid false positives.
  // "quest" is intentionally excluded — it's a substring of "keyquest.io" which is
  // the internal URL format, so it would match every app as Meta.
  const platformAliases = {
    meta: ["meta.com", "oculus"],
    steam: ["steampowered.com", "store.steam"],
    apple_vision: ["apps.apple.com", "itunes.apple.com"],
    pico: ["picoxr.com", "picovr.com"],
  }

  const buildResult = (platform) => {
    let logoSrc = getPlatformIcon ? getPlatformIcon(platform.id) : platform.icon_url
    if (platform.id === "meta") logoSrc = metalogo

    let platformUrl = url
    if (platform.id === "meta" && appData?.app_id) {
      platformUrl = `https://www.meta.com/experiences/${appData.app_id}`
    } else if (platform.id === "steam") {
      const rawId = String(appData?.app_id || appData?.game_info?.app_id || "")
      const steamId = rawId.replace(/^steam_/i, "")
      if (steamId) platformUrl = `https://store.steampowered.com/app/${steamId}`
    } else if (platform.id === "apple_vision") {
      const rawId = String(appData?.app_id || appData?.game_info?.app_id || "")
      const appleId = rawId.replace(/^apple_vision_/i, "")
      if (appleId) platformUrl = `https://apps.apple.com/app/id${appleId}`
    } else if (platform.id === "pico") {
      const rawId = String(appData?.app_id || appData?.game_info?.app_id || "")
      const picoId = rawId.replace(/^pico_/i, "")
      if (/^\d+$/.test(picoId)) platformUrl = `https://store-global.picoxr.com/global/detail/1/${picoId}`
    }

    return {
      name: getPlatformDisplayName
        ? getPlatformDisplayName(platform.id)
        : platform.display_name || platform.name,
      logo: logoSrc,
      url: platformUrl,
      id: platform.id,
    }
  }

  // Primary: match against actual store URLs (e.g. meta.com, steampowered.com)
  if (lowerUrl && !lowerUrl.includes("keyquest.io")) {
    for (const platform of platforms) {
      const aliases = platformAliases[platform.id] || []
      if (
        lowerUrl.includes(platform.id.toLowerCase()) ||
        aliases.some(alias => lowerUrl.includes(alias))
      ) {
        return buildResult(platform)
      }
    }
  }

  // Fallback for keyquest.io URLs (internal format used for all apps).
  const appId = String(appData?.app_id || appData?.game_info?.app_id || "")

  // Trust an explicit platform from the payload before guessing from the id.
  const explicitPlatform = String(appData?.game_info?.platform || appData?.platform || "").toLowerCase()
  if (explicitPlatform) {
    const matched = platforms.find(p => p.id === explicitPlatform)
    if (matched) return buildResult(matched)
  }

  // app_id is prefixed with its platform for Steam, Apple Vision and Pico
  // (e.g. "steam_12345", "apple_vision_67890", "pico_7472678219464949801") —
  // check that before falling back to the digit-length heuristic below, which
  // can't tell them apart (Pico IDs are 19 digits, so they'd read as Meta).
  if (/^pico_/i.test(appId)) {
    const picoPlatform = platforms.find(p => p.id === "pico")
    if (picoPlatform) return buildResult(picoPlatform)
  }
  if (/^apple_vision_/i.test(appId)) {
    const appleVisionPlatform = platforms.find(p => p.id === "apple_vision")
    if (appleVisionPlatform) return buildResult(appleVisionPlatform)
  }
  if (/^steam_/i.test(appId)) {
    const steamPlatform = platforms.find(p => p.id === "steam")
    if (steamPlatform) return buildResult(steamPlatform)
  }

  // Distinguish Meta from Steam via app_id length.
  // Meta Quest Store IDs are 16+ digit integers; Steam App IDs are ≤8 digits.
  const digits = appId.replace(/\D/g, "")
  if (digits.length >= 15) {
    const metaPlatform = platforms.find(p => p.id === "meta")
    if (metaPlatform) return buildResult(metaPlatform)
  } else if (digits.length >= 4 && digits.length <= 10) {
    const steamPlatform = platforms.find(p => p.id === "steam")
    if (steamPlatform) return buildResult(steamPlatform)
  }

  return null
}

// Non-terminal job_status values used across the okara AI CMO endpoints
// (app/load, marketing-strategy, chat, etc). Any other status (e.g. "done",
// "completed") — or no job_status at all — is treated as terminal.
const IN_PROGRESS_JOB_STATUSES = ["queued", "fetching_data", "generating", "thinking"]

/**
 * Re-issues `requestFn` while the backend reports an in-progress job_status,
 * so slow AI generations (article/LinkedIn/X posts, etc.) don't come back
 * empty just because the first response wasn't ready yet. Endpoints that
 * respond synchronously (no job_status field) resolve on the very first
 * call, so this is backward compatible with those.
 */
const pollForJobCompletion = async (
  requestFn,
  { maxPolls = 60, onStatus, timeoutMessage = "Generation timed out. Please try again." } = {},
) => {
  let response = await requestFn()
  let pollCount = 0

  while (
    response &&
    response.job_status &&
    IN_PROGRESS_JOB_STATUSES.includes(response.job_status)
  ) {
    if (pollCount >= maxPolls) {
      throw new Error(timeoutMessage)
    }

    onStatus?.(response.job_status, pollCount)

    const pollInterval = ["queued", "fetching_data"].includes(response.job_status)
      ? 5000
      : 10000
    await new Promise(resolve => setTimeout(resolve, pollInterval))

    response = await requestFn()
    pollCount++
  }

  return response
}

const AppScanner = ({ appName: propAppName, genre: propGenre, onBack } = {}) => {
  const { trackEvent } = useAnalytics()
  // In-memory cache for app data (now using module-level cache)

  // Add CSS styles for app name expansion and recently opened grid
  useEffect(() => {
    const appExpansionStyles = `
      .app-name-container {
        transition: all 0.3s ease;
        max-width: 150px;
      }

      .app-name-container.expanded {
        max-width: 300px;
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
      }

      .recently-opened-grid {
        display: grid;
        gap: 12px;
        grid-template-columns: repeat(5, minmax(0, 1fr));
      }

      @media (max-width: 1199px) {
        .recently-opened-grid {
          grid-template-columns: repeat(4, minmax(0, 1fr));
        }
      }

      @media (max-width: 991px) {
        .recently-opened-grid {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }
      }

      @media (max-width: 767px) {
        .recently-opened-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }

      @media (max-width: 479px) {
        .recently-opened-grid {
          grid-template-columns: repeat(1, minmax(0, 1fr));
        }
      }
    `

    const styleSheet = document.createElement("style")
    styleSheet.textContent = appExpansionStyles
    document.head.appendChild(styleSheet)

    return () => {
      document.head.removeChild(styleSheet)
    }
  }, [])

  // Use platforms context for dynamic platform management
  const {
    platforms,
    getPlatformIcon,
    getPlatformDisplayName,
  } = usePlatforms()

  // Cache management functions
  const getCachedAppData = key => {
    return appDataCache.get(key)
  }

  const setCachedAppData = (key, data) => {
    appDataCache.set(key, data)
  }

  const isAppCached = key => {
    return appDataCache.has(key)
  }

  // Generate normalized cache key that works for both names and URLs
  const generateCacheKey = input => {
    const trimmed = input.toLowerCase().trim()

    // Extract app name from URL if it's a keyquest.io URL
    if (trimmed.includes("keyquest.io/xr")) {
      const match = trimmed.match(/keyquest\.io\/xr\/.*\/(.+)$/)
      if (match) {
        return match[1] // Return just the app name part
      }
    }

    return trimmed
  }

  // Add app to recently opened list
  const addToRecentlyOpened = (appData, originalInput) => {
    if (!appData || !appData.game_info) {
      return
    }

    const appInfo = {
      id: appData.game_info.app_id || appData.app_id,
      name: appData.game_info.name || appData.name,
      icon: appData.game_info.image_url,
      originalInput: originalInput,
      timestamp: Date.now(),
    }

    // Update module-level variable
    // Remove if already exists to avoid duplicates
    const filtered = recentlyOpenedApps.filter(app => app.id !== appInfo.id)
    // Add to beginning and limit to 15 items
    recentlyOpenedApps = [appInfo, ...filtered].slice(0, 15)

    // Force re-render
    forceUpdate({})
  }

  const fetchRecentlyOpenedApps = async () => {
    setLoadingRecentlyOpened(true)
    try {
      const token = localStorage.getItem("authToken")
      const response = await fetch(
        `${okara_api}/api/app/recent-apps/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.ok) {
        const data = await response.json()
        // Transform API response to match component structure
        const transformedApps = data.latest_app_usage.recent_games.map(app => ({
          id: app.app_id,
          name: app.name,
          icon: app.image_url,
          originalInput: `https://keyquest.io${app.game_url}`,
          timestamp: Date.now(),
        }))
        recentlyOpenedApps = transformedApps
        forceUpdate({})
      }
    } catch (error) {
      console.error("Error fetching recently opened apps from API:", error)
    } finally {
      setLoadingRecentlyOpened(false)
    }
  }

  // Handle recently opened app click
  const handleRecentlyOpenedClick = app => {
    setPendingAppName(app.name || "")
    setUrl(app.originalInput)
    handleScanWithUrl(app.originalInput)
  }

  // Handle LinkedIn native sharing
  const handleLinkedInShare = () => {
    if (!linkedinPost) return

    // Construct the share content (body already contains hashtags from API)
    const shareText = linkedinPost.hook
      ? `${linkedinPost.hook}\n\n${linkedinPost.body || ""}`
      : `${linkedinPost.body || ""}`

    // LinkedIn share URL with pre-filled content
    const linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent("https://keyquest.io")}&summary=${encodeURIComponent(shareText)}`

    // Open in new tab
    window.open(linkedinShareUrl, "_blank", "width=600,height=400")

    trackEvent(ANALYTICS_EVENTS.FEATURE.AI_CMO_LINKEDIN_POST_SHARED, {
      app_id: appData?.app_id,
    })
  }

  // Handle closing chat session
  const handleCloseChatSession = async appId => {
    if (!appId) return

    try {
      await del(`${okara_api}/api/app/chat/`, {
        data: { app_id: appId },
      })
    } catch (error) {
      console.error("Error closing chat session:", error)
      // Don't show error to user as this is a cleanup operation
    }
  }

  // The AI CMO backend's `report.response` is now the same structured JSON as
  // the standalone AI Report (metrics/key_observations/recommendations/sentiment)
  // instead of an emoji-tagged text blob — read the `metrics` object directly.
  const parseMetricChanges = report => {
    const metrics = report?.response?.metrics
    if (!metrics || typeof metrics !== "object") return {}

    const normalizeDirection = direction =>
      direction === "stable" ? "neutral" : direction || "neutral"

    const toChange = (data, valueKey = "absolute_change") => {
      if (!data) return null
      return {
        value: data[valueKey] ?? null,
        percentage: typeof data.percent_change === "number" ? data.percent_change : 0,
        direction: normalizeDirection(data.direction),
      }
    }

    const changes = {}
    if (metrics.asr_change) changes.asr = toChange(metrics.asr_change)
    if (metrics.downloads) changes.downloads = toChange(metrics.downloads)
    if (metrics.ku) changes.ku = toChange(metrics.ku)
    if (metrics.ktr) changes.ktr = toChange(metrics.ktr)
    if (metrics.review_volume) changes.reviewVolume = toChange(metrics.review_volume)
    if (metrics.revenue) changes.revenue = toChange(metrics.revenue)
    if (metrics.user_rating) {
      changes.rating = {
        fromValue: metrics.user_rating.start_value ?? null,
        toValue: metrics.user_rating.end_value ?? null,
        percentage: typeof metrics.user_rating.percent_change === "number" ? metrics.user_rating.percent_change : 0,
        direction: normalizeDirection(metrics.user_rating.direction),
      }
    }

    return changes
  }

  // Validate input for scan button
  const isValidInput = () => {
    const trimmedUrl = url.trim()

    // Check if it's empty
    if (!trimmedUrl) return false

    // Check if it's a valid URL (basic validation)
    const urlPattern = /^https?:\/\/.+/i
    if (urlPattern.test(trimmedUrl)) return true

    // Check if it looks like a search query (contains spaces and no URL pattern)
    // If it's a search query, we should not allow scanning until they select a result
    return false
  }

  // Handle LinkedIn post generation
  const handleLinkedInPost = async () => {
    if (!appData?.app_id) {
      console.error("No app_id available for LinkedIn post generation")
      return
    }

    try {
      setIsGeneratingLinkedIn(true)
      setShowLinkedInModal(true)
      setLinkedinPost(null)

      const requestPayload = {
        app_id: appData.app_id,
        prompt:
          "Write a viral LinkedIn post about why this VR app is growing fast",
      }

      const data = await pollForJobCompletion(
        () => post(`${okara_api}/api/app/generate-linkedin/`, requestPayload),
        { timeoutMessage: "LinkedIn post generation timed out. Please try again." },
      )

      if (data && data.job_status && !["done", "completed"].includes(data.job_status)) {
        throw new Error(`Job failed with status: ${data.job_status}`)
      }

      setLinkedinPost(data)
      trackEvent(ANALYTICS_EVENTS.FEATURE.AI_CMO_LINKEDIN_POST_GENERATED, {
        app_id: appData?.app_id,
      })
    } catch (error) {
      console.error("Error generating LinkedIn post:", error)
      setLinkedinPost(null)
    } finally {
      setIsGeneratingLinkedIn(false)
    }
  }

  // Handle X native sharing
  const handleXShare = () => {
    if (!xPost) return

    // Construct the share content
    const shareText = xPost.hook
      ? `${xPost.hook}\n\n${xPost.body || ""}`
      : `${xPost.body || ""}`

    // X share URL with pre-filled content
    const xShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`

    // Open in new tab
    window.open(xShareUrl, "_blank", "width=600,height=400")

    trackEvent(ANALYTICS_EVENTS.FEATURE.AI_CMO_X_POST_SHARED, {
      app_id: appData?.app_id,
    })
  }

  // Handle X post generation
  const handleXPost = async () => {
    if (!appData?.app_id) {
      console.error("No app_id available for X post generation")
      return
    }

    try {
      setIsGeneratingX(true)
      setShowXModal(true)
      setXPost(null)

      const requestPayload = {
        app_id: appData.app_id,
        prompt: "Write a viral X post about why this VR app is growing fast",
      }

      const data = await pollForJobCompletion(
        () => post(`${okara_api}/api/app/generate-tweet/`, requestPayload),
        { timeoutMessage: "X post generation timed out. Please try again." },
      )

      if (data && data.job_status && !["done", "completed"].includes(data.job_status)) {
        throw new Error(`Job failed with status: ${data.job_status}`)
      }

      setXPost(data)
      trackEvent(ANALYTICS_EVENTS.FEATURE.AI_CMO_X_POST_GENERATED, {
        app_id: appData?.app_id,
      })
    } catch (error) {
      console.error("Error generating X post:", error)
      setXPost(null)
    } finally {
      setIsGeneratingX(false)
    }
  }

  // Handle Digital Marketing Strategy
  // const handleMarketingStrategy = async () => {
  //   if (!appData?.app_id) {
  //     console.error("No app ID available for marketing strategy")
  //     toastr.error("Please scan an app first to generate marketing strategy", "Error")
  //     return
  //   }

  //   try {
  //     setIsGeneratingMarketingStrategy(true)
  //     setShowMarketingStrategyModal(true)
  //     setMarketingStrategyData(null)
  //     setMarketingProgress(0)
  //     setMarketingProgressMessage("Initializing marketing strategy analysis...")

  //     let response = await post(`${okara_api}/api/app/marketing-strategy/`, {
  //       app_id: appData.app_id,
  //     })

  //     // Poll for job completion if status is queued or generating
  //     let pollCount = 0
  //     const maxPolls = 60 // Maximum 10 minutes (60 polls × 10 seconds)

  //     while (
  //       response &&
  //       response.job_status &&
  //       (response.job_status === "queued" || response.job_status === "fetching_data" || response.job_status === "generating")
  //     ) {
  //       if (pollCount >= maxPolls) {
  //         throw new Error("Marketing strategy generation timed out. Please try again.")
  //       }

  //       console.log(`Marketing strategy job status: ${response.job_status}, polling... (${pollCount + 1}/${maxPolls})`)

  //       // Use custom progress messages based on job status
  //       const progressMessages = {
  //         queued: "Initializing marketing strategy generation...",
  //         fetching_data: "Analyzing app data and market trends...",
  //         generating: "AI is crafting your comprehensive marketing strategy...",
  //       }
  //       setMarketingProgressMessage(progressMessages[response.job_status] || "Processing marketing strategy...")
  //       setMarketingProgress(Math.min((pollCount / maxPolls) * 100, 90))

  //       // Determine poll interval based on status
  //       let pollInterval = 10000 // Default 10s
  //       if (response.job_status === "queued" || response.job_status === "fetching_data") {
  //         pollInterval = 5000 // 5s for queued and fetching_data
  //       }
  //       await new Promise(resolve => setTimeout(resolve, pollInterval))

  //       response = await post(`${okara_api}/api/app/marketing-strategy/`, {
  //         app_id: appData.app_id,
  //       })

  //       pollCount++
  //     }

  //     // Check if job completed successfully
  //     if (response && response.job_status === "done" && response.strategy) {
  //       setMarketingStrategyData(response.strategy)
  //       setMarketingProgress(100)
  //       setMarketingProgressMessage("Marketing strategy generated successfully!")
  //     } else {
  //       throw new Error(
  //         response?.job_status
  //           ? `Job failed with status: ${response.job_status}`
  //           : "Failed to generate marketing strategy"
  //       )
  //     }
  //   } catch (error) {
  //     console.error("Error generating marketing strategy:", error)
  //     toastr.error(error.message || "Failed to generate marketing strategy", "Error")
  //     setMarketingStrategyData(null)
  //     setShowMarketingStrategyModal(false)
  //   } finally {
  //     setIsGeneratingMarketingStrategy(false)
  //   }
  // }

  const handleMarketingStrategy = async () => {
  if (!appData?.app_id) {
    console.error("No app ID available for marketing strategy")
    toastr.error("Please scan an app first to generate a marketing strategy", "Error")
    return
  }

  let longWaitTimer = null
  try {
    setIsGeneratingMarketingStrategy(appData.app_id)
    setShowMarketingStrategyModal(true)
    setMarketingStrategyData(null)

    // Optional: hide progress bar while generating
    setMarketingProgress(0)

    setMarketingProgressMessage(
      "We're gathering app data, market insights, competitor information, and growth opportunities to create your personalized marketing strategy. This process may take several minutes. You can continue exploring the platform while we work."
    )

    // If it's still generating after 30s, stop cycling the status message and
    // let the user know they don't need to keep waiting around for it.
    let longWaitReached = false
    longWaitTimer = setTimeout(() => {
      longWaitReached = true
      setMarketingProgressMessage(
        "This is taking longer than expected. We'll email you the results as soon as they're ready — feel free to keep exploring the platform in the meantime."
      )
    }, 30000)

    let response = await post(
      `${okara_api}/api/app/marketing-strategy/`,
      {
        app_id: appData.app_id,
      }
    )

    let pollCount = 0
    const maxPolls = 60

    while (
      response &&
      response.job_status &&
      ["queued", "fetching_data", "generating"].includes(response.job_status)
    ) {
      if (pollCount >= maxPolls) {
        throw new Error("Marketing strategy generation timed out. Please try again.")
      }

      console.log(
        `Marketing strategy job status: ${response.job_status}, polling... (${pollCount + 1}/${maxPolls})`
      )

      const progressMessages = {
        queued:
          "Preparing your marketing strategy request...",

        fetching_data:
          "We're gathering app data, market insights, competitor information, and growth opportunities to build your strategy. This may take a few minutes.",

        generating:
          "We're creating your personalized marketing strategy. You can continue using the platform while we work. We'll notify you as soon as it's ready.",
      }

      if (!longWaitReached) {
        setMarketingProgressMessage(
          progressMessages[response.job_status] ||
            "Generating your marketing strategy..."
        )
      }

      // Keep progress indeterminate instead of fake percentages
      setMarketingProgress(0)

      let pollInterval = 10000

      if (
        response.job_status === "queued" ||
        response.job_status === "fetching_data"
      ) {
        pollInterval = 5000
      }

      await new Promise(resolve => setTimeout(resolve, pollInterval))

      response = await post(
        `${okara_api}/api/app/marketing-strategy/`,
        {
          app_id: appData.app_id,
        }
      )

      pollCount++
    }

    if (response && response.job_status === "done" && response.strategy) {
      setMarketingStrategyData(response.strategy)
      setMarketingProgress(100)
      setMarketingProgressMessage("Marketing strategy generated successfully!")

      trackEvent(ANALYTICS_EVENTS.FEATURE.AI_CMO_MARKETING_STRATEGY_GENERATED, {
        app_id: appData?.app_id,
        app_name: appData?.game_info?.name || appData?.name,
      })

      toastr.success(
        "Your personalized marketing strategy is ready to review.",
        "Marketing Strategy Ready"
      )
    } else {
      throw new Error(
        response?.job_status
          ? `Job failed with status: ${response.job_status}`
          : "Failed to generate marketing strategy"
      )
    }
  } catch (error) {
    console.error("Error generating marketing strategy:", error)

    toastr.error(
      error.message || "Failed to generate marketing strategy",
      "Error"
    )

    setMarketingStrategyData(null)
    setShowMarketingStrategyModal(false)
  } finally {
    clearTimeout(longWaitTimer)
    setIsGeneratingMarketingStrategy(null)
  }
}

  // Add CSS animation for loading dots and skeleton pulse
  React.useEffect(() => {
    const style = document.createElement("style")
    style.textContent = `
      @keyframes loading-bounce {
        0%, 80%, 100% {
          transform: scale(0.8);
          opacity: 0.5;
        }
        40% {
          transform: scale(1);
          opacity: 1;
        }
      }
      @keyframes skeleton-pulse {
        0%, 100% {
          opacity: 0.4;
        }
        50% {
          opacity: 0.7;
        }
      }
    `
    document.head.appendChild(style)
    return () => {
      document.head.removeChild(style)
    }
  }, [])

  // Cleanup chat session on component unmount
  React.useEffect(() => {
    return () => {
      if (currentAppIdRef.current) {
        handleCloseChatSession(currentAppIdRef.current)
      }
    }
  }, [])

  // Toastr configuration
  toastr.options = {
    closeButton: true,
    debug: false,
    newestOnTop: false,
    progressBar: false,
    positionClass: "toast-bottom-right",
    preventDuplicates: false,
    onclick: null,
    showDuration: "300",
    hideDuration: "1000",
    timeOut: "5000",
    extendedTimeOut: "1000",
    showEasing: "swing",
    hideEasing: "linear",
    showMethod: "fadeIn",
    hideMethod: "fadeOut",
  }

  const [showPlanModal, setShowPlanModal] = useState(false)

  const [url, setUrl] = useState("")
  // Input captured when a scan starts, so the progress log does not depend on the editable `url` field
  const [scanTarget, setScanTarget] = useState("")
  const [scanning, setScanning] = useState(false)
  const [appData, setAppData] = useState(null)
  // Name to show as a placeholder title while the freshly-selected app is scanning
  // (appData is cleared/stale at that point, so we can't read the name from it yet)
  const [pendingAppName, setPendingAppName] = useState("")
  const [scanned, setScanned] = useState(false)
  const [loadingRecentlyOpened, setLoadingRecentlyOpened] = useState(true)

  // Collapse the sidebar only once the results view actually shows (Sidebar.js listens for this),
  // not just from visiting this page — the search entry screen stays consistent with every other
  // page, and only the full-bleed results view needs the extra width.
  useEffect(() => {
    if (scanned) window.dispatchEvent(new Event("ai-cmo:results-loading"))
  }, [scanned])

  const navigate = useNavigate()
  const location = useLocation()
  const { genre: routeGenreParam, slug: routeSlugParam } = useParams()
  // When embedded elsewhere (e.g. inside the game dashboard) there are no
  // /ai-cmo/:genre/:slug route params, so fall back to the props passed by
  // the embedding page. The current URL's ?id= is reused as-is either way.
  const genreParam = routeGenreParam || (propGenre ? createSlug(propGenre) : undefined)
  const slugParam = routeSlugParam || (propAppName ? createSlug(propAppName) : undefined)

  // Reflect the currently open app in the URL as /ai-cmo/{genre}/{slug}?id={app_id},
  // matching the share-link format already used elsewhere (see createSlug usages
  // below), without adding a new browser-history entry per scan.
  const syncAppIdInUrl = data => {
    // Embedded mode (onBack provided): don't rewrite the URL, it would navigate
    // away from the page hosting this component (e.g. the game dashboard).
    if (onBack) return
    const appId = data?.game_info?.app_id || data?.app_id || data?.id
    const name =
      data?.game_info?.name || data?.policy?.policy_report?.app?.name || data?.name
    if (appId && name) {
      const genreSlug = createSlug(data?.game_info?.genre?.[0] || data?.game_info?.category || "apps") || "apps"
      const nameSlug = createSlug(name)
      navigate(`/ai-cmo/${genreSlug}/${nameSlug}?id=${appId}`, { replace: true })
    } else {
      navigate("/ai-cmo", { replace: true })
    }
  }

  // Update ref when appData changes
  React.useEffect(() => {
    if (appData) {
      const appId = appData?.game_info?.app_id || appData?.app_id || appData?.id
      currentAppIdRef.current = appId
    }
  }, [appData])

  // Clear X post data when app changes
  React.useEffect(() => {
    if (appData) {
      setXPost(null)
      setLinkedinPost(null)
      setGeneratedArticle("")
      setMarketingStrategyData(null)
    }
  }, [appData?.app_id])

  // Fetch recently opened apps from API on component mount
  React.useEffect(() => {
    fetchRecentlyOpenedApps()
  }, [])
  const [chatMessages, setChatMessages] = useState([
    {
      role: "ai",
      content:
        "Hello! I'm your full time AI CMO assistant. I can help you analyze Meta Quest apps and provide insights on performance, user sentiment, and optimization strategies. How can I assist you today?",
    },
  ])
  const [chatInput, setChatInput] = useState("")
  const [sentimentHoverIndex, setSentimentHoverIndex] = useState(null)
  const [sentimentTooltip, setSentimentTooltip] = useState(null)
  const [isChatLoading, setIsChatLoading] = useState(false)
  const [scanningStage, setScanningStage] = useState(0)
  const [showArticleModal, setShowArticleModal] = useState(false)
  const [generatedArticle, setGeneratedArticle] = useState("")
  const [isGeneratingArticle, setIsGeneratingArticle] = useState(false)
  const [showLinkedInModal, setShowLinkedInModal] = useState(false)
  const [linkedinPost, setLinkedinPost] = useState(null)
  const [isGeneratingLinkedIn, setIsGeneratingLinkedIn] = useState(false)
  const [showXModal, setShowXModal] = useState(false)
  const [xPost, setXPost] = useState(null)
  const [isGeneratingX, setIsGeneratingX] = useState(false)
  const [showComingSoonModal, setShowComingSoonModal] = useState(false)
  const [comingSoonFeature] = useState("Coming Soon Feature")
  const [showMarketingStrategyModal, setShowMarketingStrategyModal] = useState(false)
  const [marketingStrategyData, setMarketingStrategyData] = useState(null)
  const [isGeneratingMarketingStrategy, setIsGeneratingMarketingStrategy] = useState(null)
  const [marketingProgress, setMarketingProgress] = useState(0)
  const [marketingProgressMessage, setMarketingProgressMessage] = useState("")
  const [showCopyToast, setShowCopyToast] = useState(false)
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
  const [metricChanges, setMetricChanges] = useState({})
  const [expandedThemeBlocks, setExpandedThemeBlocks] = useState({})
  // Name search now goes through the shared KeywordSuggestionsDetailsModal (the
  // same modal Keyword Suggestions/UGC Explorer/Influencer Explorer all use)
  // instead of a bespoke multi-platform dropdown.
  const [showSearchModal, setShowSearchModal] = useState(false)
  const [showSearchUpgradeModal, setShowSearchUpgradeModal] = useState(false)
  const [scanUpgradeRequired, setScanUpgradeRequired] = useState(false)
  const [showScanUpgradeModal, setShowScanUpgradeModal] = useState(false)
  const [recentlyOpenedPage, setRecentlyOpenedPage] = useState(1)
  const [, forceUpdate] = useState({})
  const chatContainerRef = useRef(null)
  const chatEndRef = useRef(null)
  const processedMessagesRef = useRef(new Set())
  const currentAppIdRef = useRef(null)
  const loadingRef = useRef(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Exact card styling from gameDashboard
  const cardStyle = {
    backgroundColor: "rgba(23, 24, 33, 1)",
    border: "1px solid #374151",
    borderRadius: "12px",
    padding: "16px",
    height: "100%",
    cursor: "default",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)",
  }

  const labelStyle = {
    fontSize: "10px",
    textTransform: "uppercase",
    color: "rgba(255, 255, 255, 0.4)",
    letterSpacing: "0.05em",
    margin: "0 0 6px 0",
    fontWeight: "500",
  }

  const valueStyle = {
    fontSize: "12px",
    fontWeight: "bold",
    color: "white",
    margin: 0,
    fontFamily: "monospace",
  }

  const headerStyle = {
    fontSize: "14px",
    fontWeight: "600",
    color: "white",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  }

  const secondaryCardStyle = {
    backgroundColor: "rgba(55, 65, 81, 0.5)",
    border: "1px solid #374151",
    borderRadius: "6px",
    padding: "3px",
    minHeight: "45px",
    minWidth: "70px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)",
  }

  // Skeleton loader component
  const SkeletonCard = ({ height = "200px" }) => (
    <div style={cardStyle} className="h-100">
      <div
        style={headerStyle}
        className="d-flex justify-content-between align-items-center"
      >
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: "16px",
              height: "16px",
              backgroundColor: "#374151",
              borderRadius: "4px",
            }}
          />
          <div
            style={{
              width: "80px",
              height: "16px",
              backgroundColor: "#374151",
              borderRadius: "4px",
            }}
          />
        </div>
      </div>
      <div className="card-body">
        {/* Skeleton content */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <div
            style={{
              width: "40px",
              height: "40px",
              backgroundColor: "#374151",
              borderRadius: "8px",
            }}
          />
          <div style={{ flex: 1 }}>
            <div
              style={{
                width: "120px",
                height: "14px",
                backgroundColor: "#374151",
                borderRadius: "4px",
                marginBottom: "8px",
              }}
            />
            <div
              style={{
                width: "80px",
                height: "11px",
                backgroundColor: "#374151",
                borderRadius: "4px",
              }}
            />
          </div>
        </div>

        {/* Skeleton metrics */}
        <div className="d-flex gap-2 mb-3">
          {[1, 2].map(i => (
            <div className="pdf-no-break" key={i} style={{ ...secondaryCardStyle, flex: 1 }}>
              <div
                style={{
                  width: "40px",
                  height: "16px",
                  backgroundColor: "#374151",
                  borderRadius: "4px",
                  margin: "0 auto 8px",
                }}
              />
              <div
                style={{
                  width: "30px",
                  height: "20px",
                  backgroundColor: "#374151",
                  borderRadius: "4px",
                  margin: "0 auto",
                }}
              />
            </div>
          ))}
        </div>

        {/* Skeleton details */}
        <div>
          {[1, 2, 3].map(i => (
            <div key={i} className="d-flex align-items-center gap-2 mb-2">
              <div
                style={{
                  width: "12px",
                  height: "12px",
                  backgroundColor: "#374151",
                  borderRadius: "4px",
                }}
              />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    width: "60px",
                    height: "11px",
                    backgroundColor: "#374151",
                    borderRadius: "4px",
                    marginBottom: "4px",
                  }}
                />
                <div
                  style={{
                    width: "100px",
                    height: "12px",
                    backgroundColor: "#374151",
                    borderRadius: "4px",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  const handleScan = async () => {
    if (!url.trim()) return

    // Determine if input is a URL or app name
    const input = url.trim()
    const isUrl =
      input.startsWith("http://") ||
      input.startsWith("https://") ||
      input.includes(".")
    const cacheKey = generateCacheKey(input)

    // Show a readable name immediately while the scan loads: use the typed
    // app name as-is, or derive one from the URL's last path segment.
    if (isUrl) {
      const slug = input.split("?")[0].split("/").filter(Boolean).pop() || ""
      const derivedName = slug
        .replace(/[-_]+/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase())
        .trim()
      setPendingAppName(derivedName)
    } else {
      setPendingAppName(input)
    }

    // Close previous chat session if exists
    const previousAppId = appData?.game_info?.app_id || appData?.app_id || appData?.id
    if (previousAppId) {
      handleCloseChatSession(previousAppId)
    }

    // Reset chat messages for new scan
    setChatMessages([
      {
        role: "ai",
        content:
          "Hello! I'm your full time AI CMO assistant. I can help you analyze Meta Quest apps and provide insights on performance, user sentiment, and optimization strategies. How can I assist you today?",
      },
    ])

    // Check cache first — cached apps are always allowed regardless of plan limit
    if (isAppCached(cacheKey)) {
      const cachedData = getCachedAppData(cacheKey)
      setAppData(cachedData)
      setScanned(true)
      syncAppIdInUrl(cachedData)
      setChatMessages([
        {
          role: "ai",
          content:
            "Hello! I'm your full time AI CMO assistant. I can help you analyze Meta Quest apps and provide insights on performance, user sentiment, and optimization strategies. How can I assist you today?",
        },
      ])
      return
    }

    // Only block new (uncached) scans when plan limit is reached
    if (scanUpgradeRequired) { setShowScanUpgradeModal(true); return }

    setAppData(null) // Clear stale data from a previously viewed app so the loading state does not show its name/info
    setScanTarget(input)
    setScanning(true)
    setScanningStage(0)
    setScanned(true) // Show results page immediately

    // Animation stages
    const stages = [
      "$ Initializing AI CMO...",
      `> Let me take a look at ${input}...`,
      "> Starting...",
      "> Starting my deep dive - crawling all your data",
      "> Found your app! Let me understand it",
    ]

    // Progress through animation stages
    for (let i = 0; i < stages.length; i++) {
      setScanningStage(i)
      await new Promise(resolve => setTimeout(resolve, 800))
    }

    try {
      // Make initial API call to load app data
      let response = await post(`${okara_api}/api/app/load/`, {
        [isUrl ? "link" : "name"]: input,
      })

      // Poll for job completion if status is queue or fetching_data
      while (
        response &&
        response.job_status &&
        (response.job_status === "queued" ||
          response.job_status === "fetching_data")
      ) {
        console.log(`Job status: ${response.job_status}, polling...`)
        await new Promise(resolve => setTimeout(resolve, 10000)) // Wait 10 seconds before polling again

        response = await post(`${okara_api}/api/app/load/`, {
          [isUrl ? "link" : "name"]: input,
        })
      }

      // Check if job completed successfully
      if (response && response.job_status === "completed") {
        // Update component state with API response data
        setAppData(response)
        setCachedAppData(cacheKey, response) // Cache response
        syncAppIdInUrl(response)

        trackEvent(ANALYTICS_EVENTS.FEATURE.APP_SCAN_RUN, {
          app_id: response?.game_info?.app_id || response?.app_id || response?.id,
          app_name: response?.game_info?.name || response?.name,
          input_type: isUrl ? "url" : "name",
        })

        // Reset chat messages when loading a different app
        setChatMessages([
          {
            role: "ai",
            content:
              "Hello! I'm your full time AI CMO assistant. I can help you analyze Meta Quest apps and provide insights on performance, user sentiment, and optimization strategies. How can I assist you today?",
          },
        ])

        // Parse metric changes from report
        if (response.report) {
          const changes = parseMetricChanges(response.report)
          setMetricChanges(changes)
        }
        addToRecentlyOpened(response, input) // Add to recently opened
      } else {
        throw new Error(
          response?.job_status
            ? `Job failed with status: ${response.job_status}`
            : "Failed to get completed status",
        )
      }
    } catch (error) {
      console.error("Error loading app data:", error)

      // Handle tier-based access errors
      if (error.isTierRestricted) {
        toastr.error(
          `⚠️ Upgrade Required: ${error.tierMessage || "This feature requires a higher tier subscription to access AI CMO features."}`,
          "Access Restricted",
        )
      } else if (error.isGuestRestricted) {
        toastr.error(
          "🔒 Guest Access Restricted: Please sign in or create an account to access AI CMO features.",
          "Access Restricted",
        )
      } else {
        toastr.error(error.message || "Failed to load app data", "Scan Error")
      }
      setScanned(false) // Hide results page on error
    } finally {
      setScanning(false)
      setScanningStage(0)
    }
  }

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight
      }
    })
  }

  const removeLoadingMessage = (messages, loadingId) => {
    return messages.filter(msg => msg.id !== loadingId)
  }

  const sendChatMessage = async rawMessage => {
    const message = rawMessage?.trim()

    console.log("sendChatMessage called", {
      message,
      isChatLoading,
    })

    if (!message) return

    // Prevent double submissions
    if (loadingRef.current) return

    // Prevent duplicate rapid messages
    if (processedMessagesRef.current.has(message)) {
      return
    }

    const appId =
      appData?.game_info?.app_id ||
      appData?.app_id ||
      appData?.id

    const loadingId = `loading-${Date.now()}`

    // Mark as processing
    loadingRef.current = true
    processedMessagesRef.current.add(message)
    setIsChatLoading(true)

    // No app selected
    if (!appId) {
      setChatMessages(prev => [
        ...prev,
        {
          role: "user",
          text: message,
        },
        {
          role: "ai",
          text:
            "Please scan an app first before asking questions about it. I need app data to provide intelligent insights.",
        },
      ])

      setChatInput("")
      scrollToBottom()

      processedMessagesRef.current.delete(message)
      loadingRef.current = false
      setIsChatLoading(false)

      return
    }

    // Add user + loader together
    setChatMessages(prev => [
      ...prev,
      {
        role: "user",
        text: message,
      },
      {
        id: loadingId,
        role: "ai",
        text: "",
        isLoading: true,
      },
    ])

    setChatInput("")
    scrollToBottom()

    try {
      // Initial request
      let response = await post(
        `${okara_api}/api/app/chat/`,
        {
          app_id: appId,
          message,
        }
      )

      /**
       * IMPORTANT:
       * Ideally backend should return a job_id.
       * Polling same message repeatedly is not ideal.
       */

      const jobId = response?.job_id

      let pollCount = 0
      const maxPolls = 60

      // Poll while processing
      while (
        response &&
        ["queued", "thinking"].includes(response?.job_status) &&
        pollCount < maxPolls
      ) {
        console.log(
          `Polling ${pollCount + 1}/${maxPolls}`,
          response?.job_status
        )

        await new Promise(resolve =>
          setTimeout(resolve, 2000)
        )

        pollCount++

        // Stop if component unmounted
        if (!isMountedRef.current) return

        /**
         * BEST PRACTICE:
         * Use dedicated polling endpoint with job_id.
         */

        response = await post(
          `${okara_api}/api/app/chat/`,
          jobId
            ? { job_id: jobId }
            : {
              app_id: appId,
              message,
            }
        )

        console.log("Poll response:", response)
      }

      if (!isMountedRef.current) return

      // Success
      if (response?.response) {
        setChatMessages(prev => [
          ...removeLoadingMessage(prev, loadingId),
          {
            role: "ai",
            text: response.response,
          },
        ])

        trackEvent(ANALYTICS_EVENTS.FEATURE.AI_CMO_CHAT_MESSAGE_SENT, { app_id: appId })
      }
      // Timeout
      else if (pollCount >= maxPolls) {
        setChatMessages(prev => [
          ...removeLoadingMessage(prev, loadingId),
          {
            role: "ai",
            text:
              "I apologize, but your request is taking longer than expected. Please try again in a few minutes.",
          },
        ])
      }
      // Invalid response
      else {
        console.error("Unexpected response:", response)

        setChatMessages(prev => [
          ...removeLoadingMessage(prev, loadingId),
          {
            role: "ai",
            text:
              "I apologize, but I'm having trouble processing your request right now. Please try again.",
          },
        ])
      }
    } catch (error) {
      console.error("Error in AI chat:", error)

      if (!isMountedRef.current) return

      setChatMessages(prev => [
        ...removeLoadingMessage(prev, loadingId),
        {
          role: "ai",
          text:
            "I apologize, but I encountered an error while processing your request. Please try again later.",
        },
      ])
    } finally {
      processedMessagesRef.current.delete(message)

      loadingRef.current = false
      setIsChatLoading(false)

      scrollToBottom()
    }
  }

  const handleChat = () => {
    sendChatMessage(chatInput)
  }

  const handleChatWithMessage = message => {
    sendChatMessage(message)
  }

  const handleGenerateArticle = async () => {
    if (!appData?.app_id) {
      console.error("No app ID available for article generation")
      return
    }

    setIsGeneratingArticle(true)
    setGeneratedArticle("")

    try {
      const requestPayload = {
        app_id: appData.app_id,
        prompt:
          "need a article so that user can enggage more with some good quotes and anylises dont use any negative",
      }

      const response = await pollForJobCompletion(
        () => post(`${okara_api}/api/app/generate-article/`, requestPayload),
        { timeoutMessage: "Article generation timed out. Please try again." },
      )

      if (response && response.job_status && !["done", "completed"].includes(response.job_status)) {
        throw new Error(`Job failed with status: ${response.job_status}`)
      }

      if (response && response.blocks && response.blocks.length > 0) {
        // Extract rich text content from blocks
        const richTextBlock = response.blocks.find(
          block => block.__component === "shared.rich-text",
        )
        if (richTextBlock && richTextBlock.body) {
          setGeneratedArticle(richTextBlock.body)
          trackEvent(ANALYTICS_EVENTS.FEATURE.AI_CMO_ARTICLE_GENERATED, {
            app_id: appData?.app_id,
          })
        } else {
          setGeneratedArticle("Article content not available.")
        }
      } else if (response && response.article) {
        // Fallback for old response format
        setGeneratedArticle(response.article)
        trackEvent(ANALYTICS_EVENTS.FEATURE.AI_CMO_ARTICLE_GENERATED, {
          app_id: appData?.app_id,
        })
      } else {
        setGeneratedArticle(
          "Unable to generate article at this time. Please try again later.",
        )
      }
    } catch (error) {
      console.error("Error generating article:", error)

      // Handle tier-based access errors
      if (error.isTierRestricted) {
        setGeneratedArticle(`
          <div style="padding: 20px; text-align: center; border: 1px solid #f59e0b; border-radius: 8px; background: rgba(245, 158, 11, 0.1);">
            <h4 style="color: #f59e0b; margin-bottom: 12px;">⚠️ Upgrade Required</h4>
            <p style="color: #ffffff; margin-bottom: 16px;">${error.tierMessage || "This feature requires a higher tier subscription."}</p>
            <p style="color: #9ca3af; font-size: 14px;">Please upgrade your plan to access AI content generation features.</p>
          </div>
        `)
      } else if (error.isGuestRestricted) {
        setGeneratedArticle(`
          <div style="padding: 20px; text-align: center; border: 1px solid #6b7280; border-radius: 8px; background: rgba(107, 114, 128, 0.1);">
            <h4 style="color: #6b7280; margin-bottom: 12px;">🔒 Guest Access Restricted</h4>
            <p style="color: #ffffff; margin-bottom: 16px;">Please sign in or create an account to access AI content generation features.</p>
          </div>
        `)
      } else {
        setGeneratedArticle(
          "An error occurred while generating the article. Please try again later.",
        )
      }
    } finally {
      setIsGeneratingArticle(false)
    }
  }

  const convertMarkdownToHtml = markdown => {
    if (!markdown) return ""

    let html = markdown

    // Convert headers
    html = html.replace(
      /^### (.*$)/gim,
      '<h3 style="color: #ffffff; font-size: 16px; font-weight: 600; margin: 16px 0 8px 0;">$1</h3>',
    )
    html = html.replace(
      /^## (.*$)/gim,
      '<h2 style="color: #ffffff; font-size: 18px; font-weight: 600; margin: 20px 0 10px 0;">$1</h2>',
    )
    html = html.replace(
      /^# (.*$)/gim,
      '<h1 style="color: #ffffff; font-size: 20px; font-weight: 700; margin: 24px 0 12px 0;">$1</h1>',
    )

    // Convert bold text
    html = html.replace(
      /\*\*(.*?)\*\*/g,
      '<strong style="color: #ffffff; font-weight: 600;">$1</strong>',
    )

    // Convert italic text
    html = html.replace(
      /\*(.*?)\*/g,
      '<em style="color: #ffffff; font-style: italic;">$1</em>',
    )

    // Convert blockquotes
    html = html.replace(
      /^> (.*$)/gim,
      '<blockquote style="border-left: 4px solid #374151; padding-left: 16px; margin: 16px 0; color: #9ca3af; font-style: italic;">$1</blockquote>',
    )

    // Convert images
    html = html.replace(
      /!\[([^\]]*)\]\(([^)]*)\)/g,
      '<img src="$2" alt="$1" style="max-width: 100%; height: auto; border-radius: 8px; margin: 16px 0;" />',
    )

    // Convert links
    html = html.replace(
      /\[([^\]]*)\]\(([^)]*)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #3b82f6; text-decoration: underline;">$1</a>',
    )

    // Convert line breaks
    html = html.replace(/\n\n/g, "<br /><br />")
    html = html.replace(/\n/g, "<br />")

    return html
  }

  const handleSearchResultClick = game => {
    setShowSearchModal(false)

    // /tracker/games/search (KEYWORD_SEARCH_PATH) returns `item_id`, not the old
    // `game_url` field — build the share-link URL the same way the rest of the
    // app does off item_id (see GlobalSearchBar/CompetitorsModal/createSlug usages),
    // otherwise this falls back to "https://keyquest.io/xrundefined".
    const fullUrl = `https://keyquest.io/xr/app/${createSlug(game.name || "")}?id=${game.item_id || game._id || ""}`

    // Show the selected app's name immediately while the scan loads
    setPendingAppName(game.name || "")

    // Update the URL state and immediately trigger scan with the new URL
    setUrl(fullUrl)
    handleScanWithUrl(fullUrl)
  }

  // New function to handle scan with specific URL
  const handleScanWithUrl = async specificUrl => {
    if (!specificUrl.trim()) return

    // Determine if input is a URL or app name
    const input = specificUrl.trim()
    const isUrl =
      input.startsWith("http://") ||
      input.startsWith("https://") ||
      input.includes(".")
    const cacheKey = generateCacheKey(input)

    // Cached apps are always allowed regardless of plan limit
    if (isAppCached(cacheKey)) {
      const cachedData = getCachedAppData(cacheKey)
      setAppData(cachedData)
      setScanned(true)
      syncAppIdInUrl(cachedData)
      setChatMessages([
        {
          role: "ai",
          content:
            "Hello! I'm your full time AI CMO assistant. I can help you analyze Meta Quest apps and provide insights on performance, user sentiment, and optimization strategies. How can I assist you today?",
        },
      ])
      return
    }

    // Only block new (uncached) scans when plan limit is reached
    if (scanUpgradeRequired) { setShowScanUpgradeModal(true); return }

    setAppData(null) // Clear stale data from a previously viewed app so the loading state does not show its name/info
    setScanTarget(input)
    setScanning(true)
    setScanningStage(0)
    setScanned(true) // Show results page immediately

    // Animation stages
    const stages = [
      "$ Initializing AI CMO...",
      `> Let me take a look at ${input}...`,
      "> Starting...",
      "> Starting my deep dive - crawling all your data",
      "> Found your app! Let me understand it",
    ]

    // Progress through animation stages
    for (let i = 0; i < stages.length; i++) {
      setScanningStage(i)
      await new Promise(resolve => setTimeout(resolve, 800))
    }

    try {
      // Make initial API call to load app data
      let response = await post(`${okara_api}/api/app/load/`, {
        [isUrl ? "link" : "name"]: input,
      })

      // Poll for job completion if status is queue or fetching_data
      while (
        response &&
        response.job_status &&
        (response.job_status === "queued" ||
          response.job_status === "fetching_data")
      ) {
        console.log(`Job status: ${response.job_status}, polling...`)
        await new Promise(resolve => setTimeout(resolve, 10000)) // Wait 10 seconds before polling again

        response = await post(`${okara_api}/api/app/load/`, {
          [isUrl ? "link" : "name"]: input,
        })
      }

      // Check if job completed successfully
      if (response && response.job_status === "completed") {
        // Update component state with API response data
        setAppData(response)
        setCachedAppData(cacheKey, response) // Cache response
        syncAppIdInUrl(response)

        trackEvent(ANALYTICS_EVENTS.FEATURE.APP_SCAN_RUN, {
          app_id: response?.game_info?.app_id || response?.app_id || response?.id,
          app_name: response?.game_info?.name || response?.name,
          input_type: isUrl ? "url" : "name",
        })

        // Reset chat messages when loading a different app
        setChatMessages([
          {
            role: "ai",
            content:
              "Hello! I'm your full time AI CMO assistant. I can help you analyze Meta Quest apps and provide insights on performance, user sentiment, and optimization strategies. How can I assist you today?",
          },
        ])

        // Parse metric changes from report
        if (response.report) {
          const changes = parseMetricChanges(response.report)
          setMetricChanges(changes)
        }
        addToRecentlyOpened(response, input) // Add to recently opened
      } else {
        throw new Error(
          response?.job_status
            ? `Job failed with status: ${response.job_status}`
            : "Failed to get completed status",
        )
      }
    } catch (error) {
      console.error("Error loading app data:", error)

      if (error.isTierRestricted) {
        setScanUpgradeRequired(true)
        setShowScanUpgradeModal(true)
      } else if (error.isGuestRestricted) {
        toastr.error(
          "🔒 Guest Access Restricted: Please sign in or create an account to access AI CMO features.",
          "Access Restricted",
        )
      } else {
        toastr.error(error.message || "Failed to load app data", "Scan Error")
      }
      setScanned(false)
    } finally {
      setScanning(false)
      setScanningStage(0)
    }
  }

  // Deep-link support: if the page is opened directly at /ai-cmo/{genre}/{slug}?id={app_id}
  // (e.g. a shared link), load that app on mount. This mirrors the real keyquest.io
  // app URL but with "ai-cmo" swapped in for "xr" after the domain, so swapping it
  // back reconstructs the exact link (https://keyquest.io/xr/{genre}/{slug}?id=...)
  // to send to /api/app/load/ — no lossy name search needed.
  useEffect(() => {
    if (genreParam && slugParam) {
      const reconstructedLink = `https://keyquest.io/xr/${genreParam}/${slugParam}${location.search}`
      const derivedName = slugParam
        .replace(/[-_]+/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase())
        .trim()
      setPendingAppName(derivedName)
      setUrl(reconstructedLink)
      handleScanWithUrl(reconstructedLink)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getBadgeClass = tag => {
    switch (tag) {
      case "TITLE-SAFE":
        return "bg-success text-white"
      case "DESCRIPTION-ONLY":
        return "bg-primary text-white"
      case "TRACKING-ONLY":
        return "bg-warning text-white"
      case "ACTION":
        return "bg-danger text-white"
      default:
        return "bg-secondary text-white"
    }
  }

  return (
    <BlurUpgradeContext.Provider value={{ openUpgradeModal: () => setShowPlanModal(true) }}>
      <AppsTrackingPlanModal
        show={showPlanModal}
        handleClose={() => setShowPlanModal(false)}
        title=" Upgrade your plan to unlock this feature"
      />
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 0.4;
            }
            50% {
              opacity: 0.8;
            }
          }
        `}
      </style>
      <SEO
        title={PAGE_SEO.aiCmo.title}
        description={PAGE_SEO.aiCmo.description}
        keywords={PAGE_SEO.aiCmo.keywords}
        canonical={generateCanonicalUrl("/ai-cmo")}
        robots={PAGE_SEO.aiCmo.robots}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "KeyQuest - CMO Agent",
          description:
            "First AI Agent focused on XR Product, ask and plan your perfect Go To Market actions",
          url: "https://keyquest.io/ai-cmo",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web Browser",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
        }}
      />
      <div
        className="page-content"
        style={{
          backgroundColor: "rgba(23, 24, 33, 1)",
          minHeight: "100vh",
          // NOTE: do not set `padding` (shorthand) here — it would override the
          // `.page-content` class's `padding-top` rule (app.scss) that reserves
          // space for the fixed topbar (#page-topbar), causing this view's
          // heading/"Back to Search" button to render behind the header.
          paddingLeft: "24px",
          paddingRight: "24px",
          paddingBottom: "24px",
        }}
      >
        {/* Once scanned, the Go Back button moves next to the "{App} - AI CMO
            Analysis" heading below (matching Forecasting/AI Reports) - only
            shown here, standalone, before that heading exists. */}
        {onBack && !scanned && (
          <div className="container-fluid px-4" style={{ marginTop: "16px", marginBottom: "16px" }}>
            <BackButton onClick={onBack} />
          </div>
        )}
        {/* ── Terminal-style Header ── */}
        {/* <AnimatePresence mode="wait">
        {scanned && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mb-4 mt-5"
            style={{ 
              backgroundColor: "rgba(246, 37, 90, 0.1)", 
              border: "1px solid rgba(246, 37, 90, 0.2)",
              borderRadius: "12px",
              padding: "16px"
            }}
          >
            <div className="container-fluid">
              <div className="d-flex align-items-center gap-2 font-monospace" style={{ fontSize: "13px" }}>
                <span style={{ color: "#F6255A" }}>$</span>
                <motion.span
                  initial={{ width: 0 }}
                  animate={{ width: "auto" }}
                  style={{ 
                    color: "#F6255A",
                    overflow: "hidden",
                    whitespace: "nowrap",
                    fontWeight: "600"
                  }}
                >
                  Initializing KeyQuest AI Scanner...
                </motion.span>
              </div>
              <div className="d-flex align-items-center gap-2 font-monospace mt-2" style={{ fontSize: "13px" }}>
                <span style={{ color: "#F6255A" }}>&gt;</span>
                <span style={{ color: "#10b981", fontWeight: "600" }}>Scan complete.</span>
                <span style={{ color: "#9ca3af" }}>Analyzing {appInfo.name} ({appInfo.app_id})</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence> */}

        {/* ── Hero / URL Input ── */}
        {/* No top padding once scanned: `.page-content` above already
            reserves the full topbar clearance, and this "py-3" was adding an
            extra 16px on top of it - the only one of the four "separate tool
            view" screens with that extra gap before its heading. */}
        <div
          className={`transition-all duration-700 ${scanned ? "pb-3" : "py-32"}`}
        >
          <div className="container-fluid px-4">
            <AnimatePresence mode="wait">
              {!scanned && (
                <motion.div
                  key="hero"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="text-center d-flex flex-column justify-content-center"
                  style={{ minHeight: "60vh" }}
                >
                  <div
                    className="text-center mb-5"
                    style={{ padding: "64px 0" }}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                    >
                      <h1
                        className="mb-3"
                        style={{
                          fontSize: "32px",
                          fontWeight: "bold",
                          color: "#ffffff",
                          marginBottom: "16px",
                        }}
                      >
                        Keyquest - CMO Agent
                      </h1>
                      <p
                        className="mb-4"
                        style={{
                          fontSize: "16px",
                          color: "#9ca3af",
                          marginBottom: "24px",
                        }}
                      >
                        First AI Agent focused on XR Productos, ask and plan
                        your perfect Go To Market actions
                      </p>
                      <div className="d-flex justify-content-center">
                        <div style={{ maxWidth: "600px", width: "100%" }}>
                          <div className="input-group">
                            <input
                              type="text"
                              value={url}
                              onChange={e => setUrl(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  if (isValidInput()) handleScan()
                                  else if (url.trim()) setShowSearchModal(true)
                                }
                              }}
                              placeholder="Enter Meta Quest app URL or name..."
                              className="form-control"
                              style={{
                                fontSize: "14px",
                                padding: "12px 16px",
                                backgroundColor: "rgba(55, 65, 81, 0.5)",
                                border: "1px solid #374151",
                                color: "#ffffff",
                                borderRadius: "8px 0 0 8px",
                                height: "48px",
                              }}
                            />
                            <button
                              onClick={() => {
                                if (isValidInput()) handleScan()
                                else if (url.trim()) setShowSearchModal(true)
                              }}
                              disabled={scanning || !url.trim()}
                              className="btn"
                              style={{
                                backgroundColor:
                                  scanning || !url.trim()
                                    ? "#9ca3af"
                                    : "#F6255A",
                                borderColor:
                                  scanning || !url.trim()
                                    ? "#9ca3af"
                                    : "#F6255A",
                                color: "#ffffff",
                                padding: "0 24px",
                                height: "48px",
                                borderRadius: "0 8px 8px 0",
                                fontWeight: "600",
                                cursor:
                                  scanning || !url.trim()
                                    ? "not-allowed"
                                    : "pointer",
                                opacity: scanning || !url.trim() ? 0.6 : 1,
                              }}
                            >
                              {scanning ? (
                                <span className="d-flex align-items-center gap-2">
                                  <div
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                  >
                                    <span className="visually-hidden">
                                      Loading...
                                    </span>
                                  </div>
                                  Scanning...
                                </span>
                              ) : (
                                <span className="d-flex align-items-center gap-2">
                                  <Search
                                    style={{ width: "16px", height: "16px" }}
                                  />
                                  {isValidInput() ? "Scan App" : "Search"}
                                </span>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Recently Opened Apps */}
                      {loadingRecentlyOpened && !scanned && !scanning ? (
                        <div className="container-fluid px-4 mt-4">
                          <div className="d-flex align-items-center gap-2 mb-3">
                            <Clock
                              style={{
                                width: "14px",
                                height: "14px",
                                color: "#9ca3af",
                              }}
                            />
                            <span
                              style={{
                                fontSize: "13px",
                                color: "#9ca3af",
                                fontWeight: "500",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              Recently Opened
                            </span>
                          </div>
                          <div className="recently-opened-grid">
                            {[...Array(5)].map((_, index) => (
                              <div className="pdf-no-break" key={index}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "14px",
                                  backgroundColor: "rgba(55, 65, 81, 0.5)",
                                  border: "1px solid #374151",
                                  borderRadius: "10px",
                                  gap: "12px",
                                  height: "68px",
                                }}
                              >
                                <div
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "8px",
                                    backgroundColor: "rgba(156, 163, 175, 0.2)",
                                    flexShrink: 0,
                                    animation: "pulse 1.5s ease-in-out infinite",
                                  }}
                                />
                                <div
                                  style={{
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  <div
                                    style={{
                                      height: "14px",
                                      width: "80%",
                                      backgroundColor: "rgba(156, 163, 175, 0.2)",
                                      borderRadius: "4px",
                                      animation: "pulse 1.5s ease-in-out infinite",
                                      animationDelay: `${index * 0.1}s`,
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        recentlyOpenedApps.length > 0 &&
                        !scanned &&
                        !scanning && (
                          <div className="container-fluid px-4 mt-4">
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <Clock
                                style={{
                                  width: "14px",
                                  height: "14px",
                                  color: "#9ca3af",
                                }}
                              />
                              <span
                                style={{
                                  fontSize: "13px",
                                  color: "#9ca3af",
                                  fontWeight: "500",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                }}
                              >
                                Recently Opened
                              </span>
                            </div>
                            <div className="recently-opened-grid">
                              {recentlyOpenedApps
                                .slice(
                                  (recentlyOpenedPage - 1) * 5,
                                  recentlyOpenedPage * 5,
                                )
                                .map(app => (
                                  <div
                                    key={app.id}
                                    onClick={() =>
                                      handleRecentlyOpenedClick(app)
                                    }
                                    title={app.name}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      padding: "14px",
                                      backgroundColor: "rgba(55, 65, 81, 0.5)",
                                      border: "1px solid #374151",
                                      borderRadius: "10px",
                                      cursor: "pointer",
                                      transition: "all 0.2s ease",
                                      gap: "12px",
                                      height: "68px",
                                      minWidth: 0,
                                      overflow: "hidden",
                                    }}
                                    onMouseEnter={e => {
                                      e.currentTarget.style.backgroundColor =
                                        "rgba(55, 65, 81, 0.8)"
                                      e.currentTarget.style.transform =
                                        "translateY(-2px)"
                                      e.currentTarget.style.boxShadow =
                                        "0 4px 12px rgba(0,0,0,0.3)"
                                      e.currentTarget.style.borderColor =
                                        "#F6255A"
                                    }}
                                    onMouseLeave={e => {
                                      e.currentTarget.style.backgroundColor =
                                        "rgba(55, 65, 81, 0.5)"
                                      e.currentTarget.style.transform =
                                        "translateY(0)"
                                      e.currentTarget.style.boxShadow = "none"
                                      e.currentTarget.style.borderColor =
                                        "#374151"
                                    }}
                                  >
                                    <img
                                      src={app.icon}
                                      alt={app.name}
                                      style={{
                                        width: "40px",
                                        height: "40px",
                                        borderRadius: "8px",
                                        objectFit: "cover",
                                        flexShrink: 0,
                                      }}
                                      onError={e => {
                                        e.target.src =
                                          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iOCIgZmlsbD0iIzM3NDE1MSIvPgo8cGF0aCBkPSJNMTMgMjZIMTNWMTNIMFYxMEgxM1Y4SDhWNEgxNlY4SDE0VjEwSDE0VjEzSDI2VjI2SDE0VjI4SDEwVjI2SDEzWiIgZmlsbD0iIzljYTNhZiIvPgo8L3N2Zz4="
                                      }}
                                    />
                                    <div
                                      style={{
                                        flex: 1,
                                        minWidth: 0,
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <div
                                        style={{
                                          color: "#fff",
                                          fontSize: "14px",
                                          fontWeight: "500",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          width: "100%",
                                        }}
                                      >
                                        {app.name}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                            {/* Pagination Controls */}
                            {recentlyOpenedApps.length > 5 && (
                              <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
                                <button
                                  onClick={() =>
                                    setRecentlyOpenedPage(prev =>
                                      Math.max(1, prev - 1),
                                    )
                                  }
                                  disabled={recentlyOpenedPage === 1}
                                  style={{
                                    padding: "6px 12px",
                                    minWidth: "70px",
                                    backgroundColor:
                                      recentlyOpenedPage === 1
                                        ? "rgba(246, 37, 90, 0.2)"
                                        : "rgba(246, 37, 90, 0.8)",
                                    border: "1px solid rgba(246, 37, 90, 0.3)",
                                    borderRadius: "6px",
                                    color:
                                      recentlyOpenedPage === 1
                                        ? "rgba(246, 37, 90, 0.5)"
                                        : "#fff",
                                    cursor:
                                      recentlyOpenedPage === 1
                                        ? "not-allowed"
                                        : "pointer",
                                    fontSize: "12px",
                                    transition: "all 0.2s",
                                  }}
                                  onMouseEnter={e => {
                                    if (recentlyOpenedPage !== 1) {
                                      e.currentTarget.style.backgroundColor =
                                        "rgba(246, 37, 90, 1)"
                                    }
                                  }}
                                  onMouseLeave={e => {
                                    if (recentlyOpenedPage !== 1) {
                                      e.currentTarget.style.backgroundColor =
                                        "rgba(246, 37, 90, 0.8)"
                                    }
                                  }}
                                >
                                  Previous
                                </button>
                                <span
                                  style={{
                                    color: "#9ca3af",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                  }}
                                >
                                  {recentlyOpenedPage} /{" "}
                                  {Math.ceil(recentlyOpenedApps.length / 5)}
                                </span>
                                <button
                                  onClick={() =>
                                    setRecentlyOpenedPage(prev =>
                                      Math.min(
                                        Math.ceil(
                                          recentlyOpenedApps.length / 5,
                                        ),
                                        prev + 1,
                                      ),
                                    )
                                  }
                                  disabled={
                                    recentlyOpenedPage ===
                                    Math.ceil(recentlyOpenedApps.length / 5)
                                  }
                                  style={{
                                    padding: "6px 12px",
                                    minWidth: "70px",
                                    backgroundColor:
                                      recentlyOpenedPage ===
                                        Math.ceil(recentlyOpenedApps.length / 5)
                                        ? "rgba(246, 37, 90, 0.2)"
                                        : "rgba(246, 37, 90, 0.8)",
                                    border: "1px solid rgba(246, 37, 90, 0.3)",
                                    borderRadius: "6px",
                                    color:
                                      recentlyOpenedPage ===
                                        Math.ceil(recentlyOpenedApps.length / 5)
                                        ? "rgba(246, 37, 90, 0.5)"
                                        : "#fff",
                                    cursor:
                                      recentlyOpenedPage ===
                                        Math.ceil(recentlyOpenedApps.length / 5)
                                        ? "not-allowed"
                                        : "pointer",
                                    fontSize: "12px",
                                    transition: "all 0.2s",
                                  }}
                                  onMouseEnter={e => {
                                    if (
                                      recentlyOpenedPage !==
                                      Math.ceil(recentlyOpenedApps.length / 5)
                                    ) {
                                      e.currentTarget.style.backgroundColor =
                                        "rgba(246, 37, 90, 1)"
                                    }
                                  }}
                                  onMouseLeave={e => {
                                    if (
                                      recentlyOpenedPage !==
                                      Math.ceil(recentlyOpenedApps.length / 5)
                                    ) {
                                      e.currentTarget.style.backgroundColor =
                                        "rgba(246, 37, 90, 0.8)"
                                    }
                                  }}
                                >
                                  Next
                                </button>
                              </div>
                            )}
                          </div>
                        )
                      )}
                    </motion.div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* ── Dashboard (2x2 spacious layout) ── */}
        <AnimatePresence>
          {scanned && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="container-fluid pb-16"
            >
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h1
                  className="mb-0"
                  style={{
                    fontSize: "22px",
                    fontWeight: 700,
                    color: "#fff",
                  }}
                >
                  {(appData?.game_info?.name || appData?.name || pendingAppName || "App")} - AI CMO Analysis
                </h1>
                {onBack && <BackButton onClick={onBack} />}
              </div>
              {/* Scanning Animation */}
              {scanning && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="mb-6"
                >
                  <div
                    style={{
                      backgroundColor: "rgba(246, 37, 90, 0.1)",
                      border: "1px solid rgba(246, 37, 90, 0.3)",
                      borderRadius: "12px",
                      padding: "20px",
                      marginBottom: "24px",
                    }}
                  >
                    <div className="d-flex align-items-center gap-3 mb-3">
                      <button
                        onClick={() => {
                          setScanned(false)
                          setUrl("")
                          syncAppIdInUrl(null)
                        }}
                        className="btn btn-sm d-flex align-items-center justify-content-center p-0"
                        style={{
                          backgroundColor: "transparent",
                          border: "none",
                          color: "#F6255A",
                          transition: "color 0.2s ease",
                          cursor: "pointer",
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = "#ffffff"}
                        onMouseLeave={e => e.currentTarget.style.color = "#F6255A"}
                        title="Cancel Search"
                      >
                        <ChevronLeft style={{ width: "18px", height: "18px" }} />
                      </button>
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                        style={{ color: "#F6255A" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <h2
                        style={{
                          fontSize: "18px",
                          fontWeight: "600",
                          color: "#F6255A",
                          margin: 0,
                        }}
                      >
                        AI CMO Analysis in Progress
                      </h2>
                    </div>
                    <div
                      style={{
                        backgroundColor: "rgba(23, 24, 33, 0.8)",
                        border: "1px solid #374151",
                        borderRadius: "8px",
                        padding: "16px",
                        fontFamily: "monospace",
                        fontSize: "13px",
                        color: "#10b981",
                        minHeight: "120px",
                      }}
                    >
                      <div className="space-y-2">
                        {scanningStage === 0 && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                          >
                            $ Initializing AI CMO...
                          </motion.div>
                        )}
                        {scanningStage >= 1 && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                          >
                            $ Initializing AI CMO...
                          </motion.div>
                        )}
                        {scanningStage >= 1 && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3, delay: 0.1 }}
                          >
                            {scanTarget ? `> Let me take a look at ${scanTarget}...` : "> Locating your app..."}
                          </motion.div>
                        )}
                        {scanningStage >= 2 && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3, delay: 0.1 }}
                          >
                            {"> Starting..."}
                          </motion.div>
                        )}
                        {scanningStage >= 3 && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3, delay: 0.1 }}
                          >
                            {"> Starting my deep dive - crawling all your data"}
                          </motion.div>
                        )}
                        {scanningStage >= 4 && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3, delay: 0.1 }}
                          >
                            {"> Found your app! Let me understand it"}
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Single Row: App Info + Analytics + Sentiment + KeyQuest AI */}
              <div className="row mb-4">
                {/* Panel 1: App Info */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="col-12 col-md-6 col-lg-2 aicmo-fill-col aicmo-auto-mobile"
                >
                  {scanning ? (
                    <SkeletonCard />
                  ) : (
                    <div
                      style={{
                        ...cardStyle,
                      }}
                      className="aicmo-fill-card aicmo-scroll"
                    >
                      {/* Back to Search — sits above the panel header */}
                      <div style={{ marginBottom: "8px" }}>
                        <button
                          onClick={() => {
                            setScanned(false)
                            setUrl("")
                            syncAppIdInUrl(null)
                          }}
                          className="btn btn-sm d-flex align-items-center gap-1"
                          style={{
                            backgroundColor: "transparent",
                            border: "none",
                            color: "#9ca3af",
                            fontSize: "12px",
                            padding: "2px 0",
                            transition: "color 0.2s ease",
                            cursor: "pointer",
                          }}
                          onMouseEnter={e => e.currentTarget.style.color = "#ffffff"}
                          onMouseLeave={e => e.currentTarget.style.color = "#9ca3af"}
                          title="Back to Search"
                        >
                          <ChevronLeft style={{ width: "14px", height: "14px" }} />
                          Back to Search
                        </button>
                      </div>
                      <div
                        style={headerStyle}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <div className="d-flex align-items-center gap-2">
                          <Building2
                            style={{
                              width: "16px",
                              height: "16px",
                              color: "#9ca3af",
                            }}
                          />
                          App Info
                        </div>
                        {appData?.game_info?.website_url ? (
                          <a
                            href={appData.game_info.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-link p-0"
                            title="Visit website"
                            style={{
                              color: "#4A9EFF",
                              transition: "color 0.3s ease",
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.color = "#2E7BD6"
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.color = "#4A9EFF"
                            }}
                          >
                            <ExternalLink
                              style={{
                                width: "14px",
                                height: "14px",
                              }}
                            />
                          </a>
                        ) : null}
                      </div>
                      <div className="card-body">
                        {/* App identity */}
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <div
                            className="rounded d-flex align-items-center justify-content-center"
                            style={{
                              width: "40px",
                              height: "40px",
                              backgroundColor: "rgba(246, 37, 90, 0.1)",
                              border: "1px solid rgba(246, 37, 90, 0.2)",
                              overflow: "hidden",
                              flexShrink: 0,
                              padding: "0px",
                            }}
                          >
                            {(() => {
                              const appName =
                                appData?.game_info?.name ||
                                appData?.policy?.policy_report?.app?.name ||
                                `App ID: ${appData?.app_id || "Unknown"}`

                              // Check if extracted_from starts with keyquest.io, if not, create custom URL
                              const shouldUseCustomUrl =
                                appData?.extracted_from &&
                                !appData.extracted_from
                                  .toLowerCase()
                                  .startsWith("https://keyquest.io/xr")

                              let imageUrl =
                                appData?.game_info?.portrait ||
                                appData?.game_info?.image_url ||
                                appData?.game_info?.icon

                              let appUrl = appData?.extracted_from
                              if (
                                shouldUseCustomUrl &&
                                appData?.game_info?.genre?.length > 0
                              ) {
                                const genre = appData.game_info.genre[0]
                                const slug = createSlug(appName)
                                appUrl = `https://keyquest.io/xr/${genre}/${slug}?id=${appData?.app_id || ""}`
                              }

                              return imageUrl ? (
                                <a
                                  href={appUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: "block",
                                    textDecoration: "none",
                                    color: "transparent",
                                    width: "100%",
                                    height: "100%",
                                    backgroundImage: `url(${imageUrl})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    backgroundRepeat: "no-repeat",
                                    borderRadius: "4px",
                                  }}
                                  onMouseEnter={e => {
                                    e.currentTarget.style.transform =
                                      "scale(1.05)"
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.transform = "scale(1)"
                                  }}
                                ></a>
                              ) : (
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: "#667eea",
                                    fontWeight: "600",
                                  }}
                                >
                                  {appName.charAt(0).toUpperCase()}
                                </span>
                              )
                            })()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="d-flex align-items-start gap-2">
                              {(() => {
                                const platformInfo = getPlatformInfo(
                                  appData?.extracted_from,
                                  platforms,
                                  getPlatformIcon,
                                  getPlatformDisplayName,
                                  appData,
                                )
                                return (
                                  <div className="d-flex align-items-center gap-2" style={{ width: "100%" }}>
                                    {(() => {
                                      const appName =
                                        appData?.game_info?.name ||
                                        appData?.policy?.policy_report?.app
                                          ?.name ||
                                        `App ID: ${appData?.app_id || "Unknown"}`

                                      // Check if extracted_from starts with keyquest.io, if not, create custom URL
                                      const shouldUseCustomUrl =
                                        appData?.extracted_from &&
                                        !appData.extracted_from
                                          .toLowerCase()
                                          .startsWith("https://keyquest.io/xr")

                                      let appUrl = appData?.extracted_from
                                      if (
                                        shouldUseCustomUrl &&
                                        appData?.game_info?.genre?.length > 0
                                      ) {
                                        const genre = appData.game_info.genre[0]
                                        const slug = createSlug(appName)
                                        appUrl = `https://keyquest.io/xr/${genre}/${slug}?id=${appData?.app_id || ""}`
                                      }

                                      return appData?.extracted_from ? (
                                        <a
                                          href={appUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            textDecoration: "none",
                                            color: "#ffffff",
                                            transition: "color 0.3s ease",
                                            flex: 1,
                                            minWidth: 0,
                                          }}
                                          onMouseEnter={e => {
                                            e.currentTarget.style.color =
                                              "#F6255A"
                                          }}
                                          onMouseLeave={e => {
                                            e.currentTarget.style.color =
                                              "#ffffff"
                                          }}
                                        >
                                          <h2
                                            className="fw-bold mb-0"
                                            style={{ fontSize: "12px" }}
                                          >
                                            {appName}
                                          </h2>
                                        </a>
                                      ) : (
                                        <h2
                                          className="fw-bold text-white mb-0"
                                          style={{ fontSize: "12px" }}
                                        >
                                          {appName}
                                        </h2>
                                      )
                                    })()}
                                    {platformInfo ? (
                                      <a
                                        href={platformInfo.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          color: "#9ca3af",
                                          transition: "color 0.3s ease",
                                          display: "flex",
                                          alignItems: "center",
                                          flexShrink: 0,
                                        }}
                                        onMouseEnter={e => {
                                          e.currentTarget.style.color =
                                            "#ffffff"
                                        }}
                                        onMouseLeave={e => {
                                          e.currentTarget.style.color =
                                            "#9ca3af"
                                        }}
                                        title={`View on ${platformInfo.name}`}
                                      >
                                        <img
                                          src={platformInfo.logo}
                                          alt={platformInfo.name}
                                          style={{
                                            // Pico's logo is a solid circle that fills its box,
                                            // so it reads larger than the padded Meta/Steam marks.
                                            width: platformInfo.id === "pico" ? "20px" : "28px",
                                            height: platformInfo.id === "pico" ? "20px" : "28px",
                                            objectFit: "contain",
                                          }}
                                        />
                                      </a>
                                    ) : null}
                                  </div>
                                )
                              })()}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <p
                                className="text-muted mb-0"
                                style={{ fontSize: "10px" }}
                              >
                                {appData?.game_info?.developer ||
                                  appData?.game_info?.publisher ||
                                  appData?.policy?.policy_report?.app
                                    ?.developer ||
                                  appData?.policy?.policy_report?.app
                                    ?.publisher ||
                                  "Unknown Developer"}
                              </p>
                              {/* {appData?.game_info?.website_url && (
                                <a
                                  href={appData.game_info.website_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "#4A9EFF",
                                    transition: "color 0.3s ease",
                                    display: "flex",
                                    alignItems: "center",
                                    flexShrink: 0,
                                  }}
                                  onMouseEnter={e => {
                                    e.currentTarget.style.color = "#2E7BD6"
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.color = "#4A9EFF"
                                  }}
                                >
                                  <ExternalLink
                                    style={{
                                      width: "12px",
                                      height: "12px",
                                    }}
                                  />
                                </a>
                              )} */}
                            </div>
                          </div>
                        </div>

                        {/* Key metrics */}
                        <div className="d-flex gap-2 align-items-center mb-2">
                          {appData?.game_info && (
                            <>
                              <div className="d-flex align-items-center gap-1">
                                <Star
                                  style={{
                                    width: "12px",
                                    height: "12px",
                                    color: "#F6255A",
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: "bold",
                                    color: "#ffffff",
                                  }}
                                >
                                  {formatMetricDecimal(appData.game_info.rating?.average, 2)}
                                </span>
                              </div>
                              <div className="d-flex align-items-center gap-1">
                                <Download
                                  style={{
                                    width: "12px",
                                    height: "12px",
                                    color: "#10b981",
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: "bold",
                                    color: "#ffffff",
                                  }}
                                >
                                  {formatMetricMagnitude(appData.game_info?.analytics?.d)}
                                </span>
                              </div>
                            </>
                          )}
                        </div>

                        <hr
                          className="my-2"
                          style={{ borderColor: "#374151" }}
                        />

                        {/* Details list */}
                        <div className="mb-2">
                          {/* Category and Price in same row */}
                          <div className="d-flex gap-3 mb-2">
                            <div className="d-flex align-items-start gap-2 flex-1">
                              <Gamepad2
                                className="mt-1"
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  color: "#9ca3af",
                                }}
                              />
                              <div>
                                <p
                                  className="mb-0"
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: "500",
                                    color: "#9ca3af",
                                  }}
                                >
                                  Category
                                </p>
                                <p
                                  className="mb-0"
                                  style={{ fontSize: "12px", color: "#ffffff" }}
                                >
                                  {appData?.game_info?.category || "Games"}
                                </p>
                              </div>
                            </div>
                            <div className="d-flex align-items-start gap-2 flex-1">
                              <Wallet
                                className="mt-1"
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  color: "#9ca3af",
                                }}
                              />
                              <div>
                                <p
                                  className="mb-0"
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: "500",
                                    color: "#9ca3af",
                                  }}
                                >
                                  Price
                                </p>
                                <p
                                  className="mb-0"
                                  style={{ fontSize: "12px", color: "#ffffff" }}
                                >
                                  {appData?.game_info?.price?.currency
                                    ? appData.game_info.price.currency === 'USD'
                                      ? `$${appData.game_info.price.amount}`
                                      : `${appData.game_info.price.currency} ${appData.game_info.price.amount}`
                                    : "N/A"}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Other details as separate rows */}
                          {[
                            {
                              icon: Layers,
                              label: "Genre",
                              value:
                                appData?.game_info?.genre?.slice(0, 2).join(", ") +
                                (appData?.game_info?.genre?.length > 2 ? "..." : "") || "N/A",
                            },
                          ].map(item => (
                            <div
                              key={item.label}
                              className="d-flex align-items-start gap-2 mb-2"
                            >
                              <item.icon
                                className="mt-1"
                                style={{
                                  width: "12px",
                                  height: "12px",
                                  color: "#9ca3af",
                                }}
                              />
                              <div>
                                <p
                                  className="mb-0"
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: "500",
                                    color: "#9ca3af",
                                  }}
                                >
                                  {item.label}
                                </p>
                                <p
                                  className="mb-0"
                                  style={{ fontSize: "12px", color: "#ffffff" }}
                                >
                                  {item.value}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>

                        <hr
                          className="my-2"
                          style={{ borderColor: "#374151" }}
                        />

                        {/* Checklist */}
                        <div>
                          <p
                            className="mb-1"
                            style={{
                              fontSize: "10px",
                              fontWeight: "600",
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              color: "#9ca3af",
                            }}
                          >
                            Submission Checklist
                          </p>
                          <div className="space-y-1">
                            {Object.entries(
                              appData?.policy?.policy_report
                                ?.submission_checklist || {},
                            ).map(([key, val]) => (
                              <div
                                key={key}
                                className="d-flex align-items-center gap-2"
                              >
                                {val === "provided" ? (
                                  <CheckCircle2
                                    style={{
                                      width: "12px",
                                      height: "12px",
                                      color: "#10b981",
                                    }}
                                  />
                                ) : (
                                  <AlertTriangle
                                    style={{
                                      width: "12px",
                                      height: "12px",
                                      color: "#f59e0b",
                                    }}
                                  />
                                )}
                                <span
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: "500",
                                    color: "#ffffff",
                                    opacity: 0.9,
                                  }}
                                >
                                  {key.replace(/_/g, " ")}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <hr
                          className="my-2"
                          style={{ borderColor: "#374151" }}
                        />
                        {/* Top Competitors */}
                        <div>
                          <p
                            className="mb-2"
                            style={{
                              fontSize: "10px",
                              fontWeight: "600",
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              color: "#9ca3af",
                            }}
                          >
                            Top Competitors
                          </p>

                          <div
                            className="space-y-2"
                            style={{
                              overflow: "visible",
                              position: "relative",
                            }}
                          >
                            {appData?.similar_apps?.map(app => {
                              const platformUrl = (() => {
                                if (app.platform === "meta" && app.item_id) {
                                  return `https://www.meta.com/experiences/${app.item_id}`
                                }
                                if (app.platform === "steam") {
                                  const steamId = String(app.item_id || "").replace(/^steam_/i, "")
                                  if (steamId) return `https://store.steampowered.com/app/${steamId}`
                                }
                                if (app.platform === "apple_vision") {
                                  const appleId = String(app.item_id || "").replace(/^apple_vision_/i, "")
                                  if (appleId) return `https://apps.apple.com/app/id${appleId}`
                                }
                                if (app.platform === "pico") {
                                  const picoId = String(app.item_id || "").replace(/^pico_/i, "")
                                  if (/^\d+$/.test(picoId)) return `https://store-global.picoxr.com/global/detail/1/${picoId}`
                                }
                                return app.url
                              })()

                              // Create custom KeyQuest URL
                              let customKeyQuestUrl = app.url

                              if (
                                app.platform &&
                                app.name &&
                                !app.url
                                  .toLowerCase()
                                  .startsWith("https://keyquest.io/xr")
                              ) {
                                const genre = app.genre?.[0] || "games"
                                const slug = createSlug(app.name)

                                customKeyQuestUrl = `https://keyquest.io/xr/${genre}/${slug}?id=${app.item_id || ""
                                  }`
                              }

                              const competitorPlatformInfo = {
                                name:
                                  app.platform?.charAt(0).toUpperCase() +
                                  app.platform?.slice(1),

                                logo:
                                  app.platform === "meta"
                                    ? metalogo
                                    : getPlatformIcon(app.platform),

                                url: platformUrl,
                              }

                              return (
                                <a
                                  key={app.item_id}
                                  href={customKeyQuestUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: "block",
                                    textDecoration: "none",
                                    backgroundColor: "rgba(55, 65, 81, 0.5)",
                                    borderRadius: "8px",
                                    padding: "4px",
                                    marginBottom: "8px",
                                    position: "relative",
                                    transition: "all 0.25s ease",
                                    overflow: "visible",
                                    zIndex: 1,
                                    width: "100%",
                                    maxWidth: "100%",
                                    transformOrigin: "center",
                                    cursor: "pointer",
                                  }}
                                  onMouseEnter={e => {
                                    e.currentTarget.style.backgroundColor =
                                      "rgba(55, 65, 81, 0.95)"

                                    e.currentTarget.style.transform =
                                      "translateY(-1px)"

                                    e.currentTarget.style.zIndex = "999"

                                    e.currentTarget.style.boxShadow =
                                      "0 4px 14px rgba(0,0,0,0.35)"

                                    const text = e.currentTarget.querySelector(
                                      ".app-name-container",
                                    )

                                    if (text) {
                                      text.style.overflow = "visible"
                                      text.style.textOverflow = "unset"
                                      text.style.whiteSpace = "normal"
                                    }
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.backgroundColor =
                                      "rgba(55, 65, 81, 0.5)"

                                    e.currentTarget.style.transform =
                                      "translateY(0px) scale(1)"

                                    e.currentTarget.style.zIndex = "1"

                                    e.currentTarget.style.boxShadow = "none"

                                    const text = e.currentTarget.querySelector(
                                      ".app-name-container",
                                    )

                                    if (text) {
                                      text.style.overflow = "hidden"
                                      text.style.textOverflow = "ellipsis"
                                      text.style.whiteSpace = "nowrap"
                                    }
                                  }}
                                >
                                  {/* TOP ROW */}
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: "10px",
                                    }}
                                  >
                                    {/* LEFT SIDE */}
                                    <div
                                      className="d-flex align-items-center gap-2"
                                      style={{
                                        minWidth: 0,
                                        flex: 1,
                                      }}
                                    >
                                      <img
                                        src={app.image_url}
                                        alt={app.name}
                                        style={{
                                          width: "20px",
                                          height: "20px",
                                          borderRadius: "4px",
                                          objectFit: "cover",
                                          flexShrink: 0,
                                        }}
                                        onError={e => {
                                          e.target.style.display = "none"
                                        }}
                                      />

                                      <div
                                        className="app-name-container"
                                        style={{
                                          fontSize: "12px",
                                          fontWeight: "500",
                                          color: "#ffffff",
                                          lineHeight: "1.4",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          minWidth: 0,
                                          transition: "all 0.25s ease",
                                        }}
                                      >
                                        {app.name}
                                      </div>
                                    </div>

                                    {/* RATING */}
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        marginLeft: "auto",
                                        flexShrink: 0,
                                      }}
                                    >
                                      <Star
                                        style={{
                                          width: "8px",
                                          height: "8px",
                                          color: "#F6255A",
                                        }}
                                      />

                                      <span
                                        style={{
                                          fontSize: "10px",
                                          fontFamily: "monospace",
                                          color: "#F6255A",
                                          fontWeight: "500",
                                          borderBottom: "1px solid #F6255A",
                                        }}
                                      >
                                        {(parseFloat(app.rating) || 0).toFixed(1)}
                                      </span>
                                    </div>
                                  </div>

                                  {/* PLATFORM ICON */}
                                  {/* {competitorPlatformInfo?.logo && (
                                    <div
                                      style={{
                                        marginTop: "8px",
                                      }}
                                    >
                                      <a
                                        href={platformUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={`View on ${competitorPlatformInfo.name}`}
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          textDecoration: "none",
                                        }}
                                      >
                                        <img
                                          src={competitorPlatformInfo.logo}
                                          alt={competitorPlatformInfo.name}
                                          style={{
                                            width: "18px",
                                            height: "18px",
                                            objectFit: "contain",
                                          }}
                                        />
                                      </a>
                                    </div>
                                  )} */}
                                </a>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>

                {/* Panel 2: Analytics */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="col-12 col-md-6 col-lg-3 aicmo-fill-col aicmo-auto-mobile"
                >
                  {scanning ? (
                    <SkeletonCard />
                  ) : (
                    <div style={cardStyle} className="aicmo-fill-card aicmo-scroll">
                      <div
                        style={headerStyle}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <div className="d-flex align-items-center gap-2">
                          <BarChart3
                            style={{
                              width: "16px",
                              height: "16px",
                              color: "#9ca3af",
                            }}
                          />
                          Analytics
                        </div>
                      </div>
                      <div className="card-body">
                        {/* Metrics ribbon */}
                        <div className="row g-2 mb-4">
                          {appData?.game_info ? (
                            <>
                              <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.25 }}
                                className="col-6"
                              >
                                <div style={secondaryCardStyle}>
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span style={labelStyle}>Revenue</span>
                                    <DollarSign
                                      style={{
                                        width: "12px",
                                        height: "12px",
                                        color: "#9ca3af",
                                      }}
                                    />
                                  </div>
                                  <p
                                    style={{ ...valueStyle, fontSize: "12px" }}
                                  >
                                    {formatMetricMagnitude(appData.game_info?.analytics?.revenue, "$")}
                                  </p>
                                  <div className="d-flex align-items-center gap-1 mt-1">
                                    {metricChanges.revenue?.percentage > 0 ? (
                                      <ArrowUpRight
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          color: "#10b981",
                                        }}
                                      />
                                    ) : metricChanges.revenue?.percentage < 0 ? (
                                      <ArrowDownRight
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          color: "#ef4444",
                                        }}
                                      />
                                    ) : (
                                      <ArrowUpRight
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          color: "#9ca3af",
                                        }}
                                      />
                                    )}
                                    <span
                                      className="font-monospace"
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "500",
                                        color: metricChanges.revenue?.percentage > 0
                                          ? "#10b981"
                                          : metricChanges.revenue?.percentage < 0
                                            ? "#ef4444"
                                            : "#9ca3af",
                                      }}
                                    >
                                      {metricChanges.revenue
                                        ? `${metricChanges.revenue.percentage > 0 ? "+" : metricChanges.revenue.percentage < 0 ? "-" : ""}${Math.abs(metricChanges.revenue.percentage).toFixed(1)}%`
                                        : "N/A"}
                                    </span>
                                  </div>
                                </div>
                              </motion.div>
                              <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.3 }}
                                className="col-6"
                              >
                                <div style={secondaryCardStyle}>
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span style={labelStyle}>Downloads</span>
                                    <Download
                                      style={{
                                        width: "12px",
                                        height: "12px",
                                        color: "#9ca3af",
                                      }}
                                    />
                                  </div>
                                  <p
                                    style={{ ...valueStyle, fontSize: "12px" }}
                                  >
                                    {formatMetricMagnitude(appData.game_info?.analytics?.d)}
                                  </p>
                                  <div className="d-flex align-items-center gap-1 mt-1">
                                    {metricChanges.downloads?.percentage > 0 ? (
                                      <ArrowUpRight
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          color: "#10b981",
                                        }}
                                      />
                                    ) : metricChanges.downloads?.percentage < 0 ? (
                                      <ArrowDownRight
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          color: "#ef4444",
                                        }}
                                      />
                                    ) : (
                                      <ArrowUpRight
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          color: "#9ca3af",
                                        }}
                                      />
                                    )}
                                    <span
                                      className="font-monospace"
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "500",
                                        color: metricChanges.downloads?.percentage > 0
                                          ? "#10b981"
                                          : metricChanges.downloads?.percentage < 0
                                            ? "#ef4444"
                                            : "#9ca3af",
                                      }}
                                    >
                                      {metricChanges.downloads
                                        ? `${metricChanges.downloads.percentage > 0 ? "+" : metricChanges.downloads.percentage < 0 ? "-" : ""}${Math.abs(metricChanges.downloads.percentage).toFixed(1)}%`
                                        : "N/A"}
                                    </span>
                                  </div>
                                </div>
                              </motion.div>
                              <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.35 }}
                                className="col-6"
                              >
                                <div style={secondaryCardStyle}>
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span style={labelStyle}>Reviews</span>
                                    <Users
                                      style={{
                                        width: "12px",
                                        height: "12px",
                                        color: "#9ca3af",
                                      }}
                                    />
                                  </div>
                                  <p
                                    style={{ ...valueStyle, fontSize: "12px" }}
                                  >
                                    {formatMetricMagnitude(appData.game_info?.analytics?.urv)}
                                  </p>
                                  <div className="d-flex align-items-center gap-1 mt-1">
                                    {metricChanges.downloads?.percentage > 0 ? (
                                      <ArrowUpRight
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          color: "#10b981",
                                        }}
                                      />
                                    ) : metricChanges.downloads?.percentage < 0 ? (
                                      <ArrowDownRight
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          color: "#ef4444",
                                        }}
                                      />
                                    ) : (
                                      <ArrowUpRight
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          color: "#9ca3af",
                                        }}
                                      />
                                    )}
                                    <span
                                      className="font-monospace"
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "500",
                                        color: metricChanges.downloads?.percentage > 0
                                          ? "#10b981"
                                          : metricChanges.downloads?.percentage < 0
                                            ? "#ef4444"
                                            : "#9ca3af",
                                      }}
                                    >
                                      {metricChanges.downloads
                                        ? `${metricChanges.downloads.percentage > 0 ? "+" : metricChanges.downloads.percentage < 0 ? "-" : ""}${Math.abs(metricChanges.downloads.percentage).toFixed(1)}%`
                                        : "N/A"}
                                    </span>
                                  </div>
                                </div>
                              </motion.div>
                              <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.4 }}
                                className="col-6"
                              >
                                <div style={secondaryCardStyle}>
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span style={labelStyle}>KU</span>
                                    <Target
                                      style={{
                                        width: "12px",
                                        height: "12px",
                                        color: "#9ca3af",
                                      }}
                                    />
                                  </div>
                                  <p
                                    style={{ ...valueStyle, fontSize: "12px" }}
                                  >
                                    {formatMetricDecimal(appData.game_info.analytics?.ku, 2)}
                                  </p>
                                  <div className="d-flex align-items-center gap-1 mt-1">
                                    {metricChanges.ku?.percentage > 0 ? (
                                      <ArrowUpRight
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          color: "#10b981",
                                        }}
                                      />
                                    ) : metricChanges.ku?.percentage < 0 ? (
                                      <ArrowDownRight
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          color: "#ef4444",
                                        }}
                                      />
                                    ) : (
                                      <ArrowUpRight
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          color: "#9ca3af",
                                        }}
                                      />
                                    )}
                                    <span
                                      className="font-monospace"
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "500",
                                        color: metricChanges.ku?.percentage > 0
                                          ? "#10b981"
                                          : metricChanges.ku?.percentage < 0
                                            ? "#ef4444"
                                            : "#9ca3af",
                                      }}
                                    >
                                      {metricChanges.ku
                                        ? `${metricChanges.ku.percentage > 0 ? "+" : metricChanges.ku.percentage < 0 ? "-" : ""}${Math.abs(metricChanges.ku.percentage).toFixed(2)}%`
                                        : "N/A"}
                                    </span>
                                  </div>
                                </div>
                              </motion.div>
                            </>
                          ) : (
                            <>
                              <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.25 }}
                                className="col-6 col-sm-3"
                              >
                                <div style={secondaryCardStyle}>
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span style={labelStyle}>Compliance</span>
                                    <ShieldAlert
                                      style={{
                                        width: "14px",
                                        height: "14px",
                                        color: "#9ca3af",
                                      }}
                                    />
                                  </div>
                                  <p
                                    style={{ ...valueStyle, fontSize: "16px" }}
                                  >
                                    {appData?.policy?.policy_report?.summary
                                      ?.compliance_status || "N/A"}
                                  </p>
                                  <div className="d-flex align-items-center gap-1 mt-2">
                                    <AlertTriangle
                                      style={{
                                        width: "12px",
                                        height: "12px",
                                        color:
                                          appData?.policy?.policy_report
                                            ?.summary?.compliance_status ===
                                            "compliant"
                                            ? "#10b981"
                                            : "#ef4444",
                                      }}
                                    />
                                    <span
                                      className="font-monospace"
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "500",
                                        color:
                                          appData?.policy?.policy_report
                                            ?.summary?.compliance_status ===
                                            "compliant"
                                            ? "#10b981"
                                            : "#ef4444",
                                      }}
                                    >
                                      {appData?.policy?.policy_report?.summary
                                        ?.compliance_status === "compliant"
                                        ? "Good"
                                        : "Needs Attention"}
                                    </span>
                                  </div>
                                </div>
                              </motion.div>
                              <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.3 }}
                                className="col-6 col-sm-3"
                              >
                                <div style={secondaryCardStyle}>
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span style={labelStyle}>Issues</span>
                                    <XCircle
                                      style={{
                                        width: "14px",
                                        height: "14px",
                                        color: "#9ca3af",
                                      }}
                                    />
                                  </div>
                                  <p
                                    style={{ ...valueStyle, fontSize: "16px" }}
                                  >
                                    {appData?.policy?.policy_report
                                      ?.blocking_issues?.length || 0}
                                  </p>
                                  <div className="d-flex align-items-center gap-1 mt-2">
                                    <AlertTriangle
                                      style={{
                                        width: "12px",
                                        height: "12px",
                                        color:
                                          (appData?.policy?.policy_report
                                            ?.blocking_issues?.length || 0) > 0
                                            ? "#ef4444"
                                            : "#10b981",
                                      }}
                                    />
                                    <span
                                      className="font-monospace"
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "500",
                                        color:
                                          (appData?.policy?.policy_report
                                            ?.blocking_issues?.length || 0) > 0
                                            ? "#ef4444"
                                            : "#10b981",
                                      }}
                                    >
                                      {(appData?.policy?.policy_report
                                        ?.blocking_issues?.length || 0) > 0
                                        ? "Blocking"
                                        : "Clear"}
                                    </span>
                                  </div>
                                </div>
                              </motion.div>
                              <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.35 }}
                                className="col-6 col-sm-3"
                              >
                                <div style={secondaryCardStyle}>
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span style={labelStyle}>Improvements</span>
                                    <TrendingUp
                                      style={{
                                        width: "14px",
                                        height: "14px",
                                        color: "#9ca3af",
                                      }}
                                    />
                                  </div>
                                  <p
                                    style={{ ...valueStyle, fontSize: "16px" }}
                                  >
                                    {appData?.policy?.policy_report
                                      ?.recommended_improvements?.length || 0}
                                  </p>
                                  <div className="d-flex align-items-center gap-1 mt-2">
                                    <ArrowUpRight
                                      style={{
                                        width: "12px",
                                        height: "12px",
                                        color: "#f59e0b",
                                      }}
                                    />
                                    <span
                                      className="font-monospace"
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "500",
                                        color: "#f59e0b",
                                      }}
                                    >
                                      Suggestions
                                    </span>
                                  </div>
                                </div>
                              </motion.div>
                              <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.4 }}
                                className="col-6 col-sm-3"
                              >
                                <div style={secondaryCardStyle}>
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span style={labelStyle}>Confidence</span>
                                    <Target
                                      style={{
                                        width: "14px",
                                        height: "14px",
                                        color: "#9ca3af",
                                      }}
                                    />
                                  </div>
                                  <p
                                    style={{ ...valueStyle, fontSize: "16px" }}
                                  >
                                    {Math.round(
                                      (appData?.policy?.policy_report?.summary
                                        ?.confidence || 0) * 100,
                                    )}
                                    %
                                  </p>
                                  <div className="d-flex align-items-center gap-1 mt-2">
                                    <CheckCircle2
                                      style={{
                                        width: "12px",
                                        height: "12px",
                                        color: "#10b981",
                                      }}
                                    />
                                    <span
                                      className="font-monospace"
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "500",
                                        color: "#10b981",
                                      }}
                                    >
                                      Score
                                    </span>
                                  </div>
                                </div>
                              </motion.div>
                            </>
                          )}
                        </div>

                        {/* Rating Breakdown */}
                        <hr
                          className="my-3"
                          style={{ borderColor: "#374151" }}
                        />

                        <div className="h-100">
                          <div style={headerStyle}>
                            <div className="d-flex align-items-center gap-2">
                              <ThumbsUp
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  color: "#9ca3af",
                                }}
                              />
                              Sentiment Analysis
                              {/* ... */}
                            </div>
                          </div>
                          <div className="card-body">
                            {/* Sentiment Overview */}
                            <div className="row">
                              {/* DONUT CHART */}
                              <div className="col-md-6">
                                <div className="d-flex align-items-center gap-3">
                                  {/* DONUT */}
                                  <div
                                    style={{
                                      width: 120,
                                      height: 120,
                                      borderRadius: "50%",
                                      border: "2px solid #374151",
                                      background:
                                        sentimentHoverIndex === 0
                                          ? `conic-gradient(
                                    #4ade80 0deg ${(appData?.sentiment?.sentiment_breakdown?.positive_percent || 60) * 3.6}deg,
                                    #fbbf24 ${(appData?.sentiment?.sentiment_breakdown?.positive_percent || 60) * 3.6}deg ${((appData?.sentiment?.sentiment_breakdown?.positive_percent || 60) + Math.max(appData?.sentiment?.sentiment_breakdown?.neutral_percent || 0, 5)) * 3.6}deg,
                                    #ef4444 ${((appData?.sentiment?.sentiment_breakdown?.positive_percent || 60) + Math.max(appData?.sentiment?.sentiment_breakdown?.neutral_percent || 0, 5)) * 3.6}deg 360deg
                                  )`
                                          : sentimentHoverIndex === 1
                                            ? `conic-gradient(
                                      #2d5a3d 0deg ${(appData?.sentiment?.sentiment_breakdown?.positive_percent || 60) * 3.6}deg,
                                      #fbbf24 ${(appData?.sentiment?.sentiment_breakdown?.positive_percent || 60) * 3.6}deg ${((appData?.sentiment?.sentiment_breakdown?.positive_percent || 60) + Math.max(appData?.sentiment?.sentiment_breakdown?.neutral_percent || 0, 5)) * 3.6}deg,
                                      #8b2635 ${((appData?.sentiment?.sentiment_breakdown?.positive_percent || 60) + Math.max(appData?.sentiment?.sentiment_breakdown?.neutral_percent || 0, 5)) * 3.6}deg 360deg
                                    )`
                                            : sentimentHoverIndex === 2
                                              ? `conic-gradient(
                                      #2d5a3d 0deg ${(appData?.sentiment?.sentiment_breakdown?.positive_percent || 60) * 3.6}deg,
                                      #fbbf24 ${(appData?.sentiment?.sentiment_breakdown?.positive_percent || 60) * 3.6}deg ${((appData?.sentiment?.sentiment_breakdown?.positive_percent || 60) + Math.max(appData?.sentiment?.sentiment_breakdown?.neutral_percent || 0, 5)) * 3.6}deg,
                                      #ef4444 ${((appData?.sentiment?.sentiment_breakdown?.positive_percent || 60) + Math.max(appData?.sentiment?.sentiment_breakdown?.neutral_percent || 0, 5)) * 3.6}deg 360deg
                                    )`
                                              : `conic-gradient(
                                      #4ade80 0deg ${(appData?.sentiment?.sentiment_breakdown?.positive_percent || 60) * 3.6}deg,
                                      #fbbf24 ${(appData?.sentiment?.sentiment_breakdown?.positive_percent || 60) * 3.6}deg ${((appData?.sentiment?.sentiment_breakdown?.positive_percent || 60) + Math.max(appData?.sentiment?.sentiment_breakdown?.neutral_percent || 0, 5)) * 3.6}deg,
                                      #ef4444 ${((appData?.sentiment?.sentiment_breakdown?.positive_percent || 60) + Math.max(appData?.sentiment?.sentiment_breakdown?.neutral_percent || 0, 5)) * 3.6}deg 360deg
                                    )`,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexShrink: 0,
                                      minWidth: 120,
                                      minHeight: 120,
                                      position: "relative",
                                      cursor: "pointer",
                                      transition: "all 0.3s ease",
                                      boxShadow:
                                        sentimentHoverIndex !== null
                                          ? "0 0 12px rgba(246, 37, 90, 0.15)"
                                          : "none",
                                    }}
                                    onMouseMove={e => {
                                      const rect =
                                        e.currentTarget.getBoundingClientRect()
                                      const centerX = rect.left + rect.width / 2
                                      const centerY = rect.top + rect.height / 2
                                      const angle =
                                        Math.atan2(
                                          e.clientY - centerY,
                                          e.clientX - centerX,
                                        ) *
                                        (180 / Math.PI) +
                                        90
                                      const normalizedAngle =
                                        angle < 0 ? angle + 360 : angle

                                      const positivePercent =
                                        appData?.sentiment?.sentiment_breakdown
                                          ?.positive_percent || 60
                                      const neutralPercent = Math.max(
                                        appData?.sentiment?.sentiment_breakdown
                                          ?.neutral_percent || 0,
                                        5,
                                      )

                                      let index = -1
                                      if (
                                        normalizedAngle <
                                        positivePercent * 3.6
                                      ) {
                                        index = 0
                                        setSentimentTooltip({
                                          name: "Positive",
                                          value: positivePercent,
                                          color: "#4ade80",
                                        })
                                      } else if (
                                        normalizedAngle <
                                        (positivePercent + neutralPercent) * 3.6
                                      ) {
                                        index = 1
                                        setSentimentTooltip({
                                          name: "Neutral",
                                          value: neutralPercent,
                                          color: "#fbbf24",
                                        })
                                      } else {
                                        index = 2
                                        setSentimentTooltip({
                                          name: "Negative",
                                          value:
                                            appData?.sentiment
                                              ?.sentiment_breakdown
                                              ?.negative_percent || 40,
                                          color: "#ef4444",
                                        })
                                      }
                                      setSentimentHoverIndex(index)
                                    }}
                                    onMouseLeave={() => {
                                      setSentimentHoverIndex(null)
                                      setSentimentTooltip(null)
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: 75,
                                        height: 75,
                                        borderRadius: "50%",
                                        background: "#1c1f2e",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexDirection: "column",
                                      }}
                                    >
                                      <div
                                        className="text-white mb-0"
                                        style={{
                                          fontSize: "16px",
                                          fontWeight: 500,
                                          textAlign: "center",
                                        }}
                                      >
                                        {appData?.sentiment?.sentiment_breakdown
                                          ?.positive_percent || 60}
                                      </div>
                                      <div
                                        className="text-white mb-0"
                                        style={{
                                          fontSize: "12px",
                                          fontWeight: 500,
                                          textAlign: "center",
                                        }}
                                      >
                                        %
                                      </div>
                                    </div>

                                    {/* Tooltip */}
                                    {sentimentTooltip && (
                                      <motion.div
                                        initial={{
                                          opacity: 0,
                                          scale: 0.8,
                                          y: -10,
                                        }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        exit={{
                                          opacity: 0,
                                          scale: 0.8,
                                          y: -10,
                                        }}
                                        transition={{ duration: 0.2 }}
                                        style={{
                                          position: "absolute",
                                          top: "-55px",
                                          left: "50%",
                                          transform: "translateX(-50%)",
                                          backgroundColor:
                                            "rgba(23, 24, 33, 0.98)",
                                          border: `2px solid ${sentimentTooltip.color}`,
                                          borderRadius: "8px",
                                          padding: "10px 14px",
                                          whiteSpace: "nowrap",
                                          zIndex: 1000,
                                          boxShadow: `0 4px 12px ${sentimentTooltip.color}25`,
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            fontWeight: "600",
                                            color: sentimentTooltip.color,
                                            marginBottom: "4px",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                          }}
                                        >
                                          {sentimentTooltip.name}
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "18px",
                                            fontWeight: "bold",
                                            color: sentimentTooltip.color,
                                          }}
                                        >
                                          {sentimentTooltip.value}%
                                        </div>
                                      </motion.div>
                                    )}
                                  </div>

                                  {/* LEGEND */}
                                  <div className="d-flex flex-column gap-2">
                                    <motion.div
                                      className="d-flex align-items-center gap-2"
                                      style={{
                                        cursor: "pointer",
                                        padding: "6px 10px",
                                        borderRadius: "6px",
                                        backgroundColor:
                                          sentimentHoverIndex === 0
                                            ? "rgba(74, 222, 128, 0.1)"
                                            : "transparent",
                                        border:
                                          sentimentHoverIndex === 0
                                            ? "1px solid rgba(74, 222, 128, 0.5)"
                                            : "1px solid transparent",
                                        opacity:
                                          sentimentHoverIndex === null ||
                                            sentimentHoverIndex === 0
                                            ? 1
                                            : 0.4,
                                        transition: "all 0.2s ease",
                                        transform:
                                          sentimentHoverIndex === 0
                                            ? "scale(1.05)"
                                            : "scale(1)",
                                      }}
                                      onMouseEnter={() => {
                                        setSentimentHoverIndex(0)
                                        setSentimentTooltip({
                                          name: "Positive",
                                          value:
                                            appData?.sentiment
                                              ?.sentiment_breakdown
                                              ?.positive_percent || 60,
                                          color: "#4ade80",
                                        })
                                      }}
                                      onMouseLeave={() => {
                                        setSentimentHoverIndex(null)
                                        setSentimentTooltip(null)
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          borderRadius: "50%",
                                          backgroundColor: "#4ade80",
                                        }}
                                      ></div>
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "#ffffff",
                                          fontWeight: "500",
                                        }}
                                      >
                                        Positive
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#9ca3af",
                                        }}
                                      >
                                        {appData?.sentiment?.sentiment_breakdown
                                          ?.positive_percent || 60}
                                        %
                                      </span>
                                    </motion.div>

                                    <motion.div
                                      className="d-flex align-items-center gap-2"
                                      style={{
                                        cursor: "pointer",
                                        padding: "6px 10px",
                                        borderRadius: "6px",
                                        backgroundColor:
                                          sentimentHoverIndex === 1
                                            ? "rgba(251, 191, 36, 0.1)"
                                            : "transparent",
                                        border:
                                          sentimentHoverIndex === 1
                                            ? "1px solid rgba(251, 191, 36, 0.5)"
                                            : "1px solid transparent",
                                        opacity:
                                          sentimentHoverIndex === null ||
                                            sentimentHoverIndex === 1
                                            ? 1
                                            : 0.4,
                                        transition: "all 0.2s ease",
                                        transform:
                                          sentimentHoverIndex === 1
                                            ? "scale(1.05)"
                                            : "scale(1)",
                                      }}
                                      onMouseEnter={() => {
                                        setSentimentHoverIndex(1)
                                        setSentimentTooltip({
                                          name: "Neutral",
                                          value: Math.max(
                                            appData?.sentiment
                                              ?.sentiment_breakdown
                                              ?.neutral_percent || 0,
                                            5,
                                          ),
                                          color: "#fbbf24",
                                        })
                                      }}
                                      onMouseLeave={() => {
                                        setSentimentHoverIndex(null)
                                        setSentimentTooltip(null)
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          borderRadius: "50%",
                                          backgroundColor: "#fbbf24",
                                        }}
                                      ></div>
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "#ffffff",
                                          fontWeight: "500",
                                        }}
                                      >
                                        Neutral
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#9ca3af",
                                        }}
                                      >
                                        {appData?.sentiment?.sentiment_breakdown
                                          ?.neutral_percent || 0}
                                        %
                                      </span>
                                    </motion.div>

                                    <motion.div
                                      className="d-flex align-items-center gap-2"
                                      style={{
                                        cursor: "pointer",
                                        padding: "6px 10px",
                                        borderRadius: "6px",
                                        backgroundColor:
                                          sentimentHoverIndex === 2
                                            ? "rgba(239, 68, 68, 0.1)"
                                            : "transparent",
                                        border:
                                          sentimentHoverIndex === 2
                                            ? "1px solid rgba(239, 68, 68, 0.5)"
                                            : "1px solid transparent",
                                        opacity:
                                          sentimentHoverIndex === null ||
                                            sentimentHoverIndex === 2
                                            ? 1
                                            : 0.4,
                                        transition: "all 0.2s ease",
                                        transform:
                                          sentimentHoverIndex === 2
                                            ? "scale(1.05)"
                                            : "scale(1)",
                                      }}
                                      onMouseEnter={() => {
                                        setSentimentHoverIndex(2)
                                        setSentimentTooltip({
                                          name: "Negative",
                                          value:
                                            appData?.sentiment
                                              ?.sentiment_breakdown
                                              ?.negative_percent || 40,
                                          color: "#ef4444",
                                        })
                                      }}
                                      onMouseLeave={() => {
                                        setSentimentHoverIndex(null)
                                        setSentimentTooltip(null)
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: "12px",
                                          height: "12px",
                                          borderRadius: "50%",
                                          backgroundColor: "#ef4444",
                                        }}
                                      ></div>
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "#ffffff",
                                          fontWeight: "500",
                                        }}
                                      >
                                        Negative
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#9ca3af",
                                        }}
                                      >
                                        {appData?.sentiment?.sentiment_breakdown
                                          ?.negative_percent || 40}
                                        %
                                      </span>
                                    </motion.div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Sentiment Themes */}
                            <hr
                              className="my-3"
                              style={{ borderColor: "#374151" }}
                            />
                            <div>
                              <p
                                className="mb-2"
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                  color: "#9ca3af",
                                }}
                              >
                                Sentiment Themes
                              </p>

                              {/* Positive Themes */}
                              {appData?.sentiment?.top_positive_themes?.length >
                                0 && (
                                  <div className="mb-3">
                                    <p
                                      className="mb-1"
                                      style={{
                                        fontSize: "11px",
                                        color: "#4ade80",
                                        fontWeight: "500",
                                      }}
                                    >
                                      Positive Themes
                                    </p>
                                    {appData.sentiment.top_positive_themes.map(
                                      (theme, i) => (
                                        <div className="pdf-no-break p-2 rounded mb-2" key={i}
                                          style={{
                                            backgroundColor:
                                              "rgba(74, 222, 128, 0.1)",
                                            border:
                                              "1px solid rgba(74, 222, 128, 0.2)",
                                          }}
                                        >
                                          <div
                                            className="d-flex align-items-center gap-1 mb-1"
                                            style={{ cursor: "pointer" }}
                                            onClick={() =>
                                              setExpandedThemeBlocks(prev => ({
                                                ...prev,
                                                [`positive-${i}`]:
                                                  !prev[`positive-${i}`],
                                              }))
                                            }
                                          >
                                            <ChevronDown
                                              style={{
                                                width: "10px",
                                                height: "10px",
                                                color: "#4ade80",
                                                transform: expandedThemeBlocks[
                                                  `positive-${i}`
                                                ]
                                                  ? "rotate(180deg)"
                                                  : "rotate(0deg)",
                                                transition: "transform 0.2s ease",
                                              }}
                                            />
                                            <p
                                              className="mb-0"
                                              style={{
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                color: "#4ade80",
                                              }}
                                            >
                                              {theme.theme}
                                            </p>
                                          </div>
                                          {expandedThemeBlocks[
                                            `positive-${i}`
                                          ] && (
                                              <div
                                                className="text-muted"
                                                style={{
                                                  fontSize: "10px",
                                                  color: "#9ca3af",
                                                }}
                                              >
                                                {theme.examples?.map(
                                                  (example, j) => (
                                                    <div key={j} className="mb-1">
                                                      • "{example}"
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                            )}
                                        </div>
                                      ),
                                    )}
                                  </div>
                                )}

                              {/* Negative Themes */}
                              {appData?.sentiment?.top_negative_themes?.length >
                                0 && (
                                  <div className="mb-3">
                                    <p
                                      className="mb-1"
                                      style={{
                                        fontSize: "11px",
                                        color: "#ef4444",
                                        fontWeight: "500",
                                      }}
                                    >
                                      Negative Themes
                                    </p>
                                    {appData.sentiment.top_negative_themes.map(
                                      (theme, i) => (
                                        <div className="pdf-no-break p-2 rounded mb-2" key={i}
                                          style={{
                                            backgroundColor:
                                              "rgba(239, 68, 68, 0.1)",
                                            border:
                                              "1px solid rgba(239, 68, 68, 0.2)",
                                          }}
                                        >
                                          <div
                                            className="d-flex align-items-center gap-1 mb-1"
                                            style={{ cursor: "pointer" }}
                                            onClick={() =>
                                              setExpandedThemeBlocks(prev => ({
                                                ...prev,
                                                [`negative-${i}`]:
                                                  !prev[`negative-${i}`],
                                              }))
                                            }
                                          >
                                            <ChevronDown
                                              style={{
                                                width: "10px",
                                                height: "10px",
                                                color: "#ef4444",
                                                transform: expandedThemeBlocks[
                                                  `negative-${i}`
                                                ]
                                                  ? "rotate(180deg)"
                                                  : "rotate(0deg)",
                                                transition: "transform 0.2s ease",
                                              }}
                                            />
                                            <p
                                              className="mb-0"
                                              style={{
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                color: "#ef4444",
                                              }}
                                            >
                                              {theme.issue}
                                            </p>
                                          </div>
                                          {expandedThemeBlocks[
                                            `negative-${i}`
                                          ] && (
                                              <div
                                                className="text-muted"
                                                style={{
                                                  fontSize: "10px",
                                                  color: "#9ca3af",
                                                }}
                                              >
                                                {theme.examples?.map(
                                                  (example, j) => (
                                                    <div key={j} className="mb-1">
                                                      • "{example}"
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                            )}
                                        </div>
                                      ),
                                    )}
                                  </div>
                                )}

                              {/* Bug Complaints */}
                              {appData?.sentiment?.bug_complaints?.length >
                                0 && (
                                  <div className="mb-3">
                                    <p
                                      className="mb-1"
                                      style={{
                                        fontSize: "11px",
                                        color: "#f59e0b",
                                        fontWeight: "500",
                                      }}
                                    >
                                      Bug Complaints
                                    </p>
                                    {appData.sentiment.bug_complaints.map(
                                      (bug, i) => (
                                        <div className="pdf-no-break p-2 rounded mb-2" key={i}
                                          style={{
                                            backgroundColor:
                                              "rgba(245, 158, 11, 0.1)",
                                            border:
                                              "1px solid rgba(245, 158, 11, 0.2)",
                                          }}
                                        >
                                          <div
                                            className="d-flex align-items-center gap-1 mb-1"
                                            style={{ cursor: "pointer" }}
                                            onClick={() =>
                                              setExpandedThemeBlocks(prev => ({
                                                ...prev,
                                                [`bug-${i}`]: !prev[`bug-${i}`],
                                              }))
                                            }
                                          >
                                            <ChevronDown
                                              style={{
                                                width: "10px",
                                                height: "10px",
                                                color: "#f59e0b",
                                                transform: expandedThemeBlocks[
                                                  `bug-${i}`
                                                ]
                                                  ? "rotate(180deg)"
                                                  : "rotate(0deg)",
                                                transition: "transform 0.2s ease",
                                              }}
                                            />
                                            <p
                                              className="mb-0"
                                              style={{
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                color: "#f59e0b",
                                              }}
                                            >
                                              {bug.bug}
                                            </p>
                                          </div>
                                          {expandedThemeBlocks[`bug-${i}`] && (
                                            <div
                                              className="text-muted"
                                              style={{
                                                fontSize: "10px",
                                                color: "#9ca3af",
                                              }}
                                            >
                                              {bug.examples?.map((example, j) => (
                                                <div key={j} className="mb-1">
                                                  • "{example}"
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      ),
                                    )}
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>

                {/* Panel 3: Sentiment */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="col-12 col-md-6 col-lg-3 aicmo-fill-col"
                >
                  {scanning ? (
                    <SkeletonCard />
                  ) : (
                    <div style={cardStyle} className="aicmo-fill-card aicmo-scroll">
                      <ActionsFeedPanel
                        actionsFeed={appData?.actions_feed}
                        appId={appData?.app_id || appData?.game_info?.app_id}
                        headerStyle={headerStyle}
                      />

                      <hr className="my-3" style={{ borderColor: "#374151" }} />
                      <div>
                        <p
                          className="mb-2"
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            color: "#9ca3af",
                          }}
                        >
                          Policy Issues & Recommendations
                        </p>

                        {/* Blocking Issues */}
                        {appData?.policy?.policy_report?.blocking_issues
                          ?.length > 0 && (
                            <div className="mb-3">
                              <p
                                className="mb-1"
                                style={{
                                  fontSize: "11px",
                                  color: "#ef4444",
                                  fontWeight: "500",
                                }}
                              >
                                Blocking Issues
                              </p>
                              {appData.policy.policy_report.blocking_issues.map(
                                (issue, i) => (
                                  <div className="pdf-no-break p-2 rounded mb-2" key={i}
                                    style={{
                                      backgroundColor: "rgba(239, 68, 68, 0.1)",
                                      border: "1px solid rgba(239, 68, 68, 0.2)",
                                    }}
                                  >
                                    <p
                                      className="mb-1"
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        color: "#ef4444",
                                      }}
                                    >
                                      {issue.title || "Blocking Issue"}
                                    </p>
                                    <p
                                      className="mb-0"
                                      style={{
                                        fontSize: "10px",
                                        color: "#9ca3af",
                                      }}
                                    >
                                      {issue.description ||
                                        "No description available"}
                                    </p>
                                  </div>
                                ),
                              )}
                            </div>
                          )}

                        {/* Recommended Improvements */}
                        {appData?.policy?.policy_report
                          ?.recommended_improvements?.length > 0 && (
                            <div className="mb-3">
                              <p
                                className="mb-1"
                                style={{
                                  fontSize: "11px",
                                  color: "#f59e0b",
                                  fontWeight: "500",
                                }}
                              >
                                Recommended Improvements
                              </p>
                              {appData.policy.policy_report.recommended_improvements.map(
                                (improvement, i) => (
                                  <div className="pdf-no-break p-2 rounded mb-2" key={i}
                                    style={{
                                      backgroundColor: "rgba(245, 158, 11, 0.1)",
                                      border: "1px solid rgba(245, 158, 11, 0.2)",
                                    }}
                                  >
                                    <div
                                      className="d-flex align-items-center gap-1 mb-1"
                                      style={{ cursor: "pointer" }}
                                      onClick={() =>
                                        setExpandedThemeBlocks(prev => ({
                                          ...prev,
                                          [`improvement-${i}`]:
                                            !prev[`improvement-${i}`],
                                        }))
                                      }
                                    >
                                      <ChevronDown
                                        style={{
                                          width: "10px",
                                          height: "10px",
                                          color: "#f59e0b",
                                          transform: expandedThemeBlocks[
                                            `improvement-${i}`
                                          ]
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                          transition: "transform 0.2s ease",
                                        }}
                                      />
                                      <p
                                        className="mb-0"
                                        style={{
                                          fontSize: "12px",
                                          fontWeight: "600",
                                          color: "#f59e0b",
                                        }}
                                      >
                                        {improvement.title}
                                      </p>
                                    </div>
                                    {expandedThemeBlocks[`improvement-${i}`] && (
                                      <>
                                        <p
                                          className="mb-1"
                                          style={{
                                            fontSize: "10px",
                                            color: "#9ca3af",
                                          }}
                                        >
                                          {improvement.description}
                                        </p>
                                        {improvement.suggested_improvement && (
                                          <p
                                            className="mb-0"
                                            style={{
                                              fontSize: "10px",
                                              color: "#6b7280",
                                              fontStyle: "italic",
                                            }}
                                          >
                                            💡 {improvement.suggested_improvement}
                                          </p>
                                        )}
                                      </>
                                    )}
                                  </div>
                                ),
                              )}
                            </div>
                          )}

                        {/* Store Page Gaps */}
                        {appData?.policy?.policy_report?.store_page_gaps
                          ?.length > 0 && (
                            <div className="mb-3">
                              <p
                                className="mb-1"
                                style={{
                                  fontSize: "11px",
                                  color: "#F6255A",
                                  fontWeight: "500",
                                }}
                              >
                                Store Page Gaps
                              </p>
                              {appData.policy.policy_report.store_page_gaps.map(
                                (gap, i) => (
                                  <div className="pdf-no-break p-2 rounded mb-2" key={i}
                                    style={{
                                      backgroundColor: "rgba(59, 130, 246, 0.1)",
                                      border: "1px solid rgba(59, 130, 246, 0.2)",
                                    }}
                                  >
                                    <div
                                      className="d-flex align-items-center gap-1 mb-1"
                                      style={{ cursor: "pointer" }}
                                      onClick={() =>
                                        setExpandedThemeBlocks(prev => ({
                                          ...prev,
                                          [`gap-${i}`]: !prev[`gap-${i}`],
                                        }))
                                      }
                                    >
                                      <ChevronDown
                                        style={{
                                          width: "10px",
                                          height: "10px",
                                          color: "#F6255A",
                                          transform: expandedThemeBlocks[
                                            `gap-${i}`
                                          ]
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                          transition: "transform 0.2s ease",
                                        }}
                                      />
                                      <p
                                        className="mb-0"
                                        style={{
                                          fontSize: "12px",
                                          fontWeight: "600",
                                          color: "#F6255A",
                                        }}
                                      >
                                        {humanizeFieldName(gap.field)}
                                      </p>
                                    </div>
                                    {expandedThemeBlocks[`gap-${i}`] && (
                                      <>
                                        <p
                                          className="mb-1"
                                          style={{
                                            fontSize: "10px",
                                            color: "#9ca3af",
                                          }}
                                        >
                                          {gap.issue}
                                        </p>
                                        {gap.how_to_fix && (
                                          <p
                                            className="mb-0"
                                            style={{
                                              fontSize: "10px",
                                              color: "#6b7280",
                                              fontStyle: "italic",
                                            }}
                                          >
                                            🔧 {gap.how_to_fix}
                                          </p>
                                        )}
                                      </>
                                    )}
                                  </div>
                                ),
                              )}
                            </div>
                          )}

                        {/* No Issues Message */}
                        {!appData?.policy?.policy_report?.blocking_issues
                          ?.length &&
                          !appData?.policy?.policy_report
                            ?.recommended_improvements?.length &&
                          !appData?.policy?.policy_report?.store_page_gaps
                            ?.length && (
                            <div
                              className="p-2 rounded text-center"
                              style={{
                                backgroundColor: "rgba(16, 185, 129, 0.1)",
                                border: "1px solid rgba(16, 185, 129, 0.2)",
                              }}
                            >
                              <p
                                className="mb-0"
                                style={{ fontSize: "12px", color: "#10b981" }}
                              >
                                ✅ No policy issues detected
                              </p>
                            </div>
                          )}

                        <hr
                          className="my-3"
                          style={{ borderColor: "#374151" }}
                        />
                        {/* AI Section */}
                        <div className="mt-4">
                          <p
                            className="mb-2"
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              color: "#9ca3af",
                            }}
                          >
                            AI Section
                          </p>
                          <div className="aicmo-agent-list">
                          <div
                            style={{
                              backgroundColor: "rgba(55, 65, 81, 0.4)",
                              border: "1px solid rgba(55, 65, 81, 0.3)",
                              borderRadius: "8px",
                              padding: "10px",
                              cursor: "pointer",
                              transition: "all 0.3s ease",
                            }}
                            className="d-flex align-items-center gap-2"
                            onClick={() => setShowArticleModal(true)}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                              <rect x="2" y="3" width="20" height="18" rx="2" stroke="#F6255A" strokeWidth="2"/>
                              <line x1="6" y1="8" x2="18" y2="8" stroke="#F6255A" strokeWidth="2" strokeLinecap="round"/>
                              <line x1="6" y1="12" x2="18" y2="12" stroke="#F6255A" strokeWidth="2" strokeLinecap="round"/>
                              <line x1="6" y1="16" x2="12" y2="16" stroke="#F6255A" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <p
                                style={{
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  color: "#ffffff",
                                  margin: "0",
                                  transition: "color 0.3s ease",
                                  backgroundColor: "transparent",
                                }}
                              >
                                Articles Agent
                              </p>
                            </div>
                            <ChevronRight
                              style={{
                                width: "14px",
                                height: "14px",
                                color: "rgba(156, 163, 175, 0.4)",
                                flexShrink: 0,
                                transition: "color 0.3s ease",
                              }}
                            />
                          </div>

                          <div
                            style={{
                              backgroundColor: "rgba(55, 65, 81, 0.4)",
                              border: "1px solid rgba(55, 65, 81, 0.3)",
                              borderRadius: "8px",
                              padding: "10px",
                              cursor: "pointer",
                              transition: "all 0.3s ease",
                            }}
                            className="d-flex align-items-center gap-2"
                            onClick={() => {
                              setShowLinkedInModal(true)
                              handleLinkedInPost()
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                              <rect width="24" height="24" rx="4" fill="#0A66C2"/>
                              <path d="M8 10H6V18H8V10Z" fill="white"/>
                              <circle cx="7" cy="7.5" r="1.3" fill="white"/>
                              <path d="M18 18H16V13.2C16 12.1 15.3 11.5 14.4 11.5C13.5 11.5 13 12.1 13 13.2V18H11V10H13V11C13.5 10.3 14.4 9.8 15.5 9.8C17.3 9.8 18 11 18 13V18Z" fill="white"/>
                            </svg>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <p
                                style={{
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  color: "#ffffff",
                                  margin: "0",
                                  transition: "color 0.3s ease",
                                  backgroundColor: "transparent",
                                }}
                              >
                                LinkedIn Agent
                              </p>
                            </div>
                            <ChevronRight
                              style={{
                                width: "14px",
                                height: "14px",
                                color: "rgba(156, 163, 175, 0.4)",
                                flexShrink: 0,
                                transition: "color 0.3s ease",
                              }}
                            />
                          </div>

                          <div
                            style={{
                              backgroundColor: "rgba(55, 65, 81, 0.4)",
                              border: "1px solid rgba(55, 65, 81, 0.3)",
                              borderRadius: "8px",
                              padding: "10px",
                              cursor: "pointer",
                              transition: "all 0.3s ease",
                            }}
                            className="d-flex align-items-center gap-2"
                            onClick={() => {
                              setShowXModal(true)
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                              <rect width="24" height="24" rx="4" fill="#000000"/>
                              <path d="M13.115 11.09L18.343 5H17.077L12.553 10.253L8.9 5H5L10.477 12.862L5 19.25H6.266L11.039 13.718L14.9 19.25H18.8L13.115 11.09ZM11.69 12.936L11.116 12.103L6.733 5.977H8.298L12.202 11.047L12.775 11.88L17.078 18.316H15.513L11.69 12.936Z" fill="white"/>
                            </svg>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <p
                                style={{
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  color: "#ffffff",
                                  margin: "0",
                                  transition: "color 0.3s ease",
                                  backgroundColor: "transparent",
                                }}
                              >
                                X Agent
                              </p>
                            </div>
                            <ChevronRight
                              style={{
                                width: "14px",
                                height: "14px",
                                color: "rgba(156, 163, 175, 0.4)",
                                flexShrink: 0,
                                transition: "color 0.3s ease",
                              }}
                            />
                          </div>

                          <div
                            style={{
                              backgroundColor: isGeneratingMarketingStrategy === appData?.app_id ? "rgba(55, 65, 81, 0.2)" : "rgba(55, 65, 81, 0.4)",
                              border: isGeneratingMarketingStrategy === appData?.app_id ? "1px solid rgba(55, 65, 81, 0.1)" : "1px solid rgba(55, 65, 81, 0.3)",
                              borderRadius: "8px",
                              padding: "10px",
                              cursor: isGeneratingMarketingStrategy === appData?.app_id ? "not-allowed" : "pointer",
                              transition: "all 0.3s ease",
                              opacity: isGeneratingMarketingStrategy === appData?.app_id ? 0.5 : 1,
                            }}
                            className="d-flex align-items-center gap-2"
                            onClick={() => {
                              if (isGeneratingMarketingStrategy === appData?.app_id) {
                                toastr.warning("Marketing strategy is being prepared. Please wait...", "Data in Progress")
                              } else {
                                handleMarketingStrategy()
                              }
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F6255A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                              <path d="M3 11l19-9-9 19-2-8-8-2z"/>
                            </svg>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <p
                                style={{
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  color: "#ffffff",
                                  margin: "0",
                                  transition: "color 0.3s ease",
                                  backgroundColor: "transparent",
                                }}
                              >
                                Digital Marketing
                              </p>
                            </div>
                            <ChevronRight
                              style={{
                                width: "14px",
                                height: "14px",
                                color: "rgba(156, 163, 175, 0.4)",
                                flexShrink: 0,
                                transition: "color 0.3s ease",
                              }}
                            />
                          </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>

                {/* Panel 4: KeyQuest AI */}
                
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="col-12 col-md-6 col-lg-4 aicmo-fill-col"
                >
                  {scanning ? (
                    <SkeletonCard />
                  ) : (
                    // Actual KeyQuest AI panel
                    <div
                      style={{
                        ...cardStyle,
                        border: "1px solid #F6255A",
                        boxShadow:
                          "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24), 0 0 20px rgba(246, 37, 90, 0.1)",
                      }}
                      className="aicmo-fill-card d-flex flex-column"
                    >
                      <div
                        style={headerStyle}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded d-flex align-items-center justify-content-center"
                            style={{
                              width: "32px",
                              height: "32px",
                              backgroundColor: "rgba(246, 37, 90, 0.1)",
                              borderRadius: "8px",
                            }}
                          >
                            <Bot
                              style={{
                                width: "18px",
                                height: "18px",
                                color: "#F6255A",
                              }}
                            />
                          </div>
                          <div>
                            <span
                              style={{
                                fontSize: "14px",
                                fontWeight: "600",
                                color: "#F6255A",
                                fontFamily: "monospace",
                                letterSpacing: "0.05em",
                              }}
                            >
                              KeyQuest AI
                            </span>
                            <p
                              className="mb-0"
                              style={{
                                fontSize: "12px",
                                fontWeight: "500",
                                color: "#9ca3af",
                              }}
                            >
                              Your app intelligence assistant
                            </p>
                          </div>
                        </div>
                        <span
                          className="badge"
                          style={{
                            backgroundColor: "rgba(16, 185, 129, 0.1)",
                            border: "1px solid #10b981",
                            color: "#10b981",
                            fontSize: "11px",
                            fontWeight: "500",
                            padding: "4px 8px",
                            borderRadius: "4px",
                          }}
                        >
                          Online
                        </span>
                      </div>
                      <div className="card-body flex-grow-1 d-flex flex-column pt-2" style={{ minHeight: 0 }}>
                        <div
                          ref={chatContainerRef}
                          className="flex-grow-1 mb-3 aicmo-scroll"
                          style={{ minHeight: 0 }}
                        >
                          <div className="space-y-3">
                            {/* Suggested questions */}
                            <div className="space-y-2">
                              <p
                                className="mb-2"
                                style={{
                                  fontSize: "11px",
                                  textTransform: "uppercase",
                                  color: "rgba(255, 255, 255, 0.4)",
                                  letterSpacing: "0.05em",
                                  fontWeight: 500,
                                }}
                              >
                                Suggested Questions
                              </p>
                              {[
                                "How can I improve my rating?",
                                "What keywords should I target?",
                                "Explain the revenue trend",
                                "What are my competitors doing?",
                              ].map(q => (
                                <button
                                  key={q}
                                  onClick={() => {
                                    // Send the question immediately
                                    handleChatWithMessage(q)
                                  }}
                                  className="btn btn-sm text-start w-100"
                                  style={{
                                    backgroundColor: "rgba(246, 37, 90, 0.1)",
                                    border: "1px solid rgba(246, 37, 90, 0.2)",
                                    borderRadius: "6px",
                                    padding: "8px 12px",
                                    fontSize: "11px",
                                    color: "#F6255A",
                                    transition: "all 0.2s ease",
                                  }}
                                  onMouseEnter={e => {
                                    e.currentTarget.style.backgroundColor =
                                      "rgba(246, 37, 90, 0.2)"
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.backgroundColor =
                                      "rgba(246, 37, 90, 0.1)"
                                  }}
                                >
                                  {q}
                                </button>
                              ))}
                            </div>

                            {/* Chat messages */}
                            {chatMessages.length > 1 && (
                              <div className="mt-4">
                                <div className="space-y-3">
                                  {chatMessages
                                    .slice(1)
                                    .map((message, index) => (
                                      <motion.div
                                        key={`${message.role}-${index}-${message.text}`}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className={`d-flex gap-2 ${message.role === "user" ? "justify-content-end" : "justify-content-start"}`}
                                      >
                                        {message.role === "ai" && (
                                          <div
                                            className="rounded d-flex align-items-center justify-content-center flex-shrink-0"
                                            style={{
                                              width: "24px",
                                              height: "24px",
                                              backgroundColor:
                                                "rgba(246, 37, 90, 0.1)",
                                              borderRadius: "6px",
                                            }}
                                          >
                                            <Bot
                                              style={{
                                                width: "16px",
                                                height: "16px",
                                                color: "#F6255A",
                                              }}
                                            />
                                          </div>
                                        )}
                                        <div
                                          className="p-2 rounded"
                                          style={{
                                            maxWidth: "80%",
                                            backgroundColor:
                                              message.role === "user"
                                                ? "#F6255A"
                                                : "rgba(55, 65, 81, 0.9)",
                                            border:
                                              message.role === "user"
                                                ? "1px solid #F6255A"
                                                : "1px solid #374151",
                                            borderRadius: "12px",
                                            boxShadow:
                                              message.role === "user"
                                                ? "0 4px 15px rgba(246, 37, 90, 0.3)"
                                                : "0 4px 15px rgba(55, 65, 81, 0.3)",
                                          }}
                                        >
                                          {message.isLoading ? (
                                            <div
                                              className="loading-dots"
                                              style={{
                                                display: "flex",
                                                gap: "6px",
                                                alignItems: "center",
                                              }}
                                            >
                                              <div
                                                className="dot"
                                                style={{
                                                  width: "8px",
                                                  height: "8px",
                                                  background:
                                                    "rgba(255, 255, 255, 0.6)",
                                                  borderRadius: "50%",
                                                  animation:
                                                    "loading-bounce 1.4s ease-in-out infinite both",
                                                  animationDelay: "-0.32s",
                                                }}
                                              ></div>
                                              <div
                                                className="dot"
                                                style={{
                                                  width: "8px",
                                                  height: "8px",
                                                  background:
                                                    "rgba(255, 255, 255, 0.6)",
                                                  borderRadius: "50%",
                                                  animation:
                                                    "loading-bounce 1.4s ease-in-out infinite both",
                                                  animationDelay: "-0.16s",
                                                }}
                                              ></div>
                                              <div
                                                className="dot"
                                                style={{
                                                  width: "8px",
                                                  height: "8px",
                                                  background:
                                                    "rgba(255, 255, 255, 0.6)",
                                                  borderRadius: "50%",
                                                  animation:
                                                    "loading-bounce 1.4s ease-in-out infinite both",
                                                  animationDelay: "0s",
                                                }}
                                              ></div>
                                            </div>
                                          ) : (
                                            <div
                                              className="mb-0"
                                              style={{
                                                fontSize: "13px",
                                                color:
                                                  message.role === "user"
                                                    ? "#ffffff"
                                                    : "#e2e8f0",
                                                lineHeight: 1.6,
                                                fontWeight:
                                                  message.role === "user"
                                                    ? "500"
                                                    : "400",
                                              }}
                                              dangerouslySetInnerHTML={{
                                                __html:
                                                  message.role === "ai"
                                                    ? convertMarkdownToHtml(
                                                      message.text || "",
                                                    )
                                                    : message.text,
                                              }}
                                            />
                                          )}
                                        </div>
                                        {message.role === "user" && (
                                          <div
                                            className="rounded d-flex align-items-center justify-content-center flex-shrink-0"
                                            style={{
                                              width: "24px",
                                              height: "24px",
                                              backgroundColor:
                                                "rgba(246, 37, 90, 0.1)",
                                              borderRadius: "6px",
                                            }}
                                          >
                                            <span
                                              style={{
                                                fontSize: "10px",
                                                color: "#F6255A",
                                                fontWeight: "600",
                                              }}
                                            >
                                              You
                                            </span>
                                          </div>
                                        )}
                                      </motion.div>
                                    ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div ref={chatEndRef} />

                        {/* Chat input */}
                        <div className="d-flex gap-1 mt-1">
                          <input
                            type="text"
                            value={chatInput}
                            onChange={e => setChatInput(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                e.preventDefault()
                                if (chatInput.trim() && !isChatLoading) {
                                  handleChatWithMessage(chatInput)
                                }
                              }
                            }}
                            placeholder="Ask anything about this app..."
                            className="form-control flex-grow-1"
                            style={{
                              fontSize: "12px",
                              backgroundColor: "#334155",
                              border: "1px solid #475569",
                              color: "white",
                              height: "40px",
                            }}
                          />
                          <button
                            className="btn d-flex align-items-center justify-content-center"
                            style={{
                              width: "40px",
                              height: "40px",
                              backgroundColor: "#F6255A",
                              borderColor: "#F6255A",
                              color: "#ffffff",
                            }}
                            onClick={handleChat}
                            disabled={!chatInput.trim() || isChatLoading}
                          >
                            {isChatLoading ? (
                              <div
                                className="spinner-border spinner-border-sm"
                                role="status"
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  borderWidth: "2px",
                                }}
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            ) : (
                              <Send style={{ width: "16px", height: "16px" }} />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              </div>
              {/* Footer */}
              {/* <div
                className="mt-6 text-center"
                style={{ fontSize: "12px", color: "#64748b" }}
              >
                Report generated Apr 13, 2026 · Powered by KeyQuest AI Analytics
              </div> */}
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>

          {/* AI Article Drawer */}
          <AnimatePresence>
            {showArticleModal && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                    zIndex: 1002,
                  }}
                  onClick={() => setShowArticleModal(false)}
                />
                <motion.div
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "100%" }}
                  transition={{ type: "spring", damping: 25, stiffness: 300 }}
                  style={{
                    position: "fixed",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: "600px",
                    maxWidth: "90vw",
                    backgroundColor: "#1f2937",
                    border: "1px solid #374151",
                    borderLeft: "none",
                    zIndex: 1003,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <div
                    className="modal-header d-flex justify-content-between align-items-center p-3"
                    style={{
                      borderBottom: "1px solid #374151",
                    }}
                  >
                    <h5
                      className="mb-0"
                      style={{
                        fontSize: "18px",
                        color: "#ffffff",
                        fontWeight: "600",
                      }}
                    >
                      AI Generated Article
                    </h5>
                    <button
                      className="btn btn-sm"
                      style={{
                        backgroundColor: "transparent",
                        border: "none",
                        color: "#9ca3af",
                        padding: "4px",
                      }}
                      onClick={() => setShowArticleModal(false)}
                    >
                      <X style={{ width: "20px", height: "20px" }} />
                    </button>
                  </div>

                  <div
                    className="modal-body p-4"
                    style={{
                      flex: 1,
                      overflowY: "auto",
                    }}
                  >
                    {isGeneratingArticle ? (
                      <div className="py-4">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                            style={{
                              width: "20px",
                              height: "20px",
                              borderWidth: "2px",
                              color: "#F6255A",
                            }}
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p
                            style={{
                              fontSize: "14px",
                              color: "#9ca3af",
                              marginBottom: 0,
                              fontWeight: "500",
                            }}
                          >
                            Generating article...
                          </p>
                        </div>
                        {/* Skeleton Loader */}
                        <div className="skeleton-loader">
                          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                            <div className="pdf-no-break" key={i}
                              style={{
                                height: i === 1 ? "28px" : i === 8 ? "120px" : "16px",
                                backgroundColor: "#374151",
                                borderRadius: "4px",
                                marginBottom: "12px",
                                width: i === 1 ? "70%" : i === 8 ? "100%" : i % 2 === 0 ? "90%" : "60%",
                                animation: `skeleton-pulse 1.5s ease-in-out infinite`,
                                animationDelay: `${i * 0.1}s`,
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    ) : generatedArticle ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: convertMarkdownToHtml(generatedArticle),
                        }}
                        style={{
                          fontSize: "15px",
                          color: "#ffffff",
                          lineHeight: 1.7,
                          fontFamily: "system-ui, -apple-system, sans-serif",
                        }}
                      />
                    ) : (
                      <div className="d-flex flex-column align-items-center justify-content-center py-8" style={{ minHeight: "300px" }}>
                        <div
                          style={{
                            width: "80px",
                            height: "80px",
                            borderRadius: "50%",
                            backgroundColor: "rgba(246, 37, 90, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "20px",
                          }}
                        >
                          <FileText style={{ width: "40px", height: "40px", color: "#F6255A" }} />
                        </div>
                        <h6
                          style={{
                            fontSize: "18px",
                            color: "#ffffff",
                            fontWeight: "600",
                            marginBottom: "12px",
                          }}
                        >
                          Generate AI Article
                        </h6>
                        <p
                          style={{
                            fontSize: "14px",
                            color: "#9ca3af",
                            marginBottom: "24px",
                            textAlign: "center",
                            maxWidth: "350px",
                          }}
                        >
                          Create an engaging article about this app with AI-powered insights and analysis
                        </p>
                        <button
                          className="btn btn-primary"
                          style={{
                            backgroundColor: "#F6255A",
                            border: "1px solid #F6255A",
                            color: "#ffffff",
                            padding: "12px 28px",
                            fontSize: "14px",
                            fontWeight: "500",
                            borderRadius: "8px",
                            transition: "all 0.2s ease",
                          }}
                          onClick={handleGenerateArticle}
                          onMouseEnter={e => {
                            e.target.style.backgroundColor = "#e01e4a"
                            e.target.style.borderColor = "#e01e4a"
                          }}
                          onMouseLeave={e => {
                            e.target.style.backgroundColor = "#F6255A"
                            e.target.style.borderColor = "#F6255A"
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <Brain style={{ width: "16px", height: "16px" }} />
                            Generate Article
                          </div>
                        </button>
                      </div>
                    )}
                  </div>

                  {generatedArticle && (
                    <div
                      className="modal-footer p-4"
                      style={{
                        borderTop: "1px solid #374151",
                      }}
                    >
                      <button
                        className="btn btn-secondary me-2"
                        style={{
                          backgroundColor: "transparent",
                          border: "1px solid #374151",
                          color: "#9ca3af",
                          padding: "8px 16px",
                          fontSize: "14px",
                          borderRadius: "6px",
                          transition: "all 0.2s ease",
                        }}
                        onClick={() => {
                          navigator.clipboard.writeText(generatedArticle)
                          trackEvent(ANALYTICS_EVENTS.FEATURE.AI_CMO_ARTICLE_COPIED, {
                            app_id: appData?.app_id,
                          })
                          setShowCopyToast(true)
                          setTimeout(() => setShowCopyToast(false), 2000)
                        }}
                        onMouseEnter={e => {
                          e.target.style.backgroundColor = "rgba(55, 65, 81, 0.5)"
                        }}
                        onMouseLeave={e => {
                          e.target.style.backgroundColor = "transparent"
                        }}
                      >
                        Copy Article
                      </button>
                      <button
                        className="btn btn-primary"
                        style={{
                          backgroundColor: "#F6255A",
                          border: "1px solid #F6255A",
                          color: "#ffffff",
                          padding: "8px 16px",
                          fontSize: "14px",
                          borderRadius: "6px",
                          transition: "all 0.2s ease",
                        }}
                        onClick={() => setShowArticleModal(false)}
                        onMouseEnter={e => {
                          e.target.style.backgroundColor = "#e01e4a"
                          e.target.style.borderColor = "#e01e4a"
                        }}
                        onMouseLeave={e => {
                          e.target.style.backgroundColor = "#F6255A"
                          e.target.style.borderColor = "#F6255A"
                        }}
                      >
                        Close
                      </button>
                    </div>
                  )}
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </AnimatePresence>


        {/* LinkedIn Post Drawer */}
        <AnimatePresence>
          {showLinkedInModal && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.8)",
                  zIndex: 1002,
                }}
                onClick={() => setShowLinkedInModal(false)}
              />
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                style={{
                  position: "fixed",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: "600px",
                  maxWidth: "90vw",
                  backgroundColor: "#1f2937",
                  border: "1px solid #374151",
                  borderLeft: "none",
                  zIndex: 1003,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
                onClick={e => e.stopPropagation()}
              >
                <div
                  className="modal-header p-4"
                  style={{
                    borderBottom: "1px solid #374151",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h5
                    className="modal-title mb-0"
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="#0077b5"
                    >
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                    </svg>
                    LinkedIn Post
                  </h5>
                  <button
                    className="btn btn-sm btn-link p-0"
                    style={{
                      color: "#9ca3af",
                      padding: "4px",
                    }}
                    onClick={() => setShowLinkedInModal(false)}
                  >
                    <X style={{ width: "20px", height: "20px" }} />
                  </button>
                </div>
                <div
                  className="modal-body p-4"
                  style={{
                    flex: 1,
                    overflowY: "auto",
                  }}
                >
                  {isGeneratingLinkedIn ? (
                    <div className="py-4">
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                          style={{
                            width: "20px",
                            height: "20px",
                            borderWidth: "2px",
                            color: "#0077b5",
                          }}
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p
                          style={{
                            fontSize: "14px",
                            color: "#9ca3af",
                            marginBottom: 0,
                            fontWeight: "500",
                          }}
                        >
                          Generating LinkedIn post...
                        </p>
                      </div>
                      {/* Skeleton Loader */}
                      <div className="skeleton-loader">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                          <div className="pdf-no-break" key={i}
                            style={{
                              height: i === 1 ? "24px" : "14px",
                              backgroundColor: "#374151",
                              borderRadius: "4px",
                              marginBottom: "10px",
                              width: i === 1 ? "80%" : i % 2 === 0 ? "95%" : "65%",
                              animation: `skeleton-pulse 1.5s ease-in-out infinite`,
                              animationDelay: `${i * 0.1}s`,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  ) : linkedinPost ? (
                    <div style={{ color: "#ffffff" }}>
                      {/* App Image */}
                      {linkedinPost.image_url && (
                        <div className="text-center mb-4">
                          <img
                            src={linkedinPost.image_url}
                            alt={linkedinPost.app_name}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "200px",
                              borderRadius: "8px",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      )}

                      {/* Combined LinkedIn Post */}
                      <div
                        style={{
                          fontSize: "15px",
                          lineHeight: 1.7,
                          whiteSpace: "pre-line",
                        }}
                      >
                        {linkedinPost.hook && (
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: "600",
                              marginBottom: "12px",
                              color: "#ffffff",
                            }}
                          >
                            {linkedinPost.hook}
                          </div>
                        )}
                        {linkedinPost.body && linkedinPost.body}
                      </div>

                      {/* Note about image */}
                      {linkedinPost.image_url && (
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#6b7280",
                            marginTop: "16px",
                            fontStyle: "italic",
                            padding: "12px",
                            backgroundColor: "rgba(107, 114, 128, 0.1)",
                            borderRadius: "6px",
                          }}
                        >
                          💡 Note: Image will need to be added manually when posting
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="d-flex flex-column align-items-center justify-content-center py-8" style={{ minHeight: "300px" }}>
                      <div
                        style={{
                          width: "80px",
                          height: "80px",
                          borderRadius: "50%",
                          backgroundColor: "rgba(0, 119, 181, 0.1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: "20px",
                        }}
                      >
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 24 24"
                          fill="#0077b5"
                        >
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                        </svg>
                      </div>
                      <h6
                        style={{
                          fontSize: "18px",
                          color: "#ffffff",
                          fontWeight: "600",
                          marginBottom: "12px",
                        }}
                      >
                        Generate LinkedIn Post
                      </h6>
                      <p
                        style={{
                          fontSize: "14px",
                          color: "#9ca3af",
                          marginBottom: "24px",
                          textAlign: "center",
                          maxWidth: "350px",
                        }}
                      >
                        Create a viral LinkedIn post about this app with AI-powered content
                      </p>
                      <button
                        className="btn btn-primary"
                        style={{
                          backgroundColor: "#0077b5",
                          border: "1px solid #0077b5",
                          color: "#ffffff",
                          padding: "12px 28px",
                          fontSize: "14px",
                          fontWeight: "500",
                          borderRadius: "8px",
                          transition: "all 0.2s ease",
                        }}
                        onClick={handleLinkedInPost}
                        onMouseEnter={e => {
                          e.target.style.backgroundColor = "#005885"
                          e.target.style.borderColor = "#005885"
                        }}
                        onMouseLeave={e => {
                          e.target.style.backgroundColor = "#0077b5"
                          e.target.style.borderColor = "#0077b5"
                        }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <Brain style={{ width: "16px", height: "16px" }} />
                          Generate Post
                        </div>
                      </button>
                    </div>
                  )}
                </div>

                {linkedinPost && (
                  <div
                    className="modal-footer p-4"
                    style={{
                      borderTop: "1px solid #374151",
                    }}
                  >
                    <button
                      className="btn btn-secondary me-2"
                      style={{
                        backgroundColor: "transparent",
                        border: "1px solid #374151",
                        color: "#9ca3af",
                        padding: "8px 16px",
                        fontSize: "14px",
                        borderRadius: "6px",
                        transition: "all 0.2s ease",
                      }}
                      onClick={() => {
                        const contentToCopy = `${linkedinPost.hook ? linkedinPost.hook + "\n\n" : ""}${linkedinPost.body || ""}`
                        navigator.clipboard.writeText(contentToCopy)
                        trackEvent(ANALYTICS_EVENTS.FEATURE.AI_CMO_LINKEDIN_POST_COPIED, {
                          app_id: appData?.app_id,
                        })
                        setShowCopyToast(true)
                        setTimeout(() => setShowCopyToast(false), 2000)
                      }}
                      onMouseEnter={e => {
                        e.target.style.backgroundColor = "rgba(55, 65, 81, 0.5)"
                      }}
                      onMouseLeave={e => {
                        e.target.style.backgroundColor = "transparent"
                      }}
                    >
                      Copy Post
                    </button>
                    <button
                      className="btn btn-primary me-2"
                      style={{
                        backgroundColor: "#0077b5",
                        border: "1px solid #0077b5",
                        color: "#ffffff",
                        padding: "8px 16px",
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        borderRadius: "6px",
                        transition: "all 0.2s ease",
                      }}
                      onClick={handleLinkedInShare}
                      onMouseEnter={e => {
                        e.target.style.backgroundColor = "#005885"
                        e.target.style.borderColor = "#005885"
                      }}
                      onMouseLeave={e => {
                        e.target.style.backgroundColor = "#0077b5"
                        e.target.style.borderColor = "#0077b5"
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                      </svg>
                      Share on LinkedIn
                    </button>
                    <button
                      className="btn btn-primary"
                      style={{
                        backgroundColor: "#F6255A",
                        border: "1px solid #F6255A",
                        color: "#ffffff",
                        padding: "8px 16px",
                        fontSize: "14px",
                        borderRadius: "6px",
                        transition: "all 0.2s ease",
                      }}
                      onClick={() => setShowLinkedInModal(false)}
                      onMouseEnter={e => {
                        e.target.style.backgroundColor = "#e01e4a"
                        e.target.style.borderColor = "#e01e4a"
                      }}
                      onMouseLeave={e => {
                        e.target.style.backgroundColor = "#F6255A"
                        e.target.style.borderColor = "#F6255A"
                      }}
                    >
                      Close
                    </button>
                  </div>
                )}
              </motion.div>
            </>
          )}

          {showXModal && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.8)",
                  zIndex: 1002,
                }}
                onClick={() => setShowXModal(false)}
              />
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                style={{
                  position: "fixed",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: "450px",
                  maxWidth: "100vw",
                  backgroundColor: "#1f2937",
                  boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.5)",
                  zIndex: 1003,
                  display: "flex",
                  flexDirection: "column",
                  borderLeft: "1px solid #374151",
                }}
              >
                <div
                  className="modal-header p-4"
                  style={{
                    borderBottom: "1px solid #374151",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h5 className="modal-title m-0 text-white d-flex align-items-center gap-2">
                    <X style={{ width: "20px", height: "20px" }} />
                    Generated X Post
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setShowXModal(false)}
                    style={{ fontSize: "14px" }}
                  />
                </div>

                <div
                  className="modal-body p-4 custom-scrollbar"
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    backgroundColor: "#111827",
                  }}
                >
                  {isGeneratingX ? (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100 min-h-[300px]">
                      <div
                        className="spinner-border text-primary mb-3"
                        role="status"
                        style={{
                          width: "3rem",
                          height: "3rem",
                          color: "#F6255A",
                        }}
                      />
                      <p
                        style={{
                          fontSize: "14px",
                          color: "#9ca3af",
                          marginBottom: 0,
                          fontWeight: "500",
                        }}
                      >
                        Generating X post...
                      </p>
                    </div>
                  ) : xPost ? (
                    <div style={{ color: "#ffffff" }}>
                      {/* App Image */}
                      {xPost.image_url && (
                        <div className="text-center mb-4">
                          <img
                            src={xPost.image_url}
                            alt={xPost.app_name}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "200px",
                              borderRadius: "8px",
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      )}

                      {/* Combined X Post */}
                      <div
                        style={{
                          fontSize: "15px",
                          lineHeight: 1.7,
                          whiteSpace: "pre-line",
                        }}
                      >
                        {xPost.hook && (
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: "600",
                              marginBottom: "12px",
                              color: "#ffffff",
                            }}
                          >
                            {xPost.hook}
                          </div>
                        )}
                        {xPost.body && xPost.body}
                      </div>

                      {/* Note about image */}
                      {xPost.image_url && (
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#6b7280",
                            marginTop: "16px",
                            fontStyle: "italic",
                            padding: "12px",
                            backgroundColor: "rgba(107, 114, 128, 0.1)",
                            borderRadius: "6px",
                          }}
                        >
                          💡 Note: Image will need to be added manually when posting
                        </div>
                      )}                    </div>
                  ) : (
                    <div className="d-flex flex-column align-items-center justify-content-center py-8" style={{ minHeight: "300px" }}>
                      <div
                        style={{
                          width: "80px",
                          height: "80px",
                          borderRadius: "50%",
                          backgroundColor: "rgba(246, 37, 90, 0.1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: "20px",
                        }}
                      >
                        <X
                          style={{
                            width: "40px",
                            height: "40px",
                            color: "#F6255A"
                          }}
                        />
                      </div>
                      <h6
                        style={{
                          fontSize: "18px",
                          color: "#ffffff",
                          fontWeight: "600",
                          marginBottom: "12px",
                        }}
                      >
                        Generate X Post
                      </h6>
                      <p
                        style={{
                          fontSize: "14px",
                          color: "#9ca3af",
                          marginBottom: "24px",
                          textAlign: "center",
                          maxWidth: "350px",
                        }}
                      >
                        Create a viral X (Twitter) post about this app with AI-powered content
                      </p>
                      <button
                        className="btn btn-primary"
                        style={{
                          backgroundColor: "#F6255A",
                          border: "1px solid #F6255A",
                          color: "#ffffff",
                          padding: "12px 28px",
                          fontSize: "14px",
                          fontWeight: "500",
                          borderRadius: "8px",
                          transition: "all 0.2s ease",
                        }}
                        onClick={handleXPost}
                        onMouseEnter={e => {
                          e.target.style.backgroundColor = "#e01e4a"
                          e.target.style.borderColor = "#e01e4a"
                        }}
                        onMouseLeave={e => {
                          e.target.style.backgroundColor = "#F6255A"
                          e.target.style.borderColor = "#F6255A"
                        }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <Brain style={{ width: "16px", height: "16px" }} />
                          Generate Post
                        </div>
                      </button>
                    </div>
                  )}
                </div>

                {xPost && (
                  <div
                    className="modal-footer p-4"
                    style={{
                      borderTop: "1px solid #374151",
                    }}
                  >
                    <button
                      className="btn btn-secondary me-2"
                      style={{
                        backgroundColor: "transparent",
                        border: "1px solid #374151",
                        color: "#9ca3af",
                        padding: "8px 16px",
                        fontSize: "14px",
                        borderRadius: "6px",
                        transition: "all 0.2s ease",
                      }}
                      onClick={() => {
                        const contentToCopy = `${xPost.hook ? xPost.hook + "\n\n" : ""}${xPost.body || ""}`
                        navigator.clipboard.writeText(contentToCopy)
                        trackEvent(ANALYTICS_EVENTS.FEATURE.AI_CMO_X_POST_COPIED, {
                          app_id: appData?.app_id,
                        })
                        setShowCopyToast(true)
                        setTimeout(() => setShowCopyToast(false), 2000)
                      }}
                      onMouseEnter={e => {
                        e.target.style.backgroundColor = "rgba(55, 65, 81, 0.5)"
                      }}
                      onMouseLeave={e => {
                        e.target.style.backgroundColor = "transparent"
                      }}
                    >
                      Copy Post
                    </button>
                    <button
                      className="btn btn-primary me-2"
                      style={{
                        backgroundColor: "#1DA1F2",
                        border: "1px solid #1DA1F2",
                        color: "#ffffff",
                        padding: "8px 16px",
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        borderRadius: "6px",
                        transition: "all 0.2s ease",
                      }}
                      onClick={handleXShare}
                      onMouseEnter={e => {
                        e.target.style.backgroundColor = "#1a91da"
                        e.target.style.borderColor = "#1a91da"
                      }}
                      onMouseLeave={e => {
                        e.target.style.backgroundColor = "#1DA1F2"
                        e.target.style.borderColor = "#1DA1F2"
                      }}
                    >
                      Share on X
                    </button>
                    <button
                      className="btn btn-primary"
                      style={{
                        backgroundColor: "#F6255A",
                        border: "1px solid #F6255A",
                        color: "#ffffff",
                        padding: "8px 16px",
                        fontSize: "14px",
                        borderRadius: "6px",
                        transition: "all 0.2s ease",
                      }}
                      onClick={() => setShowXModal(false)}
                      onMouseEnter={e => {
                        e.target.style.backgroundColor = "#e01e4a"
                        e.target.style.borderColor = "#e01e4a"
                      }}
                      onMouseLeave={e => {
                        e.target.style.backgroundColor = "#F6255A"
                        e.target.style.borderColor = "#F6255A"
                      }}
                    >
                      Close
                    </button>
                  </div>
                )}
              </motion.div>
            </>
          )}

          {showMarketingStrategyModal && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.8)",
                  zIndex: 1002,
                }}
                onClick={() => setShowMarketingStrategyModal(false)}
              />
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                style={{
                  position: "fixed",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: "600px",
                  maxWidth: "90vw",
                  backgroundColor: "#1f2937",
                  border: "1px solid #374151",
                  borderLeft: "none",
                  zIndex: 1003,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
                onClick={e => e.stopPropagation()}
              >
                <div
                  className="modal-header d-flex justify-content-between align-items-center p-4"
                  style={{
                    borderBottom: "1px solid #374151",
                    background: "linear-gradient(135deg, rgba(246, 37, 90, 0.1) 0%, rgba(31, 41, 55, 0.3) 100%)",
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "8px",
                        background: "linear-gradient(135deg, #F6255A 0%, #e01e4a 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 4px 12px rgba(246, 37, 90, 0.3)",
                      }}
                    >
                      <Target style={{ width: "20px", height: "20px", color: "#ffffff" }} />
                    </div>
                    <div>
                      <h5
                        className="mb-0"
                        style={{
                          fontSize: "18px",
                          color: "#ffffff",
                          fontWeight: "700",
                          letterSpacing: "-0.5px",
                        }}
                      >
                        Digital Marketing Strategy
                      </h5>
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#9ca3af",
                          margin: "2px 0 0 0",
                        }}
                      >
                        AI-powered insights & recommendations
                      </p>
                    </div>
                  </div>
                  <button
                    className="btn btn-sm"
                    style={{
                      backgroundColor: "rgba(55, 65, 81, 0.5)",
                      border: "1px solid rgba(55, 65, 81, 0.8)",
                      color: "#9ca3af",
                      padding: "8px",
                      borderRadius: "8px",
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => setShowMarketingStrategyModal(false)}
                    onMouseEnter={e => {
                      e.target.style.backgroundColor = "rgba(239, 68, 68, 0.2)"
                      e.target.style.borderColor = "#ef4444"
                      e.target.style.color = "#ef4444"
                    }}
                    onMouseLeave={e => {
                      e.target.style.backgroundColor = "rgba(55, 65, 81, 0.5)"
                      e.target.style.borderColor = "rgba(55, 65, 81, 0.8)"
                      e.target.style.color = "#9ca3af"
                    }}
                  >
                    <X style={{ width: "18px", height: "18px" }} />
                  </button>
                </div>
                <div
                  className="modal-body p-4"
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    background: "linear-gradient(180deg, #1f2937 0%, #111827 100%)",
                  }}
                >
                  {isGeneratingMarketingStrategy === appData?.app_id ? (
                    <div className="py-4">
                      <div
                        style={{
                          textAlign: "center",
                          padding: "40px 20px",
                          background: "linear-gradient(135deg, rgba(246, 37, 90, 0.05) 0%, rgba(31, 41, 55, 0.3) 100%)",
                          borderRadius: "16px",
                          border: "1px solid rgba(246, 37, 90, 0.2)",
                          marginBottom: "24px",
                        }}
                      >
                        <div
                          style={{
                            width: "64px",
                            height: "64px",
                            borderRadius: "50%",
                            background: "linear-gradient(135deg, #F6255A 0%, #e01e4a 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 20px",
                            animation: "pulse 2s ease-in-out infinite",
                          }}
                        >
                          <Brain style={{ width: "32px", height: "32px", color: "#ffffff" }} />
                        </div>
                        <p
                          style={{
                            fontSize: "16px",
                            color: "#ffffff",
                            marginBottom: "8px",
                            fontWeight: "600",
                          }}
                        >
                          Analyzing Your Market
                        </p>
                        <p
                          style={{
                            fontSize: "14px",
                            color: "#9ca3af",
                            marginBottom: "24px",
                            fontWeight: "400",
                          }}
                        >
                          {marketingProgressMessage}<span style={{ animation: "dots 1.5s infinite" }}>...</span>
                        </p>
                        <style>{`
                          @keyframes dots {
                            0%, 20% { opacity: 0; }
                            40% { opacity: 1; }
                            100% { opacity: 1; }
                          }
                        `}</style>
                      </div>
                      {/* Skeleton Loader */}
                      <div className="skeleton-loader">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                          <div className="pdf-no-break" key={i}
                            style={{
                              height: i === 1 ? "32px" : i === 8 ? "120px" : "16px",
                              backgroundColor: "rgba(55, 65, 81, 0.6)",
                              borderRadius: "8px",
                              marginBottom: "12px",
                              width: i === 1 ? "70%" : i === 8 ? "100%" : i % 2 === 0 ? "90%" : "60%",
                              animation: `skeleton-pulse 1.5s ease-in-out infinite`,
                              animationDelay: `${i * 0.1}s`,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  ) : marketingStrategyData ? (
                    <div id="marketing-strategy-content" style={{ color: "#ffffff", padding: "20px", backgroundColor: "#1f2937", widows: 2, orphans: 2, overflowX: "hidden", wordBreak: "break-word" }}>
                      <style>{`
                        .pdf-no-break {
                          page-break-inside: avoid !important;
                          break-inside: avoid !important;
                        }
                        @media (max-width: 500px) {
                          .ms-responsive-grid {
                            grid-template-columns: 1fr !important;
                          }
                          .ms-meta-pair {
                            flex-direction: column !important;
                            align-items: flex-start !important;
                            gap: 3px !important;
                          }
                          .ms-meta-pair svg {
                            display: none;
                          }
                          .ms-channel-row {
                            flex-direction: column !important;
                            align-items: flex-start !important;
                            gap: 6px !important;
                          }
                          .ms-channel-row .ms-channel-meta {
                            flex-wrap: wrap !important;
                          }
                          .ms-spacebetween {
                            flex-wrap: wrap !important;
                            gap: 8px !important;
                          }
                          .ms-spacebetween .ms-flex-shrink {
                            flex-shrink: 0;
                          }
                        }
                      `}</style>
                      {/* Executive Summary */}
                      {marketingStrategyData?.executive_summary && (
                        <div style={{ marginBottom: "32px" }}>
                          <div className="d-flex align-items-center gap-2 mb-4">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "linear-gradient(135deg, #F6255A 0%, #e01e4a 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <TrendingUp style={{ width: "16px", height: "16px", color: "#ffffff" }} />
                            </div>
                            <h6
                              style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                margin: 0,
                                color: "#ffffff",
                                letterSpacing: "-0.5px",
                              }}
                            >
                              Executive Summary
                            </h6>
                          </div>
                          <div className="pdf-no-break" style={{ backgroundColor: "rgba(55, 65, 81, 0.5)",
                              borderRadius: "8px",
                              padding: "16px",
                              marginBottom: "16px",
                            }}
                          >
                            <p
                              style={{
                                fontSize: "15px",
                                fontWeight: "600",
                                color: "#ffffff",
                                marginBottom: "8px",
                                margin: 0,
                              }}
                            >
                              {marketingStrategyData.executive_summary.headline}
                            </p>
                            <p
                              style={{
                                fontSize: "13px",
                                color: "#9ca3af",
                                lineHeight: "1.6",
                                marginBottom: "16px",
                                margin: "8px 0 16px 0",
                              }}
                            >
                              {marketingStrategyData.executive_summary.overview}
                            </p>
                            {marketingStrategyData?.executive_summary?.key_stats && (
                              <div
                                className="ms-responsive-grid"
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                                  gap: "12px",
                                }}
                              >
                                <StatCard
                                  label="Downloads"
                                  value={formatNumber(
                                    marketingStrategyData.executive_summary.key_stats.total_downloads,
                                  )}
                                />
                                <StatCard
                                  label="Revenue"
                                  value={`$${formatNumber(
                                    marketingStrategyData.executive_summary.key_stats.total_revenue_usd,
                                  )}`}
                                />
                                <StatCard
                                  label="Rating"
                                  value={formatMetricDecimal(marketingStrategyData.executive_summary.key_stats.avg_rating, 2)}
                                />
                                <StatCard
                                  label="Reviews"
                                  value={formatNumber(
                                    marketingStrategyData.executive_summary.key_stats.total_reviews,
                                  )}
                                />
                              </div>
                            )}
                          </div>
                          <div
                            className="ms-responsive-grid"
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                              gap: "12px",
                            }}
                          >
                            <InfoCard
                              label="Market Position"
                              value={marketingStrategyData.executive_summary.market_position}
                              subtext={marketingStrategyData.executive_summary.market_position_rationale}
                            />
                            <InfoCard
                              label="Urgency"
                              value={marketingStrategyData.executive_summary.urgency}
                              subtext={marketingStrategyData.executive_summary.urgency_rationale}
                              color={
                                marketingStrategyData.executive_summary.urgency === "High"
                                  ? "#ef4444"
                                  : marketingStrategyData.executive_summary.urgency === "Medium"
                                    ? "#f59e0b"
                                    : "#10b981"
                              }
                            />
                          </div>
                          {marketingStrategyData?.executive_summary?.biggest_opportunity && (
                            <div
                              style={{
                                marginTop: "16px",
                                padding: "12px",
                                backgroundColor: "rgba(16, 185, 129, 0.1)",
                                border: "1px solid rgba(16, 185, 129, 0.3)",
                                borderRadius: "8px",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  color: "#10b981",
                                  marginBottom: "4px",
                                  margin: 0,
                                }}
                              >
                                Biggest Opportunity
                              </p>
                              <p
                                style={{
                                  fontSize: "13px",
                                  color: "#ffffff",
                                  margin: "4px 0 0 0",
                                }}
                              >
                                {marketingStrategyData.executive_summary.biggest_opportunity}
                              </p>
                            </div>
                          )}
                          {marketingStrategyData?.executive_summary?.biggest_threat && (
                            <div
                              style={{
                                marginTop: "12px",
                                padding: "12px",
                                backgroundColor: "rgba(239, 68, 68, 0.1)",
                                border: "1px solid rgba(239, 68, 68, 0.3)",
                                borderRadius: "8px",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  color: "#ef4444",
                                  marginBottom: "4px",
                                  margin: 0,
                                }}
                              >
                                Biggest Threat
                              </p>
                              <p
                                style={{
                                  fontSize: "13px",
                                  color: "#ffffff",
                                  margin: "4px 0 0 0",
                                }}
                              >
                                {marketingStrategyData.executive_summary.biggest_threat}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Product Context */}
                      {marketingStrategyData?.product_context && (
                        <div style={{ marginBottom: "32px" }}>
                          <div className="d-flex align-items-center gap-2 mb-4">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Gamepad2 style={{ width: "16px", height: "16px", color: "#ffffff" }} />
                            </div>
                            <h6
                              style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                margin: 0,
                                color: "#ffffff",
                                letterSpacing: "-0.5px",
                              }}
                            >
                              Product Context
                            </h6>
                          </div>
                          <div
                            className="ms-responsive-grid"
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                              gap: "12px",
                              marginBottom: "16px",
                            }}
                          >
                            <InfoCard
                              label="App Name"
                              value={marketingStrategyData.product_context.app_name}
                            />
                            <InfoCard
                              label="Developer"
                              value={marketingStrategyData.product_context.developer}
                            />
                          </div>
                          <div style={{ marginBottom: "12px" }}>
                            <p
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                color: "#ffffff",
                                marginBottom: "8px",
                                margin: 0,
                              }}
                            >
                              Genre
                            </p>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              {marketingStrategyData.product_context.genre?.map((g, idx) => (
                                <span
                                  key={idx}
                                  style={{
                                    fontSize: "11px",
                                    backgroundColor: "rgba(246, 37, 90, 0.2)",
                                    color: "#F6255A",
                                    padding: "4px 8px",
                                    borderRadius: "4px",
                                  }}
                                >
                                  {g}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div style={{ marginBottom: "12px" }}>
                            <p
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                color: "#ffffff",
                                marginBottom: "8px",
                                margin: 0,
                              }}
                            >
                              Supported Platforms
                            </p>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              {marketingStrategyData.product_context.supported_platforms?.map((p, idx) => (
                                <span
                                  key={idx}
                                  style={{
                                    fontSize: "11px",
                                    backgroundColor: "rgba(55, 65, 81, 0.5)",
                                    color: "#ffffff",
                                    padding: "4px 8px",
                                    borderRadius: "4px",
                                  }}
                                >
                                  {p}
                                </span>
                              ))}
                            </div>
                          </div>
                          <InfoCard
                            label="Pricing"
                            value={marketingStrategyData.product_context.is_free ? "Free" : "Paid"}
                          />
                        </div>
                      )}

                      {/* Market Analysis */}
                      {marketingStrategyData?.market_analysis && (
                        <div style={{ marginBottom: "32px" }}>
                          <div className="d-flex align-items-center gap-2 mb-4">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "linear-gradient(135deg, #F6255A 0%, #e01e4a 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <BarChart3 style={{ width: "16px", height: "16px", color: "#ffffff" }} />
                            </div>
                            <h6
                              style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                margin: 0,
                                color: "#ffffff",
                                letterSpacing: "-0.5px",
                              }}
                            >
                              Market Analysis
                            </h6>
                          </div>
                          {marketingStrategyData?.market_analysis?.performance_metrics && (
                            <div style={{ marginBottom: "16px" }}>
                              <p
                                style={{
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  color: "#ffffff",
                                  marginBottom: "12px",
                                  margin: 0,
                                }}
                              >
                                Performance Metrics
                              </p>
                              <div
                                className="ms-responsive-grid"
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                                  gap: "12px",
                                }}
                              >
                                <StatCard
                                  label="Revenue per Download"
                                  value={formatMetricDecimal(marketingStrategyData.market_analysis.performance_metrics.revenue_per_download, 2, { prefix: "$" })}
                                />
                                <StatCard
                                  label="Review Rate"
                                  value={formatMetricDecimal(marketingStrategyData.market_analysis.performance_metrics.review_rate_pct, 2, { suffix: "%" })}
                                />
                              </div>
                            </div>
                          )}
                          {marketingStrategyData?.market_analysis?.growth_analysis && (
                            <div style={{ marginBottom: "16px" }}>
                              <p
                                style={{
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  color: "#ffffff",
                                  marginBottom: "12px",
                                  margin: 0,
                                }}
                              >
                                Growth Analysis
                              </p>
                              <InfoCard
                                label="Status"
                                value={marketingStrategyData.market_analysis.growth_analysis.status}
                                subtext={marketingStrategyData.market_analysis.growth_analysis.momentum_explanation}
                              />
                            </div>
                          )}
                          {marketingStrategyData.market_analysis.sentiment_analysis && (
                            <div style={{ marginBottom: "16px" }}>
                              <p
                                style={{
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  color: "#ffffff",
                                  marginBottom: "12px",
                                  margin: 0,
                                }}
                              >
                                Sentiment Analysis
                              </p>
                              <div className="pdf-no-break" style={{ backgroundColor: "rgba(55, 65, 81, 0.5)",
                                  borderRadius: "8px",
                                  padding: "16px",
                                  marginBottom: "12px",
                                }}
                              >
                                <p
                                  style={{
                                    fontSize: "14px",
                                    color: "#ffffff",
                                    marginBottom: "8px",
                                    margin: 0,
                                  }}
                                >
                                  Overall: {marketingStrategyData.market_analysis.sentiment_analysis.overall}
                                </p>
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "16px",
                                    marginTop: "8px",
                                  }}
                                >
                                  <span style={{ fontSize: "12px", color: "#10b981" }}>
                                    Positive: {marketingStrategyData.market_analysis.sentiment_analysis.positive_pct}%
                                  </span>
                                  <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                                    Neutral: {marketingStrategyData.market_analysis.sentiment_analysis.neutral_pct}%
                                  </span>
                                  <span style={{ fontSize: "12px", color: "#ef4444" }}>
                                    Negative: {marketingStrategyData.market_analysis.sentiment_analysis.negative_pct}%
                                  </span>
                                </div>
                              </div>
                              {marketingStrategyData.market_analysis.sentiment_analysis.top_positive_themes?.length >
                                0 && (
                                  <div style={{ marginBottom: "12px" }}>
                                    <p
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        color: "#10b981",
                                        marginBottom: "8px",
                                        margin: 0,
                                      }}
                                    >
                                      Top Positive Themes
                                    </p>
                                    {marketingStrategyData.market_analysis.sentiment_analysis.top_positive_themes.map(
                                      (theme, idx) => (
                                        <div className="pdf-no-break" key={idx}
                                          style={{
                                            padding: "10px",
                                            backgroundColor: "rgba(55, 65, 81, 0.2)",
                                            borderRadius: "6px",
                                            marginBottom: "8px",
                                          }}
                                        >
                                          <p
                                            style={{
                                              fontSize: "13px",
                                              fontWeight: "500",
                                              color: "#ffffff",
                                              marginBottom: "4px",
                                              margin: 0,
                                            }}
                                          >
                                            {theme.theme}
                                          </p>
                                          <p
                                            style={{
                                              fontSize: "11px",
                                              color: "#9ca3af",
                                              marginBottom: "4px",
                                              margin: "4px 0",
                                            }}
                                          >
                                            {theme.evidence}
                                          </p>
                                          <p
                                            style={{
                                              fontSize: "11px",
                                              color: "#10b981",
                                              margin: "4px 0 0 0",
                                            }}
                                          >
                                            {theme.marketing_implication}
                                          </p>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                )}
                              {marketingStrategyData.market_analysis.sentiment_analysis.top_negative_themes?.length >
                                0 && (
                                  <div>
                                    <p
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        color: "#ef4444",
                                        marginBottom: "8px",
                                        margin: 0,
                                      }}
                                    >
                                      Top Negative Themes
                                    </p>
                                    {marketingStrategyData.market_analysis.sentiment_analysis.top_negative_themes.map(
                                      (theme, idx) => (
                                        <div className="pdf-no-break" key={idx}
                                          style={{
                                            padding: "10px",
                                            backgroundColor: "rgba(55, 65, 81, 0.2)",
                                            borderRadius: "6px",
                                            marginBottom: "8px",
                                          }}
                                        >
                                          <p
                                            style={{
                                              fontSize: "13px",
                                              fontWeight: "500",
                                              color: "#ffffff",
                                              marginBottom: "4px",
                                              margin: 0,
                                            }}
                                          >
                                            {theme.theme}
                                          </p>
                                          <p
                                            style={{
                                              fontSize: "11px",
                                              color: "#9ca3af",
                                              marginBottom: "4px",
                                              margin: "4px 0",
                                            }}
                                          >
                                            {theme.evidence}
                                          </p>
                                          <p
                                            style={{
                                              fontSize: "11px",
                                              color: "#f59e0b",
                                              margin: "4px 0 0 0",
                                            }}
                                          >
                                            Fix: {theme.recommended_fix}
                                          </p>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                )}
                              {marketingStrategyData.market_analysis.sentiment_analysis.bug_complaints?.length > 0 && (
                                <div style={{ marginTop: "12px" }}>
                                  <p
                                    style={{
                                      fontSize: "12px",
                                      fontWeight: "600",
                                      color: "#ef4444",
                                      marginBottom: "8px",
                                      margin: 0,
                                    }}
                                  >
                                    Bug Complaints
                                  </p>
                                  {marketingStrategyData.market_analysis.sentiment_analysis.bug_complaints.map((bug, idx) => (
                                    <div className="pdf-no-break" key={idx}
                                      style={{
                                        padding: "10px",
                                        backgroundColor: "rgba(239, 68, 68, 0.1)",
                                        border: "1px solid rgba(239, 68, 68, 0.3)",
                                        borderRadius: "6px",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                                        <span style={{ fontSize: "13px", fontWeight: "600", color: "#ffffff" }}>
                                          {bug.bug}
                                        </span>
                                        <span
                                          style={{
                                            fontSize: "11px",
                                            color: bug.priority === "High" ? "#ef4444" : "#f59e0b",
                                          }}
                                        >
                                          Priority: {bug.priority}
                                        </span>
                                      </div>
                                      <div style={{ fontSize: "11px", color: "#9ca3af" }}>
                                        Impact: {bug.impact}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {marketingStrategyData.market_analysis.keyword_intelligence && (
                            <div style={{ marginTop: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-4">
                                <div
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                    borderRadius: "6px",
                                    background: "linear-gradient(135deg, #F6255A 0%, #e01e4a 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Search style={{ width: "14px", height: "14px", color: "#ffffff" }} />
                                </div>
                                <p style={{ fontSize: "14px", fontWeight: "700", color: "#ffffff", marginBottom: 0 }}>
                                  Keyword Intelligence
                                </p>
                              </div>
                              <div style={{ marginBottom: "16px" }}>
                                <div className="d-flex align-items-center gap-2 mb-3">
                                  <TrendingUp style={{ width: "14px", height: "14px", color: "#10b981" }} />
                                  <p style={{ fontSize: "12px", fontWeight: "600", color: "#10b981", marginBottom: 0 }}>
                                    Top Keywords by Download Volume
                                  </p>
                                </div>
                                <div style={{ background: "rgba(16, 185, 129, 0.1)", borderRadius: "10px", padding: "12px", border: "1px solid rgba(16, 185, 129, 0.2)" }}>
                                  {marketingStrategyData.market_analysis.keyword_intelligence.top_keywords_by_download_volume?.map((kw, idx) => (
                                    <div className="pdf-no-break" key={idx} style={{ fontSize: "13px", color: "#9ca3af", marginBottom: "6px", display: "flex", alignItems: "center", gap: "8px", paddingBottom: "6px", borderBottom: idx < marketingStrategyData.market_analysis.keyword_intelligence.top_keywords_by_download_volume.length - 1 ? "1px solid rgba(16, 185, 129, 0.1)" : "none" }}>
                                      <span style={{ color: "#ffffff", fontWeight: "600", flex: 1 }}>{kw.keyword}</span>
                                      <span style={{ color: "#10b981", fontWeight: "600" }}>{formatNumber(kw.estimated_downloads)}</span>
                                      <span style={{ fontSize: "11px", padding: "2px 6px", borderRadius: "4px", backgroundColor: "rgba(16, 185, 129, 0.2)", color: "#10b981", fontWeight: "600" }}>
                                        {kw.opportunity_score}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              {marketingStrategyData.market_analysis.keyword_intelligence.untapped_keywords?.length > 0 && (
                                <div style={{ marginBottom: "16px" }}>
                                  <div className="d-flex align-items-center gap-2 mb-3">
                                    <Target style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                    <p style={{ fontSize: "12px", fontWeight: "600", color: "#F6255A", marginBottom: 0 }}>
                                      Untapped Keywords
                                    </p>
                                  </div>
                                  {marketingStrategyData.market_analysis.keyword_intelligence.untapped_keywords.map((kw, idx) => (
                                    <div className="pdf-no-break" key={idx}
                                      style={{
                                        padding: "12px",
                                        background: "linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.05) 100%)",
                                        borderRadius: "10px",
                                        marginBottom: "8px",
                                        border: "1px solid rgba(59, 130, 246, 0.2)",
                                        transition: "all 0.2s ease",
                                      }}
                                    >
                                      <div style={{ fontSize: "14px", fontWeight: "600", color: "#ffffff", marginBottom: "6px" }}>
                                        {kw.keyword}
                                      </div>
                                      <div style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "6px", lineHeight: "1.5" }}>
                                        {kw.rationale}
                                      </div>
                                      <div style={{ fontSize: "12px", color: "#F6255A", fontWeight: "600", display: "flex", alignItems: "center", gap: "4px" }}>
                                        <ArrowUpRight style={{ width: "12px", height: "12px" }} />
                                        Estimated Traffic: {kw.estimated_traffic}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {marketingStrategyData.market_analysis.keyword_intelligence.keyword_gaps_vs_competitors?.length > 0 && (
                                <div>
                                  <div className="d-flex align-items-center gap-2 mb-3">
                                    <AlertTriangle style={{ width: "14px", height: "14px", color: "#f59e0b" }} />
                                    <p style={{ fontSize: "12px", fontWeight: "600", color: "#f59e0b", marginBottom: 0 }}>
                                      Keyword Gaps vs Competitors
                                    </p>
                                  </div>
                                  {marketingStrategyData.market_analysis.keyword_intelligence.keyword_gaps_vs_competitors.map((gap, idx) => (
                                    <div className="pdf-no-break" key={idx}
                                      style={{
                                        padding: "12px",
                                        background: "linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(245, 158, 11, 0.05) 100%)",
                                        borderRadius: "10px",
                                        marginBottom: "8px",
                                        border: "1px solid rgba(245, 158, 11, 0.2)",
                                        transition: "all 0.2s ease",
                                      }}
                                    >
                                      <div style={{ fontSize: "14px", fontWeight: "600", color: "#ffffff", marginBottom: "8px" }}>
                                        {gap.keyword}
                                      </div>
                                      <div style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "6px", display: "flex", gap: "16px" }}>
                                        <span>Competitor Ranking: <span style={{ color: "#ef4444", fontWeight: "600" }}>{gap.competitor_ranking}</span></span>
                                        <span>Our Status: <span style={{ color: "#10b981", fontWeight: "600" }}>{gap.our_status}</span></span>
                                      </div>
                                      <div style={{ fontSize: "12px", color: "#f59e0b", fontWeight: "600", display: "flex", alignItems: "center", gap: "4px" }}>
                                        <Zap style={{ width: "12px", height: "12px" }} />
                                        Action: {gap.action}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {marketingStrategyData.market_analysis.competitor_benchmarks?.length > 0 && (
                            <div style={{ marginTop: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-4">
                                <div
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                    borderRadius: "6px",
                                    background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Users style={{ width: "14px", height: "14px", color: "#ffffff" }} />
                                </div>
                                <p style={{ fontSize: "14px", fontWeight: "700", color: "#ffffff", marginBottom: 0 }}>
                                  Competitor Benchmarks
                                </p>
                              </div>
                              {marketingStrategyData.market_analysis.competitor_benchmarks.map((comp, idx) => (
                                <div className="pdf-no-break" key={idx} style={{ 
                                  padding: "14px", 
                                  background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)", 
                                  borderRadius: "10px", 
                                  marginBottom: "10px",
                                  border: "1px solid rgba(55, 65, 81, 0.6)",
                                  transition: "all 0.2s ease" 
                                }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                                    <div className="d-flex align-items-center gap-2">
                                      <Building2 style={{ width: "16px", height: "16px", color: "#9ca3af" }} />
                                      <span style={{ fontSize: "14px", fontWeight: "700", color: "#ffffff" }}>{comp.name}</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <Star style={{ width: "14px", height: "14px", color: "#F6255A", fill: "#F6255A" }} />
                                      <span style={{ fontSize: "13px", color: "#F6255A", fontWeight: "600" }}>{typeof comp.rating === 'number' ? comp.rating.toFixed(1) : comp.rating}</span>
                                    </div>
                                  </div>
                                  <div style={{ fontSize: "13px", color: "#9ca3af", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                                    <ShieldAlert style={{ width: "14px", height: "14px", color: comp.threat_level === "High" ? "#ef4444" : "#f59e0b" }} />
                                    Threat Level: <span style={{ color: comp.threat_level === "High" ? "#ef4444" : "#f59e0b", fontWeight: "600" }}>{comp.threat_level}</span>
                                  </div>
                                  <div style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic", lineHeight: "1.5" }}>
                                    {comp.threat_rationale}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {marketingStrategyData.market_analysis.market_opportunity_score && (
                            <div style={{ marginTop: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-4">
                                <div
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                    borderRadius: "6px",
                                    background: "linear-gradient(135deg, #F6255A 0%, #e01e4a 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Target style={{ width: "14px", height: "14px", color: "#ffffff" }} />
                                </div>
                                <p style={{ fontSize: "14px", fontWeight: "700", color: "#ffffff", marginBottom: 0 }}>
                                  Market Opportunity Score
                                </p>
                              </div>
                              <InfoCard
                                label="Overall Score"
                                value={formatMetricDecimal(marketingStrategyData.market_analysis.market_opportunity_score.overall, 1)}
                                subtext={marketingStrategyData.market_analysis.market_opportunity_score.rationale}
                                color="#F6255A"
                                icon={Star}
                              />
                              {marketingStrategyData.market_analysis.market_opportunity_score.subcategory_scores && (
                                <div style={{ marginTop: "16px" }}>
                                  <p
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: "600",
                                      color: "#ffffff",
                                      marginBottom: "12px",
                                      margin: 0,
                                    }}
                                  >
                                    Subcategory Scores
                                  </p>
                                  <div
                                    className="ms-responsive-grid"
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                                      gap: "12px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        padding: "12px",
                                        background: "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)",
                                        borderRadius: "10px",
                                        border: "1px solid rgba(16, 185, 129, 0.2)",
                                      }}
                                    >
                                      <div className="d-flex align-items-center gap-2 mb-2">
                                        <Search style={{ width: "12px", height: "12px", color: "#10b981" }} />
                                        <p style={{ fontSize: "11px", color: "#9ca3af", marginBottom: 0, textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "500" }}>
                                          ASO Opportunity
                                        </p>
                                      </div>
                                      <p style={{ fontSize: "16px", color: "#10b981", fontWeight: "700", marginBottom: 0 }}>
                                        {marketingStrategyData.market_analysis.market_opportunity_score.subcategory_scores.aso_opportunity}
                                      </p>
                                    </div>
                                    <div
                                      style={{
                                        padding: "12px",
                                        background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)",
                                        borderRadius: "10px",
                                        border: "1px solid rgba(59, 130, 246, 0.2)",
                                      }}
                                    >
                                      <div className="d-flex align-items-center gap-2 mb-2">
                                        <Heart style={{ width: "12px", height: "12px", color: "#F6255A" }} />
                                        <p style={{ fontSize: "11px", color: "#9ca3af", marginBottom: 0, textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "500" }}>
                                          Sentiment Opportunity
                                        </p>
                                      </div>
                                      <p style={{ fontSize: "16px", color: "#F6255A", fontWeight: "700", marginBottom: 0 }}>
                                        {marketingStrategyData.market_analysis.market_opportunity_score.subcategory_scores.sentiment_opportunity}
                                      </p>
                                    </div>
                                    <div
                                      style={{
                                        padding: "12px",
                                        background: "linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)",
                                        borderRadius: "10px",
                                        border: "1px solid rgba(245, 158, 11, 0.2)",
                                      }}
                                    >
                                      <div className="d-flex align-items-center gap-2 mb-2">
                                        <ArrowUpRight style={{ width: "12px", height: "12px", color: "#f59e0b" }} />
                                        <p style={{ fontSize: "11px", color: "#9ca3af", marginBottom: 0, textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "500" }}>
                                          Competitor Gap
                                        </p>
                                      </div>
                                      <p style={{ fontSize: "16px", color: "#f59e0b", fontWeight: "700", marginBottom: 0 }}>
                                        {marketingStrategyData.market_analysis.market_opportunity_score.subcategory_scores.competitor_gap_opportunity}
                                      </p>
                                    </div>
                                    <div
                                      style={{
                                        padding: "12px",
                                        background: "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)",
                                        borderRadius: "10px",
                                        border: "1px solid rgba(139, 92, 246, 0.2)",
                                      }}
                                    >
                                      <div className="d-flex align-items-center gap-2 mb-2">
                                        <FileText style={{ width: "12px", height: "12px", color: "#8b5cf6" }} />
                                        <p style={{ fontSize: "11px", color: "#9ca3af", marginBottom: 0, textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "500" }}>
                                          Content Opportunity
                                        </p>
                                      </div>
                                      <p style={{ fontSize: "16px", color: "#8b5cf6", fontWeight: "700", marginBottom: 0 }}>
                                        {marketingStrategyData.market_analysis.market_opportunity_score.subcategory_scores.content_opportunity}
                                      </p>
                                    </div>
                                    <div
                                      style={{
                                        padding: "12px",
                                        background: "linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(236, 72, 153, 0.05) 100%)",
                                        borderRadius: "10px",
                                        border: "1px solid rgba(236, 72, 153, 0.2)",
                                      }}
                                    >
                                      <div className="d-flex align-items-center gap-2 mb-2">
                                        <TrendingUp style={{ width: "12px", height: "12px", color: "#F6255A" }} />
                                        <p style={{ fontSize: "11px", color: "#9ca3af", marginBottom: 0, textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "500" }}>
                                          Growth Opportunity
                                        </p>
                                      </div>
                                      <p style={{ fontSize: "16px", color: "#F6255A", fontWeight: "700", marginBottom: 0 }}>
                                        {marketingStrategyData.market_analysis.market_opportunity_score.subcategory_scores.growth_opportunity}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Positioning */}
                      {marketingStrategyData?.positioning && (
                        <div style={{ marginBottom: "32px" }}>
                          <div className="d-flex align-items-center gap-2 mb-4">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "linear-gradient(135deg, #F6255A 0%, #e01e4a 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Target style={{ width: "16px", height: "16px", color: "#ffffff" }} />
                            </div>
                            <h6
                              style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                margin: 0,
                                color: "#ffffff",
                                letterSpacing: "-0.5px",
                              }}
                            >
                              Positioning
                            </h6>
                          </div>
                          {marketingStrategyData.positioning.tagline && (
                            <div
                              style={{
                                backgroundColor: "rgba(246, 37, 90, 0.1)",
                                border: "1px solid rgba(246, 37, 90, 0.3)",
                                borderRadius: "8px",
                                padding: "16px",
                                marginBottom: "16px",
                                textAlign: "center",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "18px",
                                  fontWeight: "700",
                                  color: "#F6255A",
                                  margin: 0,
                                }}
                              >
                                "{marketingStrategyData.positioning.tagline}"
                              </p>
                            </div>
                          )}
                          {marketingStrategyData.positioning.tagline_alternatives?.length > 0 && (
                            <div style={{ marginBottom: "16px" }}>
                              <p
                                style={{
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  color: "#ffffff",
                                  marginBottom: "8px",
                                  margin: 0,
                                }}
                              >
                                Alternative Taglines
                              </p>
                              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                {marketingStrategyData.positioning.tagline_alternatives.map((alt, idx) => (
                                  <span
                                    key={idx}
                                    style={{
                                      fontSize: "12px",
                                      backgroundColor: "rgba(55, 65, 81, 0.5)",
                                      color: "#9ca3af",
                                      padding: "6px 12px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    "{alt}"
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {marketingStrategyData.positioning.usp && (
                            <div style={{ marginBottom: "16px" }}>
                              <p
                                style={{
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  color: "#ffffff",
                                  marginBottom: "8px",
                                  margin: 0,
                                }}
                              >
                                Unique Selling Proposition
                              </p>
                              <p
                                style={{
                                  fontSize: "13px",
                                  color: "#9ca3af",
                                  lineHeight: "1.6",
                                  margin: "8px 0 0 0",
                                }}
                              >
                                {marketingStrategyData.positioning.usp}
                              </p>
                            </div>
                          )}
                          {marketingStrategyData.positioning.brand_voice && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-4">
                                <div
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                    borderRadius: "6px",
                                    background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <MessageSquare style={{ width: "14px", height: "14px", color: "#ffffff" }} />
                                </div>
                                <p
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "700",
                                    color: "#ffffff",
                                    marginBottom: 0,
                                  }}
                                >
                                  Brand Voice
                                </p>
                              </div>
                              <div className="pdf-no-break" style={{ background: "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)",
                                  borderRadius: "12px",
                                  padding: "16px",
                                  border: "1px solid rgba(139, 92, 246, 0.2)",
                                }}
                              >
                                <div style={{ marginBottom: "16px" }}>
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                    <Heart style={{ width: "12px", height: "12px", color: "#8b5cf6" }} />
                                    <p style={{ fontSize: "12px", color: "#9ca3af", marginBottom: 0, textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "500" }}>
                                      Tone
                                    </p>
                                  </div>
                                  <p style={{ fontSize: "14px", color: "#ffffff", fontWeight: "500", marginBottom: 0, marginTop: "8px", lineHeight: "1.5" }}>
                                    {marketingStrategyData.positioning.brand_voice.tone}
                                  </p>
                                </div>
                                {marketingStrategyData.positioning.brand_voice.personality_traits?.length > 0 && (
                                  <div style={{ marginBottom: "16px" }}>
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                      <Users style={{ width: "12px", height: "12px", color: "#8b5cf6" }} />
                                      <p style={{ fontSize: "12px", color: "#9ca3af", marginBottom: 0, textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "500" }}>
                                        Personality Traits
                                      </p>
                                    </div>
                                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                      {marketingStrategyData.positioning.brand_voice.personality_traits.map((trait, idx) => (
                                        <span
                                          key={idx}
                                          style={{
                                            fontSize: "12px",
                                            background: "linear-gradient(135deg, rgba(246, 37, 90, 0.2) 0%, rgba(246, 37, 90, 0.1) 100%)",
                                            color: "#F6255A",
                                            padding: "6px 12px",
                                            borderRadius: "8px",
                                            fontWeight: "500",
                                            border: "1px solid rgba(246, 37, 90, 0.3)",
                                          }}
                                        >
                                          {trait}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {marketingStrategyData.positioning.brand_voice.avoid?.length > 0 && (
                                  <div style={{ marginBottom: "16px" }}>
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                      <XCircle style={{ width: "12px", height: "12px", color: "#ef4444" }} />
                                      <p style={{ fontSize: "12px", color: "#ef4444", marginBottom: 0, textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "600" }}>
                                        Avoid
                                      </p>
                                    </div>
                                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                      {marketingStrategyData.positioning.brand_voice.avoid.map((item, idx) => (
                                        <span
                                          key={idx}
                                          style={{
                                            fontSize: "12px",
                                            background: "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(239, 68, 68, 0.05) 100%)",
                                            color: "#ef4444",
                                            padding: "6px 12px",
                                            borderRadius: "8px",
                                            fontWeight: "500",
                                            border: "1px solid rgba(239, 68, 68, 0.3)",
                                          }}
                                        >
                                          {item}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {marketingStrategyData.positioning.brand_voice.example_copy && (
                                  <div>
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                      <MessageSquare style={{ width: "12px", height: "12px", color: "#10b981" }} />
                                      <p style={{ fontSize: "12px", color: "#10b981", marginBottom: 0, textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: "600" }}>
                                        Example Copy
                                      </p>
                                    </div>
                                    <div style={{ 
                                      padding: "12px", 
                                      background: "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)",
                                      borderRadius: "8px",
                                      border: "1px solid rgba(16, 185, 129, 0.2)" 
                                    }}>
                                      <p style={{ fontSize: "13px", color: "#ffffff", fontStyle: "italic", margin: 0, lineHeight: "1.6" }}>
                                        "{marketingStrategyData.positioning.brand_voice.example_copy}"
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          {marketingStrategyData.positioning.target_audiences?.length > 0 && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-4">
                                <div
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                    borderRadius: "6px",
                                    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Users style={{ width: "14px", height: "14px", color: "#ffffff" }} />
                                </div>
                                <p
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "700",
                                    color: "#ffffff",
                                    marginBottom: 0,
                                  }}
                                >
                                  Target Audiences
                                </p>
                              </div>
                              {marketingStrategyData.positioning.target_audiences.map((audience, idx) => (
                                <div className="pdf-no-break" key={idx}
                                  style={{ background: "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)",
                                    borderRadius: "12px",
                                    padding: "16px",
                                    marginBottom: "12px",
                                    border: "1px solid rgba(16, 185, 129, 0.2)",
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                                    <p
                                      style={{
                                        fontSize: "15px",
                                        fontWeight: "700",
                                        color: "#ffffff",
                                        marginBottom: "0",
                                        margin: 0,
                                        flex: 1,
                                        minWidth: 0,
                                        marginRight: "8px",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {audience.segment}
                                    </p>
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        background: "linear-gradient(135deg, rgba(246, 37, 90, 0.2) 0%, rgba(246, 37, 90, 0.1) 100%)",
                                        color: "#F6255A",
                                        padding: "6px 12px",
                                        borderRadius: "8px",
                                        fontWeight: "700",
                                        border: "1px solid rgba(246, 37, 90, 0.3)",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {audience.size_estimate}
                                    </span>
                                  </div>
                                  <p
                                    style={{
                                      fontSize: "13px",
                                      color: "#9ca3af",
                                      lineHeight: "1.6",
                                      marginBottom: "12px",
                                      margin: "8px 0 12px 0",
                                    }}
                                  >
                                    {audience.description}
                                  </p>
                                  <div
                                    className="ms-responsive-grid"
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                                      gap: "10px",
                                      fontSize: "12px",
                                      marginBottom: "12px",
                                    }}
                                  >
                                    <div className="d-flex align-items-center gap-2">
                                      <Wallet style={{ width: "14px", height: "14px", color: "#10b981" }} />
                                      <span style={{ color: "#9ca3af" }}>LTV: </span>
                                      <span style={{ color: "#ffffff", fontWeight: "600" }}>{audience.estimated_ltv}</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <Globe style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                      <span style={{ color: "#9ca3af" }}>Primary: </span>
                                      <span style={{ color: "#ffffff", fontWeight: "600" }}>{audience.primary_channel}</span>
                                    </div>
                                  </div>
                                  {audience.pain_points?.length > 0 && (
                                    <div style={{ marginBottom: "12px" }}>
                                      <div className="d-flex align-items-center gap-2 mb-2">
                                        <AlertCircle style={{ width: "12px", height: "12px", color: "#ef4444" }} />
                                        <p style={{ fontSize: "12px", color: "#ef4444", marginBottom: 0, fontWeight: "600" }}>
                                          Pain Points
                                        </p>
                                      </div>
                                      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                        {audience.pain_points.map((point, idx) => (
                                          <span
                                            key={idx}
                                            style={{
                                              fontSize: "12px",
                                              background: "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(239, 68, 68, 0.05) 100%)",
                                              color: "#ef4444",
                                              padding: "6px 12px",
                                              borderRadius: "8px",
                                              fontWeight: "500",
                                              border: "1px solid rgba(239, 68, 68, 0.3)",
                                            }}
                                          >
                                            {point}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {audience.buying_triggers?.length > 0 && (
                                    <div>
                                      <div className="d-flex align-items-center gap-2 mb-2">
                                        <Zap style={{ width: "12px", height: "12px", color: "#10b981" }} />
                                        <p style={{ fontSize: "12px", color: "#10b981", marginBottom: 0, fontWeight: "600" }}>
                                          Buying Triggers
                                        </p>
                                      </div>
                                      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                        {audience.buying_triggers.map((trigger, idx) => (
                                          <span
                                            key={idx}
                                            style={{
                                              fontSize: "12px",
                                              background: "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)",
                                              color: "#10b981",
                                              padding: "6px 12px",
                                              borderRadius: "8px",
                                              fontWeight: "500",
                                              border: "1px solid rgba(16, 185, 129, 0.3)",
                                            }}
                                          >
                                            {trigger}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  <div
                                    className="ms-responsive-grid"
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                                      gap: "10px",
                                      fontSize: "12px",
                                      marginBottom: "12px",
                                    }}
                                  >
                                    <div className="d-flex align-items-center gap-2">
                                      <MessageSquare style={{ width: "14px", height: "14px", color: "#8b5cf6" }} />
                                      <span style={{ color: "#9ca3af" }}>Messaging: </span>
                                      <span style={{ color: "#ffffff", fontWeight: "600" }}>{audience.messaging_angle}</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <FileText style={{ width: "14px", height: "14px", color: "#f59e0b" }} />
                                      <span style={{ color: "#9ca3af" }}>Content: </span>
                                      <span style={{ color: "#ffffff", fontWeight: "600" }}>{audience.content_format}</span>
                                    </div>
                                  </div>
                                  {audience.secondary_channels?.length > 0 && (
                                    <div>
                                      <div className="d-flex align-items-center gap-2 mb-2">
                                        <Rss style={{ width: "12px", height: "12px", color: "#6b7280" }} />
                                        <p style={{ fontSize: "12px", color: "#6b7280", marginBottom: 0, fontWeight: "500" }}>
                                          Secondary Channels
                                        </p>
                                      </div>
                                      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                        {audience.secondary_channels.map((channel, idx) => (
                                          <span
                                            key={idx}
                                            style={{
                                              fontSize: "12px",
                                              background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                              color: "#ffffff",
                                              padding: "6px 12px",
                                              borderRadius: "8px",
                                              fontWeight: "500",
                                              border: "1px solid rgba(55, 65, 81, 0.6)",
                                            }}
                                          >
                                            {channel}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {marketingStrategyData.positioning.competitive_advantages?.length > 0 && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-4">
                                <div
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                    borderRadius: "6px",
                                    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <ShieldCheck style={{ width: "14px", height: "14px", color: "#ffffff" }} />
                                </div>
                                <p
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "700",
                                    color: "#ffffff",
                                    marginBottom: 0,
                                  }}
                                >
                                  Competitive Advantages
                                </p>
                              </div>
                              {marketingStrategyData.positioning.competitive_advantages.map((advantage, idx) => (
                                <div className="pdf-no-break" key={idx}
                                  style={{
                                    padding: "14px",
                                    background: "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)",
                                    border: "1px solid rgba(16, 185, 129, 0.2)",
                                    borderRadius: "10px",
                                    marginBottom: "10px",
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <p
                                    style={{
                                      fontSize: "14px",
                                      fontWeight: "700",
                                      color: "#ffffff",
                                      marginBottom: "8px",
                                      margin: 0,
                                    }}
                                  >
                                    {advantage.advantage}
                                  </p>
                                  <p
                                    style={{
                                      fontSize: "12px",
                                      color: "#9ca3af",
                                      marginBottom: "8px",
                                      margin: "8px 0 8px 0",
                                      lineHeight: "1.5",
                                    }}
                                  >
                                    {advantage.evidence}
                                  </p>
                                  {advantage.how_to_amplify && (
                                    <p
                                      style={{
                                        fontSize: "12px",
                                        color: "#10b981",
                                        marginBottom: 0,
                                        margin: 0,
                                        fontWeight: "600",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                      }}
                                    >
                                      <Zap style={{ width: "12px", height: "12px" }} />
                                      <strong>How to Amplify:</strong> {advantage.how_to_amplify}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {marketingStrategyData.positioning.weaknesses_to_address?.length > 0 && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-4">
                                <div
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                    borderRadius: "6px",
                                    background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <AlertTriangle style={{ width: "14px", height: "14px", color: "#ffffff" }} />
                                </div>
                                <p
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "700",
                                    color: "#ffffff",
                                    marginBottom: 0,
                                  }}
                                >
                                  Weaknesses to Address
                                </p>
                              </div>
                              {marketingStrategyData.positioning.weaknesses_to_address.map((weakness, idx) => (
                                <div className="pdf-no-break" key={idx}
                                  style={{
                                    padding: "14px",
                                    background: "linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)",
                                    border: "1px solid rgba(245, 158, 11, 0.2)",
                                    borderRadius: "10px",
                                    marginBottom: "10px",
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                                    <p
                                      style={{
                                        fontSize: "14px",
                                        fontWeight: "700",
                                        color: "#ffffff",
                                        marginBottom: 0,
                                        margin: 0,
                                      }}
                                    >
                                      {weakness.weakness}
                                    </p>
                                    <div style={{ display: "flex", gap: "10px" }}>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          padding: "4px 8px",
                                          borderRadius: "6px",
                                          backgroundColor: weakness.impact === "High" ? "rgba(239, 68, 68, 0.2)" : "rgba(245, 158, 11, 0.2)",
                                          color: weakness.impact === "High" ? "#ef4444" : "#f59e0b",
                                          fontWeight: "700",
                                        }}
                                      >
                                        {weakness.impact}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          padding: "4px 8px",
                                          borderRadius: "6px",
                                          backgroundColor: "rgba(107, 114, 128, 0.2)",
                                          color: "#9ca3af",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {weakness.timeline}
                                      </span>
                                    </div>
                                  </div>
                                  <p
                                    style={{
                                      fontSize: "12px",
                                      color: "#f59e0b",
                                      marginBottom: 0,
                                      margin: "8px 0 0 0",
                                      fontWeight: "600",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "6px",
                                    }}
                                  >
                                    <CheckCircle2 style={{ width: "12px", height: "12px" }} />
                                    Mitigation: {weakness.mitigation_strategy}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                          {marketingStrategyData.positioning.positioning_statement && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-4">
                                <div
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                    borderRadius: "6px",
                                    background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <FileText style={{ width: "14px", height: "14px", color: "#ffffff" }} />
                                </div>
                                <p
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "700",
                                    color: "#ffffff",
                                    marginBottom: 0,
                                  }}
                                >
                                  Positioning Statement
                                </p>
                              </div>
                              <div
                                style={{
                                  padding: "16px",
                                  background: "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)",
                                  borderRadius: "12px",
                                  border: "1px solid rgba(139, 92, 246, 0.2)",
                                }}
                              >
                                <p
                                  style={{
                                    fontSize: "14px",
                                    color: "#9ca3af",
                                    lineHeight: "1.6",
                                    fontStyle: "italic",
                                    margin: 0,
                                  }}
                                >
                                  "{marketingStrategyData.positioning.positioning_statement}"
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Campaigns */}
                      {marketingStrategyData?.campaigns?.length > 0 && (
                        <div style={{ marginBottom: "32px" }}>
                          <div className="d-flex align-items-center gap-2 mb-4">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "linear-gradient(135deg, #F6255A 0%, #e01e4a 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Layers style={{ width: "16px", height: "16px", color: "#ffffff" }} />
                            </div>
                            <h6
                              style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                margin: 0,
                                color: "#ffffff",
                                letterSpacing: "-0.5px",
                              }}
                            >
                              Campaigns
                            </h6>
                          </div>
                          <p
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              marginBottom: "16px",
                              color: "#10b981",
                            }}
                          >
                            Recommended Campaigns
                          </p>
                          {marketingStrategyData.campaigns.map((campaign, idx) => (
                            <div className="pdf-no-break" key={idx}
                              style={{ background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                borderRadius: "12px",
                                padding: "18px",
                                marginBottom: "16px",
                                pageBreakInside: "avoid",
                                border: "1px solid rgba(55, 65, 81, 0.6)",
                                transition: "all 0.2s ease",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  marginBottom: "14px",
                                }}
                              >
                                <div className="d-flex align-items-center gap-2" style={{ flex: 1, minWidth: 0, marginRight: "8px" }}>
                                  <Target style={{ width: "16px", height: "16px", color: "#F6255A" }} />
                                  <p
                                    style={{
                                      fontSize: "16px",
                                      fontWeight: "700",
                                      color: "#ffffff",
                                      marginBottom: "4px",
                                      margin: 0,
                                    }}
                                  >
                                    {campaign.name}
                                  </p>
                                </div>
                                <span
                                  style={{
                                    fontSize: "11px",
                                    padding: "6px 12px",
                                    borderRadius: "8px",
                                    backgroundColor:
                                      campaign.priority === "High"
                                        ? "rgba(239, 68, 68, 0.2)"
                                        : campaign.priority === "Medium"
                                          ? "rgba(245, 158, 11, 0.2)"
                                          : "rgba(16, 185, 129, 0.2)",
                                    color:
                                      campaign.priority === "High"
                                        ? "#ef4444"
                                        : campaign.priority === "Medium"
                                          ? "#f59e0b"
                                          : "#10b981",
                                    fontWeight: "700",
                                    border:
                                      campaign.priority === "High"
                                        ? "1px solid rgba(239, 68, 68, 0.3)"
                                        : campaign.priority === "Medium"
                                          ? "1px solid rgba(245, 158, 11, 0.3)"
                                          : "1px solid rgba(16, 185, 129, 0.3)",
                                    flexShrink: 0,
                                  }}
                                >
                                  {campaign.priority}
                                </span>
                              </div>
                              <p
                                style={{
                                  fontSize: "14px",
                                  color: "#9ca3af",
                                  marginBottom: "14px",
                                  margin: "0 0 14px 0",
                                  lineHeight: "1.6",
                                }}
                              >
                                {campaign.core_message}
                              </p>
                              <div
                                className="ms-responsive-grid"
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                                  gap: "10px",
                                  marginBottom: "14px",
                                  fontSize: "13px",
                                }}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <Users style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                  <span style={{ color: "#9ca3af" }}>Target: </span>
                                  <span style={{ color: "#ffffff", fontWeight: "600" }}>{campaign.target_audience}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <Wallet style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                  <span style={{ color: "#9ca3af" }}>Budget: </span>
                                  <span style={{ color: "#ffffff", fontWeight: "600" }}>{campaign.budget_range}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <Clock style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                  <span style={{ color: "#9ca3af" }}>Duration: </span>
                                  <span style={{ color: "#ffffff", fontWeight: "600" }}>{campaign.duration}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <TrendingUp style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                  <span style={{ color: "#9ca3af" }}>Expected ROI: </span>
                                  <span style={{ color: "#10b981", fontWeight: "600" }}>{campaign.expected_roi}</span>
                                </div>
                              </div>
                              {campaign.channels?.length > 0 && (
                                <div style={{ marginBottom: "14px" }}>
                                  <div className="d-flex align-items-center gap-2 mb-3">
                                    <Rss style={{ width: "14px", height: "14px", color: "#8b5cf6" }} />
                                    <p
                                      style={{
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        color: "#ffffff",
                                        marginBottom: 0,
                                      }}
                                    >
                                      Channels
                                    </p>
                                  </div>
                                  {campaign.channels.map((channel, chIdx) => (
                                    <div className="pdf-no-break" key={chIdx}
                                      style={{
                                        padding: "10px",
                                        background: "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)",
                                        borderRadius: "8px",
                                        marginBottom: "8px",
                                        border: "1px solid rgba(139, 92, 246, 0.2)",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: "8px",
                                      }}
                                    >
                                      <span style={{ color: "#ffffff", fontWeight: "600", flex: 1, minWidth: 0 }}>
                                        {channel.channel}
                                      </span>
                                      <div style={{ display: "flex", gap: "12px", fontSize: "12px", color: "#9ca3af", flexShrink: 0 }}>
                                        <span>{channel.role}</span>
                                        <span style={{ color: "#8b5cf6", fontWeight: "600" }}>{channel.budget_allocation_pct}% budget</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {campaign.content_ideas?.length > 0 && (
                                <div>
                                  <div className="d-flex align-items-center gap-2 mb-3">
                                    <FileText style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                    <p
                                      style={{
                                        fontSize: "13px",
                                        fontWeight: "600",
                                        color: "#ffffff",
                                        marginBottom: 0,
                                      }}
                                    >
                                      Content Ideas
                                    </p>
                                  </div>
                                  {campaign.content_ideas.map((idea, ideaIdx) => (
                                    <div className="pdf-no-break" key={ideaIdx}
                                      style={{
                                        padding: "10px",
                                        background: "linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(236, 72, 153, 0.05) 100%)",
                                        borderRadius: "8px",
                                        marginBottom: "8px",
                                        border: "1px solid rgba(236, 72, 153, 0.2)",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                      }}
                                    >
                                      <span style={{ color: "#ffffff", fontWeight: "600" }}>
                                        {idea.title}
                                      </span>
                                      <div style={{ display: "flex", gap: "12px", fontSize: "12px", color: "#9ca3af" }}>
                                        <span>{idea.format}</span>
                                        <span style={{ color: "#F6255A", fontWeight: "600" }}>{idea.platform}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Social Media Intelligence */}
                      {marketingStrategyData?.social_media_intelligence && (
                        <div style={{ marginBottom: "32px" }}>
                          <div className="d-flex align-items-center gap-2 mb-4">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "linear-gradient(135deg, #F6255A 0%, #e01e4a 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <MessageSquare style={{ width: "16px", height: "16px", color: "#ffffff" }} />
                            </div>
                            <h6
                              style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                margin: 0,
                                color: "#ffffff",
                                letterSpacing: "-0.5px",
                              }}
                            >
                              Social Media Intelligence
                            </h6>
                          </div>
                          <div style={{ padding: "16px", background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "12px" }}>
                            <p style={{ fontSize: "13px", color: "#9ca3af", margin: "0 0 12px 0", fontWeight: "500" }}>
                              Status: <span style={{ color: "#10b981", fontWeight: "600" }}>{marketingStrategyData?.social_media_intelligence?.status}</span>
                            </p>

                            {/* Platform Opportunities */}
                            {marketingStrategyData?.social_media_intelligence?.platform_opportunities?.length > 0 && (
                              <div style={{ marginTop: "16px" }}>
                                <div className="d-flex align-items-center gap-2 mb-3">
                                  <TrendingUp style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                  <p style={{ fontSize: "14px", fontWeight: "600", color: "#ffffff", marginBottom: 0 }}>
                                    Platform Opportunities
                                  </p>
                                </div>
                                <div className="ms-responsive-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "10px" }}>
                                  {marketingStrategyData?.social_media_intelligence?.platform_opportunities?.map((opportunity, idx) => (
                                    <div className="pdf-no-break" key={idx} style={{ 
                                      padding: "12px", 
                                      background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)", 
                                      borderRadius: "10px",
                                      border: "1px solid rgba(55, 65, 81, 0.6)",
                                      transition: "all 0.2s ease" 
                                    }}>
                                      <p style={{ fontSize: "13px", fontWeight: "700", color: "#ffffff", margin: "0 0 6px 0" }}>
                                        {opportunity.platform.toUpperCase()}
                                      </p>
                                      <p style={{ fontSize: "12px", color: "#9ca3af", margin: "0", lineHeight: "1.5" }}>
                                        {opportunity.insight}
                                      </p>
                                      {opportunity.competitors_count > 0 && (
                                        <div style={{ marginTop: "8px" }}>
                                          <span style={{ 
                                            fontSize: "11px", 
                                            color: "#f59e0b",
                                            fontWeight: "600",
                                            padding: "2px 8px",
                                            borderRadius: "4px",
                                            backgroundColor: "rgba(245, 158, 11, 0.2)"
                                          }}>
                                            {opportunity.competitors_count} competitors
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Competitor Social Presence */}
                            {/* {marketingStrategyData?.social_media_intelligence?.competitor_social_presence?.length > 0 && (
                              <div style={{ marginTop: "16px" }}>
                                <p style={{ fontSize: "13px", fontWeight: "600", color: "#ffffff", marginBottom: "8px" }}>
                                  Competitor Social Presence
                                </p>
                                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                  {marketingStrategyData?.social_media_intelligence?.competitor_social_presence?.slice(0, 3).map((competitor, idx) => (
                                    <div className="pdf-no-break" key={idx} style={{ padding: "8px", backgroundColor: "rgba(55, 65, 81, 0.5)", borderRadius: "6px" }}>
                                      <p style={{ fontSize: "12px", fontWeight: "600", color: "#ffffff", margin: "0 0 6px 0" }}>
                                        {competitor.competitor} - {competitor.publisher_name}
                                      </p>
                                      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                                        {competitor.platforms?.map((platform, pIdx) => (
                                          <div className="pdf-no-break" key={pIdx} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                            <span style={{
                                              fontSize: "10px",
                                              padding: "2px 6px",
                                              borderRadius: "3px",
                                              backgroundColor: platform.presence_level === "Good" ? "rgba(16, 185, 129, 0.2)" :
                                                               platform.presence_level === "Medium" ? "rgba(245, 158, 11, 0.2)" :
                                                               "rgba(239, 68, 68, 0.2)",
                                              color: platform.presence_level === "Good" ? "#10b981" :
                                                     platform.presence_level === "Medium" ? "#f59e0b" :
                                                     "#ef4444",
                                              fontWeight: "600"
                                            }}>
                                              {platform.platform_name}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )} */}

                            {marketingStrategyData?.social_media_intelligence?.summary && (
                              <p style={{ fontSize: "11px", color: "#9ca3af", margin: "12px 0 0 0", fontStyle: "italic" }}>
                                {marketingStrategyData?.social_media_intelligence?.summary}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Competitor Ad Intelligence */}
                      {marketingStrategyData?.competitor_ad_intelligence?.length > 0 && (
                        <div style={{ marginBottom: "32px" }}>
                          <div className="d-flex align-items-center gap-2 mb-4">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Eye style={{ width: "16px", height: "16px", color: "#ffffff" }} />
                            </div>
                            <h6
                              style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                margin: 0,
                                color: "#ffffff",
                                letterSpacing: "-0.5px",
                              }}
                            >
                              Competitor Ad Intelligence
                            </h6>
                          </div>
                          {marketingStrategyData?.competitor_ad_intelligence?.map((adIntel, idx) => (
                            <div className="pdf-no-break" key={idx} style={{ 
                              padding: "14px", 
                              background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)", 
                              border: "1px solid rgba(55, 65, 81, 0.6)", 
                              borderRadius: "10px", 
                              marginBottom: "10px",
                              transition: "all 0.2s ease" 
                            }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                                <div className="d-flex align-items-center gap-2">
                                  <Building2 style={{ width: "16px", height: "16px", color: "#9ca3af" }} />
                                  <p style={{ fontSize: "15px", fontWeight: "700", color: "#ffffff", margin: "0" }}>
                                    {adIntel.name}
                                  </p>
                                </div>
                                <span style={{
                                  fontSize: "11px",
                                  padding: "6px 12px",
                                  borderRadius: "8px",
                                  backgroundColor: adIntel.threat_level === "High" ? "rgba(239, 68, 68, 0.2)" :
                                                 adIntel.threat_level === "Medium" ? "rgba(245, 158, 11, 0.2)" : "rgba(16, 185, 129, 0.2)",
                                  color: adIntel.threat_level === "High" ? "#ef4444" :
                                         adIntel.threat_level === "Medium" ? "#f59e0b" : "#10b981",
                                  fontWeight: "700",
                                  border: adIntel.threat_level === "High" ? "1px solid rgba(239, 68, 68, 0.3)" :
                                         adIntel.threat_level === "Medium" ? "1px solid rgba(245, 158, 11, 0.3)" : "1px solid rgba(16, 185, 129, 0.3)"
                                }}>
                                  {adIntel.threat_level} Threat
                                </span>
                              </div>
                              <div className="ms-responsive-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px", marginBottom: "12px" }}>
                                <div className="d-flex align-items-center gap-2">
                                  <Rss style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                  <span style={{ fontSize: "12px", color: "#9ca3af" }}>Ads: </span>
                                  <span style={{ fontSize: "13px", color: "#ffffff", fontWeight: "700" }}>{adIntel.ad_count}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <CircleDot style={{ width: "14px", height: "14px", color: adIntel.has_active_ads ? "#10b981" : "#ef4444" }} />
                                  <span style={{ fontSize: "12px", color: "#9ca3af" }}>Active: </span>
                                  <span style={{ fontSize: "13px", color: adIntel.has_active_ads ? "#10b981" : "#ef4444", fontWeight: "700" }}>
                                    {adIntel.has_active_ads ? "Yes" : "No"}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <Layers style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                  <span style={{ fontSize: "12px", color: "#9ca3af" }}>Platforms: </span>
                                  <span style={{ fontSize: "13px", color: "#ffffff", fontWeight: "700" }}>{adIntel.platforms?.length || 0}</span>
                                </div>
                              </div>
                              {adIntel.unique_strategy && (
                                <div style={{ marginTop: "10px", padding: "12px", background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)", borderRadius: "8px", border: "1px solid rgba(55, 65, 81, 0.6)" }}>
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                    <Target style={{ width: "12px", height: "12px", color: "#9ca3af" }} />
                                    <p style={{ fontSize: "12px", color: "#9ca3af", margin: "0", fontWeight: "600" }}>Strategy:</p>
                                  </div>
                                  <p style={{ fontSize: "13px", color: "#ffffff", margin: "0", lineHeight: "1.5" }}>{adIntel.unique_strategy}</p>
                                </div>
                              )}
                              {adIntel.counter_strategy && (
                                <div style={{ marginTop: "10px", padding: "12px", background: "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)", borderRadius: "8px", border: "1px solid rgba(16, 185, 129, 0.3)" }}>
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                    <CheckCircle2 style={{ width: "12px", height: "12px", color: "#10b981" }} />
                                    <p style={{ fontSize: "12px", color: "#10b981", margin: "0", fontWeight: "600" }}>Counter Strategy:</p>
                                  </div>
                                  <p style={{ fontSize: "13px", color: "#ffffff", margin: "0", lineHeight: "1.5" }}>{adIntel.counter_strategy}</p>
                                </div>
                              )}
                              {adIntel.opportunity && (
                                <div style={{ marginTop: "10px", padding: "12px", background: "linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)", borderRadius: "8px", border: "1px solid rgba(245, 158, 11, 0.3)" }}>
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                    <ArrowUpRight style={{ width: "12px", height: "12px", color: "#f59e0b" }} />
                                    <p style={{ fontSize: "12px", color: "#f59e0b", margin: "0", fontWeight: "600" }}>Opportunity:</p>
                                  </div>
                                  <p style={{ fontSize: "13px", color: "#ffffff", margin: "0", lineHeight: "1.5" }}>{adIntel.opportunity}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* SEO/ASO Strategy */}
                      {marketingStrategyData?.seo_aso_strategy && (
                        <div style={{ marginBottom: "32px" }}>
                          <div className="d-flex align-items-center gap-2 mb-4">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Search style={{ width: "16px", height: "16px", color: "#ffffff" }} />
                            </div>
                            <h6
                              style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                margin: 0,
                                color: "#ffffff",
                                letterSpacing: "-0.5px",
                              }}
                            >
                              SEO/ASO Strategy
                            </h6>
                          </div>
                          {marketingStrategyData.seo_aso_strategy.current_aso_health && (
                            <div style={{ marginBottom: "16px" }}>
                              <InfoCard
                                label="ASO Health Score"
                                value={marketingStrategyData.seo_aso_strategy.current_aso_health.score}
                                subtext={`Strengths: ${marketingStrategyData.seo_aso_strategy.current_aso_health.strengths?.join(", ")}`}
                              />
                            </div>
                          )}
                          {marketingStrategyData.seo_aso_strategy.title_optimization && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <FileText style={{ width: "14px", height: "14px", color: "#10b981" }} />
                                <p
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#ffffff",
                                    marginBottom: 0,
                                  }}
                                >
                                  Title Optimization
                                </p>
                              </div>
                              <div className="pdf-no-break" style={{ background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                  borderRadius: "10px",
                                  padding: "14px",
                                  border: "1px solid rgba(55, 65, 81, 0.6)",
                                }}
                              >
                                <p
                                  style={{
                                    fontSize: "12px",
                                    color: "#9ca3af",
                                    marginBottom: "8px",
                                    margin: 0,
                                    fontWeight: "500",
                                  }}
                                >
                                  Current: {marketingStrategyData.seo_aso_strategy.title_optimization.current_title}
                                </p>
                                <p
                                  style={{
                                    fontSize: "14px",
                                    color: "#10b981",
                                    fontWeight: "600",
                                    marginBottom: "8px",
                                    margin: "8px 0",
                                    padding: "10px",
                                    background: "rgba(16, 185, 129, 0.1)",
                                    borderRadius: "6px",
                                    border: "1px solid rgba(16, 185, 129, 0.2)",
                                  }}
                                >
                                  Recommended: {marketingStrategyData.seo_aso_strategy.title_optimization.recommended_title}
                                </p>
                                <p
                                  style={{
                                    fontSize: "11px",
                                    color: "#9ca3af",
                                    margin: "4px 0 0 0",
                                  }}
                                >
                                  {marketingStrategyData.seo_aso_strategy.title_optimization.rationale}
                                </p>
                              </div>
                            </div>
                          )}
                          {marketingStrategyData.seo_aso_strategy.keyword_strategy?.length > 0 && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Search style={{ width: "14px", height: "14px", color: "#10b981" }} />
                                <p
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#ffffff",
                                    marginBottom: 0,
                                  }}
                                >
                                  Keyword Strategy
                                </p>
                              </div>
                              {marketingStrategyData.seo_aso_strategy.keyword_strategy.map((keyword, idx) => (
                                <div className="pdf-no-break" key={idx}
                                  style={{
                                    padding: "12px",
                                    background: "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)",
                                    borderRadius: "10px",
                                    marginBottom: "10px",
                                    border: "1px solid rgba(16, 185, 129, 0.2)",
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <span style={{ color: "#ffffff", fontWeight: "700", fontSize: "14px" }}>{keyword.keyword}</span>
                                    <span style={{ 
                                      color: "#10b981", 
                                      fontWeight: "700",
                                      padding: "4px 8px",
                                      borderRadius: "6px",
                                      backgroundColor: "rgba(16, 185, 129, 0.2)",
                                      fontSize: "11px"
                                    }}>
                                      Priority: #{keyword.priority_rank}
                                    </span>
                                  </div>
                                  <div className="d-flex gap-6" style={{ color: "#9ca3af", margin: "8px 0 0 0", fontSize: "13px" }}>
                                    <div className="d-flex align-items-center gap-2">
                                      <TrendingUp style={{ width: "12px", height: "12px" }} />
                                      <span>Downloads: {formatNumber(keyword.monthly_download_volume)}</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <Target style={{ width: "12px", height: "12px" }} />
                                      <span>{keyword.recommended_placement}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Content Strategy */}
                      {marketingStrategyData?.content_strategy && (
                        <div style={{ marginBottom: "32px" }}>
                          <div className="d-flex align-items-center gap-2 mb-4">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <FileText style={{ width: "16px", height: "16px", color: "#ffffff" }} />
                            </div>
                            <h6
                              style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                margin: 0,
                                color: "#ffffff",
                                letterSpacing: "-0.5px",
                              }}
                            >
                              Content Strategy
                            </h6>
                          </div>
                          {marketingStrategyData.content_strategy.content_pillars?.length > 0 && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Layers style={{ width: "14px", height: "14px", color: "#8b5cf6" }} />
                                <p
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#ffffff",
                                    marginBottom: 0,
                                  }}
                                >
                                  Content Pillars
                                </p>
                              </div>
                              {marketingStrategyData.content_strategy.content_pillars.map((pillar, idx) => (
                                <div className="pdf-no-break" key={idx}
                                  style={{
                                    padding: "12px",
                                    background: "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)",
                                    borderRadius: "10px",
                                    marginBottom: "10px",
                                    border: "1px solid rgba(139, 92, 246, 0.2)",
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <p
                                    style={{
                                      fontSize: "15px",
                                      fontWeight: "700",
                                      color: "#ffffff",
                                      marginBottom: "8px",
                                      margin: 0,
                                    }}
                                  >
                                    {pillar.pillar}
                                  </p>
                                  <div className="d-flex gap-6" style={{ fontSize: "13px", color: "#9ca3af" }}>
                                    <div className="d-flex align-items-center gap-2">
                                      <FileText style={{ width: "12px", height: "12px" }} />
                                      <span>{pillar.content_types?.join(", ")}</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <Clock style={{ width: "12px", height: "12px" }} />
                                      <span>{pillar.cadence}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {marketingStrategyData.content_strategy.viral_hooks?.length > 0 && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Zap style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <p
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#ffffff",
                                    marginBottom: 0,
                                  }}
                                >
                                  Viral Hooks
                                </p>
                              </div>
                              {marketingStrategyData.content_strategy.viral_hooks.map((hook, idx) => (
                                <div className="pdf-no-break" key={idx}
                                  style={{
                                    padding: "12px",
                                    background: "linear-gradient(135deg, rgba(246, 37, 90, 0.1) 0%, rgba(246, 37, 90, 0.05) 100%)",
                                    border: "1px solid rgba(246, 37, 90, 0.3)",
                                    borderRadius: "10px",
                                    marginBottom: "10px",
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <p
                                    style={{
                                      fontSize: "15px",
                                      fontWeight: "700",
                                      color: "#F6255A",
                                      marginBottom: "8px",
                                      margin: 0,
                                    }}
                                  >
                                    {hook.hook}
                                  </p>
                                  <div className="d-flex gap-6" style={{ fontSize: "13px", color: "#9ca3af" }}>
                                    <div className="d-flex align-items-center gap-2">
                                      <Globe style={{ width: "12px", height: "12px" }} />
                                      <span>{hook.platform}</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <Bot style={{ width: "12px", height: "12px" }} />
                                      <span>{hook.mechanic}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {marketingStrategyData.content_strategy.platform_breakdown?.length > 0 && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Globe style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <p
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#ffffff",
                                    marginBottom: 0,
                                  }}
                                >
                                  Platform Breakdown
                                </p>
                              </div>
                              {marketingStrategyData.content_strategy.platform_breakdown.map(
                                (platform, idx) => (
                                  <div className="pdf-no-break" key={idx}
                                    style={{
                                      padding: "12px",
                                      background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)",
                                      borderRadius: "10px",
                                      marginBottom: "10px",
                                      border: "1px solid rgba(59, 130, 246, 0.2)",
                                      transition: "all 0.2s ease",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      <span style={{ color: "#ffffff", fontWeight: "700", fontSize: "14px" }}>
                                        {platform.platform}
                                      </span>
                                      <span style={{ 
                                        color: "#10b981", 
                                        fontWeight: "700",
                                        padding: "4px 8px",
                                        borderRadius: "6px",
                                        backgroundColor: "rgba(16, 185, 129, 0.2)",
                                        fontSize: "11px"
                                      }}>
                                        {platform.audience_fit}
                                      </span>
                                    </div>
                                    <div className="d-flex gap-6" style={{ color: "#9ca3af", margin: "8px 0 0 0", fontSize: "13px" }}>
                                      <div className="d-flex align-items-center gap-2">
                                        <FileText style={{ width: "12px", height: "12px" }} />
                                        <span>{platform.content_type}</span>
                                      </div>
                                      <div className="d-flex align-items-center gap-2">
                                        <Clock style={{ width: "12px", height: "12px" }} />
                                        <span>{platform.frequency}</span>
                                      </div>
                                      <div className="d-flex align-items-center gap-2">
                                        <Sun style={{ width: "12px", height: "12px", color: "#f59e0b" }} />
                                        <span>{platform.best_posting_time}</span>
                                      </div>
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          )}
                        </div>
                      )}


                      {/* Retention and Growth */}
                      {marketingStrategyData?.retention_and_growth && (
                        <div style={{ marginBottom: "32px" }}>
                          <div className="d-flex align-items-center gap-2 mb-4">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <TrendingUp style={{ width: "16px", height: "16px", color: "#ffffff" }} />
                            </div>
                            <h6
                              style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                margin: 0,
                                color: "#ffffff",
                                letterSpacing: "-0.5px",
                              }}
                            >
                              Retention and Growth
                            </h6>
                          </div>
                          {marketingStrategyData.retention_and_growth.current_retention_health && (
                            <InfoCard
                              label="Current Retention Health"
                              value={marketingStrategyData.retention_and_growth.current_retention_health.assessment}
                              subtext={marketingStrategyData.retention_and_growth.current_retention_health.evidence}
                              color={marketingStrategyData.retention_and_growth.current_retention_health.assessment === "At Risk" ? "#ef4444" : "#10b981"}
                            />
                          )}
                          {marketingStrategyData.retention_and_growth.churn_risks?.length > 0 && (
                            <div style={{ marginTop: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <AlertTriangle style={{ width: "14px", height: "14px", color: "#ef4444" }} />
                                <p style={{ fontSize: "14px", fontWeight: "600", color: "#ffffff", marginBottom: 0 }}>Churn Risks</p>
                              </div>
                              {marketingStrategyData.retention_and_growth.churn_risks.map((risk, idx) => (
                                <div className="pdf-no-break" key={idx} style={{ 
                                  padding: "12px",
                                  background: "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)",
                                  borderRadius: "10px",
                                  marginBottom: "10px",
                                  border: "1px solid rgba(239, 68, 68, 0.3)",
                                  transition: "all 0.2s ease"
                                }}>
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                    <XCircle style={{ width: "12px", height: "12px", color: "#ef4444" }} />
                                    <span style={{ fontSize: "14px", fontWeight: "700", color: "#ef4444" }}>{risk.risk}</span>
                                  </div>
                                  <div className="d-flex align-items-center gap-2" style={{ fontSize: "13px", color: "#9ca3af", marginTop: "6px" }}>
                                    <CheckCircle2 style={{ width: "12px", height: "12px", color: "#10b981" }} />
                                    <span>{risk.mitigation}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Action Feed */}
                      {marketingStrategyData?.action_feed && (
                        <div style={{ marginBottom: "32px" }}>
                          <div className="d-flex align-items-center gap-2 mb-4">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "linear-gradient(135deg, #F6255A 0%, #e01e4a 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Zap style={{ width: "16px", height: "16px", color: "#ffffff" }} />
                            </div>
                            <h6
                              style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                margin: 0,
                                color: "#ffffff",
                                letterSpacing: "-0.5px",
                              }}
                            >
                              Action Feed
                            </h6>
                          </div>
                          {marketingStrategyData.action_feed.week_1?.length > 0 && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Clock style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <p
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#ffffff",
                                    marginBottom: 0,
                                  }}
                                >
                                  Week 1
                                </p>
                              </div>
                              {marketingStrategyData.action_feed.week_1.map((action, idx) => (
                                <ActionItem key={idx} action={action} />
                              ))}
                            </div>
                          )}
                          {marketingStrategyData.action_feed.month_1?.length > 0 && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Calendar style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <p
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#ffffff",
                                    marginBottom: 0,
                                  }}
                                >
                                  Month 1
                                </p>
                              </div>
                              {marketingStrategyData.action_feed.month_1.map((action, idx) => (
                                <ActionItem key={idx} action={action} />
                              ))}
                            </div>
                          )}
                          {marketingStrategyData.action_feed.quarter_1?.length > 0 && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <TrendingUp style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <p
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#ffffff",
                                    marginBottom: 0,
                                  }}
                                >
                                  Quarter 1
                                </p>
                              </div>
                              {marketingStrategyData.action_feed.quarter_1.map((action, idx) => (
                                <ActionItem key={idx} action={action} />
                              ))}
                            </div>
                          )}
                          {marketingStrategyData.action_feed.north_star_goal && (
                            <div
                              style={{
                                padding: "14px",
                                background: "linear-gradient(135deg, rgba(246, 37, 90, 0.1) 0%, rgba(246, 37, 90, 0.05) 100%)",
                                border: "1px solid rgba(246, 37, 90, 0.3)",
                                borderRadius: "10px",
                                marginTop: "16px",
                              }}
                            >
                              <div className="d-flex align-items-center gap-2 mb-2">
                                <Target style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <p
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: "#F6255A",
                                    marginBottom: 0,
                                  }}
                                >
                                  North Star Goal
                                </p>
                              </div>
                              <p
                                style={{
                                  fontSize: "14px",
                                  color: "#ffffff",
                                  margin: "8px 0 0 0",
                                  fontWeight: "500",
                                }}
                              >
                                {marketingStrategyData.action_feed.north_star_goal}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Budget Allocation */}
                      {marketingStrategyData?.budget_allocation && (
                        <div style={{ marginBottom: "32px" }}>
                          <div className="d-flex align-items-center gap-2 mb-4">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "linear-gradient(135deg, #F6255A 0%, #e01e4a 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <DollarSign style={{ width: "16px", height: "16px", color: "#ffffff" }} />
                            </div>
                            <h6
                              style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                margin: 0,
                                color: "#ffffff",
                                letterSpacing: "-0.5px",
                              }}
                            >
                              Budget Allocation
                            </h6>
                          </div>
                          <div className="pdf-no-break" style={{ background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)",
                              borderRadius: "12px",
                              padding: "20px",
                              marginBottom: "20px",
                              border: "1px solid rgba(59, 130, 246, 0.2)",
                            }}
                          >
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <DollarSign style={{ width: "20px", height: "20px", color: "#F6255A" }} />
                              <p
                                style={{
                                  fontSize: "22px",
                                  fontWeight: "700",
                                  color: "#F6255A",
                                  marginBottom: 0,
                                  margin: 0,
                                }}
                              >
                                {marketingStrategyData.budget_allocation.total_budget_recommendation}
                              </p>
                            </div>
                            <p
                              style={{
                                fontSize: "14px",
                                color: "#9ca3af",
                                margin: "12px 0 0 0",
                                lineHeight: "1.6",
                              }}
                            >
                              {marketingStrategyData.budget_allocation.rationale}
                            </p>
                          </div>
                          {marketingStrategyData.budget_allocation.breakdown?.length > 0 && (
                            <div>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <PieChart style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <p style={{ fontSize: "14px", fontWeight: "600", color: "#ffffff", marginBottom: 0 }}>
                                  Breakdown
                                </p>
                              </div>
                              {marketingStrategyData.budget_allocation.breakdown.map((item, idx) => (
                                <div className="pdf-no-break" key={idx}
                                  style={{
                                    padding: "14px",
                                    background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                    borderRadius: "10px",
                                    marginBottom: "10px",
                                    border: "1px solid rgba(55, 65, 81, 0.6)",
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    <div className="d-flex align-items-center gap-2">
                                      <Layers style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                      <span style={{ color: "#ffffff", fontWeight: "700", fontSize: "15px" }}>
                                        {item.category}
                                      </span>
                                    </div>
                                    <span style={{ 
                                      color: "#F6255A", 
                                      fontWeight: "700",
                                      padding: "6px 12px",
                                      borderRadius: "8px",
                                      backgroundColor: "rgba(246, 37, 90, 0.2)",
                                      fontSize: "14px"
                                    }}>
                                      {item.allocation_pct}%
                                    </span>
                                  </div>
                                  <div className="d-flex gap-6" style={{ fontSize: "13px", color: "#9ca3af", marginTop: "8px" }}>
                                    <div className="d-flex align-items-center gap-2">
                                      <Wallet style={{ width: "12px", height: "12px" }} />
                                      <span>{item.monthly_estimate}</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <MessageSquare style={{ width: "12px", height: "12px" }} />
                                      <span>{item.rationale}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Risk Register */}
                      {marketingStrategyData?.risk_register?.length > 0 && (
                        <div style={{ marginBottom: "32px" }}>
                          <div className="d-flex align-items-center gap-2 mb-4">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "linear-gradient(135deg, #F6255A 0%, #e01e4a 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <AlertTriangle style={{ width: "16px", height: "16px", color: "#ffffff" }} />
                            </div>
                            <h6
                              style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                margin: 0,
                                color: "#ffffff",
                                letterSpacing: "-0.5px",
                              }}
                            >
                              Risk Register
                            </h6>
                          </div>
                          {marketingStrategyData.risk_register.map((risk, idx) => (
                            <div className="pdf-no-break" key={idx}
                              style={{
                                padding: "14px",
                                background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                borderRadius: "10px",
                                marginBottom: "10px",
                                border: "1px solid rgba(55, 65, 81, 0.6)",
                                transition: "all 0.2s ease",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: "12px",
                                }}
                              >
                                <span style={{ color: "#ffffff", fontWeight: "700", fontSize: "15px" }}>
                                  {risk.risk}
                                </span>
                                <div style={{ display: "flex", gap: "10px", fontSize: "12px" }}>
                                  <span
                                    style={{
                                      padding: "6px 12px",
                                      borderRadius: "8px",
                                      backgroundColor:
                                        risk.likelihood === "High"
                                          ? "rgba(239, 68, 68, 0.2)"
                                          : risk.likelihood === "Medium"
                                            ? "rgba(245, 158, 11, 0.2)"
                                            : "rgba(16, 185, 129, 0.2)",
                                      color:
                                        risk.likelihood === "High"
                                          ? "#ef4444"
                                          : risk.likelihood === "Medium"
                                            ? "#f59e0b"
                                            : "#10b981",
                                      fontWeight: "700",
                                      border:
                                        risk.likelihood === "High"
                                          ? "1px solid rgba(239, 68, 68, 0.3)"
                                          : risk.likelihood === "Medium"
                                            ? "1px solid rgba(245, 158, 11, 0.3)"
                                            : "1px solid rgba(16, 185, 129, 0.3)",
                                    }}
                                  >
                                    {risk.likelihood}
                                  </span>
                                  <span
                                    style={{
                                      padding: "6px 12px",
                                      borderRadius: "8px",
                                      backgroundColor:
                                        risk.impact === "High"
                                          ? "rgba(239, 68, 68, 0.2)"
                                          : risk.impact === "Medium"
                                            ? "rgba(245, 158, 11, 0.2)"
                                            : "rgba(16, 185, 129, 0.2)",
                                      color:
                                        risk.impact === "High"
                                          ? "#ef4444"
                                          : risk.impact === "Medium"
                                            ? "#f59e0b"
                                            : "#10b981",
                                      fontWeight: "700",
                                      border:
                                        risk.impact === "High"
                                          ? "1px solid rgba(239, 68, 68, 0.3)"
                                          : risk.impact === "Medium"
                                            ? "1px solid rgba(245, 158, 11, 0.3)"
                                            : "1px solid rgba(16, 185, 129, 0.3)",
                                    }}
                                  >
                                    {risk.impact}
                                  </span>
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2" style={{ fontSize: "13px", color: "#9ca3af", marginBottom: "8px" }}>
                                <AlertCircle style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <span style={{ fontWeight: "500" }}>Trigger: {risk.trigger}</span>
                              </div>
                              <div className="d-flex align-items-center gap-2" style={{ fontSize: "13px", color: "#10b981", fontWeight: "600", margin: 0 }}>
                                <CheckCircle2 style={{ width: "14px", height: "14px" }} />
                                <span>Mitigation: {risk.mitigation}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Success Metrics */}
                      {marketingStrategyData?.success_metrics_dashboard && (
                        <div style={{ marginBottom: "32px" }}>
                          <div className="d-flex align-items-center gap-2 mb-4">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "linear-gradient(135deg, #F6255A 0%, #e01e4a 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <BarChart3 style={{ width: "16px", height: "16px", color: "#ffffff" }} />
                            </div>
                            <h6
                              style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                margin: 0,
                                color: "#ffffff",
                                letterSpacing: "-0.5px",
                              }}
                            >
                              Success Metrics
                            </h6>
                          </div>
                          <div className="pdf-no-break" style={{ background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                              borderRadius: "12px",
                              padding: "20px",
                              marginBottom: "20px",
                              border: "1px solid rgba(55, 65, 81, 0.6)",
                            }}
                          >
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <Target style={{ width: "16px", height: "16px", color: "#F6255A" }} />
                              <p
                                style={{
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  color: "#9ca3af",
                                  marginBottom: 0,
                                }}
                              >
                                North Star Metric
                              </p>
                            </div>
                            <p
                              style={{
                                fontSize: "16px",
                                fontWeight: "700",
                                color: "#ffffff",
                                margin: "8px 0 0 0",
                              }}
                            >
                              {marketingStrategyData.success_metrics_dashboard.north_star_metric}
                            </p>
                          </div>
                          {marketingStrategyData.success_metrics_dashboard.primary_kpis?.length > 0 && (
                            <div>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <ThumbsUp style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <p
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#ffffff",
                                    marginBottom: 0,
                                  }}
                                >
                                  Primary KPIs
                                </p>
                              </div>
                              {marketingStrategyData.success_metrics_dashboard.primary_kpis.map((kpi, idx) => (
                                <div className="pdf-no-break" key={idx}
                                  style={{
                                    padding: "14px",
                                    background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                    borderRadius: "10px",
                                    marginBottom: "10px",
                                    border: "1px solid rgba(55, 65, 81, 0.6)",
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    <span style={{ color: "#ffffff", fontWeight: "700", fontSize: "14px" }}>{kpi.kpi}</span>
                                    <span style={{ 
                                      color: "#10b981", 
                                      fontWeight: "700",
                                      padding: "4px 8px",
                                      borderRadius: "6px",
                                      backgroundColor: "rgba(16, 185, 129, 0.2)",
                                      fontSize: "12px"
                                    }}>{kpi.current_value}</span>
                                  </div>
                                  <div className="d-flex gap-6" style={{ fontSize: "13px", color: "#9ca3af", marginTop: "8px" }}>
                                    <div className="d-flex align-items-center gap-2">
                                      <Clock style={{ width: "12px", height: "12px" }} />
                                      <span>30d: {kpi["30_day_target"]}</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <TrendingUp style={{ width: "12px", height: "12px" }} />
                                      <span>90d: {kpi["90_day_target"]}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Monetization Context */}
                      {marketingStrategyData?.monetization_context && (
                        <div style={{ marginBottom: "32px" }}>
                          <div className="d-flex align-items-center gap-2 mb-4">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "linear-gradient(135deg, #F6255A 0%, #e01e4a 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <DollarSign style={{ width: "16px", height: "16px", color: "#ffffff" }} />
                            </div>
                            <h6
                              style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                margin: 0,
                                color: "#ffffff",
                                letterSpacing: "-0.5px",
                              }}
                            >
                              Monetization
                            </h6>
                          </div>
                          {marketingStrategyData.monetization_context.addon_items?.length > 0 && (
                            <div>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Wallet style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <p
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#ffffff",
                                    marginBottom: 0,
                                  }}
                                >
                                  Add-ons ({marketingStrategyData.monetization_context.addon_count})
                                </p>
                              </div>
                              {marketingStrategyData.monetization_context.addon_items.map((addon, idx) => (
                                <div className="pdf-no-break" key={idx}
                                  style={{
                                    padding: "14px",
                                    background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                    borderRadius: "10px",
                                    marginBottom: "10px",
                                    border: "1px solid rgba(55, 65, 81, 0.6)",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <span style={{ color: "#ffffff", fontWeight: "700", fontSize: "15px" }}>{addon.name}</span>
                                  <span style={{ 
                                    color: "#10b981", 
                                    fontWeight: "700",
                                    padding: "6px 12px",
                                    borderRadius: "8px",
                                    backgroundColor: "rgba(16, 185, 129, 0.2)",
                                    fontSize: "14px"
                                  }}>
                                    ${addon.price_usd}
                                  </span>
                                </div>
                              ))}
                              <div
                                style={{
                                  padding: "14px",
                                  background: "linear-gradient(135deg, rgba(246, 37, 90, 0.1) 0%, rgba(246, 37, 90, 0.05) 100%)",
                                  border: "1px solid rgba(246, 37, 90, 0.3)",
                                  borderRadius: "10px",
                                  marginTop: "12px",
                                }}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <TrendingUp style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                  <span style={{ color: "#9ca3af", fontSize: "13px", fontWeight: "500" }}>Max Spend: </span>
                                  <span style={{ color: "#F6255A", fontWeight: "700", fontSize: "15px" }}>
                                    ${marketingStrategyData.monetization_context.max_spend_usd}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Data Sources Used */}
                      {/* {marketingStrategyData?.data_sources_used?.length > 0 && (
                        <div style={{ marginBottom: "24px" }}>
                          <h6
                            style={{
                              fontSize: "16px",
                              fontWeight: "600",
                              marginBottom: "16px",
                              color: "#F6255A",
                            }}
                          >
                            Data Sources Used
                          </h6>
                          <div className="pdf-no-break" style={{ backgroundColor: "rgba(55, 65, 81, 0.5)",
                              borderRadius: "8px",
                              padding: "16px",
                            }}
                          >
                            {marketingStrategyData.data_sources_used.map((source, idx) => (
                              <div className="pdf-no-break" key={idx}
                                style={{
                                  padding: "12px",
                                  backgroundColor: "rgba(55, 65, 81, 0.3)",
                                  borderRadius: "6px",
                                  marginBottom: idx < marketingStrategyData.data_sources_used.length - 1 ? "8px" : "0",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    marginBottom: "8px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: "600",
                                      color: "#ffffff",
                                    }}
                                  >
                                    {source.tool}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      padding: "2px 8px",
                                      borderRadius: "4px",
                                      backgroundColor:
                                        source.status === "Success"
                                          ? "rgba(16, 185, 129, 0.2)"
                                          : "rgba(239, 68, 68, 0.2)",
                                      color:
                                        source.status === "Success"
                                          ? "#10b981"
                                          : "#ef4444",
                                      fontWeight: "600",
                                    }}
                                  >
                                    {source.status}
                                  </span>
                                </div>
                                <p
                                  style={{
                                    fontSize: "12px",
                                    color: "#9ca3af",
                                    marginBottom: "0",
                                    margin: 0,
                                  }}
                                >
                                  Data Retrieved: {source.data_retrieved}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )} */}

                      {/* Enterprise Intelligence Report */}
                      {marketingStrategyData?.enterprise_intelligence_report && (
                        <div style={{ marginBottom: "32px" }}>
                          <div className="d-flex align-items-center gap-2 mb-4">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "6px",
                                background: "linear-gradient(135deg, #F6255A 0%, #e01e4a 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Building2 style={{ width: "16px", height: "16px", color: "#ffffff" }} />
                            </div>
                            <h6
                              style={{
                                fontSize: "18px",
                                fontWeight: "700",
                                margin: 0,
                                color: "#ffffff",
                                letterSpacing: "-0.5px",
                              }}
                            >
                              Enterprise Intelligence Report
                            </h6>
                          </div>
                          <div className="pdf-no-break" style={{ background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                              borderRadius: "12px",
                              padding: "20px",
                              marginBottom: "20px",
                              border: "1px solid rgba(55, 65, 81, 0.6)",
                            }}
                          >
                            <div
                              className="ms-responsive-grid"
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                                gap: "16px",
                                fontSize: "13px",
                              }}
                            >
                              <div className="d-flex align-items-center gap-2 ms-meta-pair">
                                <FileText style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                <span style={{ color: "#9ca3af" }}>Document Type: </span>
                                <span style={{ color: "#ffffff", fontWeight: "600" }}>
                                  {marketingStrategyData.enterprise_intelligence_report.document_type}
                                </span>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <Bot style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                <span style={{ color: "#9ca3af" }}>Generated By: </span>
                                <span style={{ color: "#ffffff", fontWeight: "600" }}>
                                  {marketingStrategyData.enterprise_intelligence_report.generated_by}
                                </span>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <Layers style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                <span style={{ color: "#9ca3af" }}>Version: </span>
                                <span style={{ color: "#ffffff", fontWeight: "600" }}>
                                  {marketingStrategyData.enterprise_intelligence_report.version}
                                </span>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <Clock style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                <span style={{ color: "#9ca3af" }}>Generated At: </span>
                                <span style={{ color: "#ffffff", fontWeight: "600" }}>
                                  {marketingStrategyData.enterprise_intelligence_report.generated_at}
                                </span>
                              </div>
                            </div>
                          </div>
                          {marketingStrategyData.enterprise_intelligence_report.product_context && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Gamepad2 style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <h6
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    marginBottom: 0,
                                    color: "#ffffff",
                                  }}
                                >
                                  Product Context
                                </h6>
                              </div>
                              <div className="pdf-no-break" style={{ background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                  borderRadius: "12px",
                                  padding: "18px",
                                  border: "1px solid rgba(55, 65, 81, 0.6)",
                                }}
                              >
                                <div style={{ marginBottom: "12px", display: "flex", gap: "16px" }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <Star style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                    <span style={{ color: "#9ca3af" }}>Product Name: </span>
                                    <span style={{ color: "#ffffff", fontWeight: "600" }}>
                                      {marketingStrategyData.enterprise_intelligence_report.product_context.product_name}
                                    </span>
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    <Target style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                    <span style={{ color: "#9ca3af" }}>Category: </span>
                                    <span style={{ color: "#ffffff", fontWeight: "600" }}>
                                      {marketingStrategyData.enterprise_intelligence_report.product_context.category}
                                    </span>
                                  </div>
                                </div>
                                <div style={{ marginBottom: "12px", display: "flex", gap: "16px" }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <Globe style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                    <span style={{ color: "#9ca3af" }}>Platform: </span>
                                    <span style={{ color: "#ffffff", fontWeight: "600" }}>
                                      {marketingStrategyData.enterprise_intelligence_report.product_context.platform}
                                    </span>
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    <MessageSquare style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                    <span style={{ color: "#9ca3af" }}>Summary: </span>
                                    <span style={{ color: "#ffffff", fontWeight: "600" }}>
                                      {marketingStrategyData.enterprise_intelligence_report.product_context.summary}
                                    </span>
                                  </div>
                                </div>
                                {marketingStrategyData.enterprise_intelligence_report.product_context.capabilities_analyzed && (
                                  <div style={{ marginTop: "12px" }}>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <CheckCircle2 style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                      <span style={{ color: "#9ca3af", fontWeight: "600", fontSize: "13px" }}>Capabilities Analyzed: </span>
                                    </div>
                                    <div style={{ marginTop: "8px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                                      {marketingStrategyData.enterprise_intelligence_report.product_context.capabilities_analyzed.map((capability, idx) => (
                                        <span
                                          key={idx}
                                          style={{
                                            fontSize: "12px",
                                            backgroundColor: "rgba(246, 37, 90, 0.2)",
                                            color: "#F6255A",
                                            padding: "6px 12px",
                                            borderRadius: "8px",
                                            fontWeight: "600",
                                            border: "1px solid rgba(246, 37, 90, 0.3)",
                                          }}
                                        >
                                          {capability}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {marketingStrategyData.enterprise_intelligence_report.competitor_intelligence?.length > 0 && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Users style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <h6
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    marginBottom: 0,
                                    color: "#ffffff",
                                  }}
                                >
                                  Competitor Intelligence
                                </h6>
                              </div>
                              <div className="pdf-no-break" style={{ background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                  borderRadius: "12px",
                                  padding: "18px",
                                  border: "1px solid rgba(55, 65, 81, 0.6)",
                                }}
                              >
                                {marketingStrategyData.enterprise_intelligence_report.competitor_intelligence.map((competitor, idx) => (
                                  <div className="pdf-no-break" key={idx}
                                    style={{
                                      padding: "14px",
                                      background: "linear-gradient(135deg, rgba(55, 65, 81, 0.4) 0%, rgba(55, 65, 81, 0.2) 100%)",
                                      borderRadius: "10px",
                                      marginBottom: idx < marketingStrategyData.enterprise_intelligence_report.competitor_intelligence.length - 1 ? "10px" : "0",
                                      border: "1px solid rgba(55, 65, 81, 0.5)",
                                      transition: "all 0.2s ease",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: "10px",
                                      }}
                                    >
                                      <div className="d-flex align-items-center gap-2" style={{ flex: 1, minWidth: 0 }}>
                                        <Building2 style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                        <span
                                          style={{
                                            fontSize: "14px",
                                            fontWeight: "700",
                                            color: "#ffffff",
                                          }}
                                        >
                                          {competitor.name}
                                        </span>
                                      </div>
                                      {competitor.website && competitor.website !== "N/A" && (
                                        <span
                                          style={{
                                            fontSize: "11px",
                                            color: "#9ca3af",
                                            fontWeight: "500",
                                            flexShrink: 0,
                                          }}
                                        >
                                          {competitor.website}
                                        </span>
                                      )}
                                    </div>
                                    {competitor.key_seo_keywords?.length > 0 && (
                                      <div style={{ marginBottom: "10px" }}>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                          <Search style={{ width: "12px", height: "12px", color: "#9ca3af" }} />
                                          <span style={{ color: "#9ca3af", fontSize: "12px", fontWeight: "600" }}>SEO Keywords: </span>
                                        </div>
                                        <div style={{ marginTop: "6px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                          {competitor.key_seo_keywords.map((keyword, kIdx) => (
                                            <span
                                              key={kIdx}
                                              style={{
                                                fontSize: "11px",
                                                backgroundColor: "rgba(16, 185, 129, 0.2)",
                                                color: "#10b981",
                                                padding: "4px 8px",
                                                borderRadius: "6px",
                                                fontWeight: "600",
                                                border: "1px solid rgba(16, 185, 129, 0.3)",
                                              }}
                                            >
                                              {keyword}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {competitor.ai_insights?.length > 0 && (
                                      <div>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                          <Bot style={{ width: "12px", height: "12px", color: "#F6255A" }} />
                                          <span style={{ color: "#9ca3af", fontSize: "12px", fontWeight: "600" }}>AI Insights: </span>
                                        </div>
                                        {competitor.ai_insights.map((insight, iIdx) => (
                                          <div className="pdf-no-break" key={iIdx}
                                            style={{
                                              marginTop: "6px",
                                              fontSize: "12px",
                                              color: "#ffffff",
                                              paddingLeft: "12px",
                                              borderLeft: "2px solid #F6255A",
                                              backgroundColor: "rgba(246, 37, 90, 0.05)",
                                              padding: "10px",
                                              borderRadius: "4px",
                                            }}
                                          >
                                            {insight}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {marketingStrategyData.enterprise_intelligence_report.media_press_analysis && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Newspaper style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <h6
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    marginBottom: 0,
                                    color: "#ffffff",
                                  }}
                                >
                                  Media & Press Analysis
                                </h6>
                              </div>
                              <div className="pdf-no-break" style={{ background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                  borderRadius: "12px",
                                  padding: "18px",
                                  border: "1px solid rgba(55, 65, 81, 0.6)",
                                  fontSize: "13px",
                                }}
                              >
                                <div style={{ marginBottom: "12px", display: "flex", gap: "16px" }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <MessageSquare style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                    <span style={{ color: "#9ca3af" }}>Market Narrative: </span>
                                    <span style={{ color: "#ffffff", fontWeight: "600" }}>
                                      {marketingStrategyData.enterprise_intelligence_report.media_press_analysis.market_narrative}
                                    </span>
                                  </div>
                                </div>
                                {marketingStrategyData.enterprise_intelligence_report.media_press_analysis.key_channels?.length > 0 && (
                                  <div style={{ marginBottom: "12px" }}>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <Rss style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                      <span style={{ color: "#9ca3af", fontWeight: "600" }}>Key Channels: </span>
                                    </div>
                                    <div style={{ marginTop: "8px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                                      {marketingStrategyData.enterprise_intelligence_report.media_press_analysis.key_channels.map((channel, idx) => (
                                        <span
                                          key={idx}
                                          style={{
                                            fontSize: "12px",
                                            backgroundColor: "rgba(246, 37, 90, 0.2)",
                                            color: "#F6255A",
                                            padding: "6px 12px",
                                            borderRadius: "8px",
                                            fontWeight: "600",
                                            border: "1px solid rgba(246, 37, 90, 0.3)",
                                          }}
                                        >
                                          {channel}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {marketingStrategyData.enterprise_intelligence_report.media_press_analysis.narrative_themes?.length > 0 && (
                                  <div>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <Tag style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                      <span style={{ color: "#9ca3af", fontWeight: "600" }}>Narrative Themes: </span>
                                    </div>
                                    <div style={{ marginTop: "8px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                                      {marketingStrategyData.enterprise_intelligence_report.media_press_analysis.narrative_themes.map((theme, idx) => (
                                        <span
                                          key={idx}
                                          style={{
                                            fontSize: "12px",
                                            backgroundColor: "rgba(55, 65, 81, 0.5)",
                                            color: "#9ca3af",
                                            padding: "6px 12px",
                                            borderRadius: "8px",
                                            fontWeight: "600",
                                            border: "1px solid rgba(55, 65, 81, 0.6)",
                                          }}
                                        >
                                          {theme}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {marketingStrategyData.enterprise_intelligence_report.seo_intelligence && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Search style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <h6
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    marginBottom: 0,
                                    color: "#ffffff",
                                  }}
                                >
                                  SEO Intelligence
                                </h6>
                              </div>
                              <div className="pdf-no-break" style={{ background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                  borderRadius: "12px",
                                  padding: "18px",
                                  border: "1px solid rgba(55, 65, 81, 0.6)",
                                  fontSize: "13px",
                                }}
                              >
                                {marketingStrategyData.enterprise_intelligence_report.seo_intelligence.high_opportunity_keywords?.length > 0 && (
                                  <div style={{ marginBottom: "12px" }}>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <TrendingUp style={{ width: "14px", height: "14px", color: "#10b981" }} />
                                      <span style={{ color: "#9ca3af", fontWeight: "600" }}>High Opportunity Keywords: </span>
                                    </div>
                                    <div style={{ marginTop: "8px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                                      {marketingStrategyData.enterprise_intelligence_report.seo_intelligence.high_opportunity_keywords.map((keyword, idx) => (
                                        <span
                                          key={idx}
                                          style={{
                                            fontSize: "12px",
                                            backgroundColor: "rgba(16, 185, 129, 0.2)",
                                            color: "#10b981",
                                            padding: "6px 12px",
                                            borderRadius: "8px",
                                            fontWeight: "600",
                                            border: "1px solid rgba(16, 185, 129, 0.3)",
                                          }}
                                        >
                                          {keyword}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {marketingStrategyData.enterprise_intelligence_report.seo_intelligence.ai_detected?.length > 0 && (
                                  <div style={{ marginBottom: "12px" }}>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <Bot style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                      <span style={{ color: "#9ca3af", fontWeight: "600" }}>AI Detected: </span>
                                    </div>
                                    {marketingStrategyData.enterprise_intelligence_report.seo_intelligence.ai_detected.map((detection, idx) => (
                                      <div className="pdf-no-break" key={idx}
                                        style={{
                                          marginTop: "6px",
                                          fontSize: "12px",
                                          color: "#ffffff",
                                          paddingLeft: "12px",
                                          borderLeft: "2px solid #F6255A",
                                          backgroundColor: "rgba(246, 37, 90, 0.05)",
                                          padding: "10px",
                                          borderRadius: "4px",
                                        }}
                                      >
                                        {detection}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {marketingStrategyData.enterprise_intelligence_report.seo_intelligence.keyword_table?.length > 0 && (
                                  <div>
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                      <Table style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                      <p style={{ color: "#ffffff", fontWeight: "600", marginBottom: 0, fontSize: "13px" }}>
                                        Keyword Table
                                      </p>
                                    </div>
                                    {marketingStrategyData.enterprise_intelligence_report.seo_intelligence.keyword_table.map((keywordData, idx) => (
                                      <div className="pdf-no-break" key={idx}
                                        style={{
                                          padding: "14px",
                                          background: "linear-gradient(135deg, rgba(55, 65, 81, 0.4) 0%, rgba(55, 65, 81, 0.2) 100%)",
                                          borderRadius: "10px",
                                          marginBottom: idx < marketingStrategyData.enterprise_intelligence_report.seo_intelligence.keyword_table.length - 1 ? "10px" : "0",
                                          border: "1px solid rgba(55, 65, 81, 0.5)",
                                          transition: "all 0.2s ease",
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: "10px",
                                          }}
                                        >
                                          <div className="d-flex align-items-center gap-2" style={{ flex: 1, minWidth: 0, marginRight: "8px" }}>
                                            <Search style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                            <span
                                              style={{
                                                fontSize: "14px",
                                                fontWeight: "700",
                                                color: "#ffffff",
                                              }}
                                            >
                                              {keywordData.keyword}
                                            </span>
                                          </div>
                                          <span
                                            style={{
                                              fontSize: "12px",
                                              color: "#10b981",
                                              fontWeight: "600",
                                              padding: "4px 8px",
                                              borderRadius: "6px",
                                              backgroundColor: "rgba(16, 185, 129, 0.2)",
                                              border: "1px solid rgba(16, 185, 129, 0.3)",
                                              flexShrink: 0,
                                            }}
                                          >
                                            {formatNumber(keywordData.estimated_downloads)} downloads
                                          </span>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                          <Target style={{ width: "12px", height: "12px", color: "#F6255A" }} />
                                          <p
                                            style={{
                                              fontSize: "13px",
                                              color: "#9ca3af",
                                              marginBottom: "0",
                                              margin: 0,
                                              fontWeight: "500",
                                            }}
                                          >
                                            {keywordData.action}
                                          </p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {marketingStrategyData.enterprise_intelligence_report.positioning_matrix && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Target style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <h6
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    marginBottom: 0,
                                    color: "#ffffff",
                                  }}
                                >
                                  Positioning Matrix
                                </h6>
                              </div>
                              <div className="pdf-no-break" style={{ background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                  borderRadius: "12px",
                                  padding: "18px",
                                  border: "1px solid rgba(55, 65, 81, 0.6)",
                                  fontSize: "13px",
                                }}
                              >
                                <div style={{ marginBottom: "12px", display: "flex", gap: "16px", flexWrap: "wrap" }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <FileText style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                    <span style={{ color: "#9ca3af" }}>Positioning Statement: </span>
                                    <span style={{ color: "#ffffff", fontWeight: "600" }}>
                                      {marketingStrategyData.enterprise_intelligence_report.positioning_matrix.positioning_statement}
                                    </span>
                                  </div>
                                </div>
                                {marketingStrategyData.enterprise_intelligence_report.positioning_matrix.key_advantages?.length > 0 && (
                                  <div>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <Star style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                      <span style={{ color: "#9ca3af", fontWeight: "600" }}>Key Advantages: </span>
                                    </div>
                                    <div style={{ marginTop: "8px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                                      {marketingStrategyData.enterprise_intelligence_report.positioning_matrix.key_advantages.map((advantage, idx) => (
                                        <span
                                          key={idx}
                                          style={{
                                            fontSize: "12px",
                                            backgroundColor: "rgba(16, 185, 129, 0.2)",
                                            color: "#10b981",
                                            padding: "6px 12px",
                                            borderRadius: "8px",
                                            fontWeight: "600",
                                            border: "1px solid rgba(16, 185, 129, 0.3)",
                                          }}
                                        >
                                          {advantage}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {marketingStrategyData.enterprise_intelligence_report.marketing_recommendations && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Lightbulb style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <h6
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    marginBottom: 0,
                                    color: "#ffffff",
                                  }}
                                >
                                  Marketing Recommendations
                                </h6>
                              </div>
                              <div className="pdf-no-break" style={{ background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                  borderRadius: "12px",
                                  padding: "18px",
                                  border: "1px solid rgba(55, 65, 81, 0.6)",
                                  fontSize: "13px",
                                }}
                              >
                                {marketingStrategyData.enterprise_intelligence_report.marketing_recommendations.phase_1_authority && (
                                  <div style={{ marginBottom: "16px", paddingBottom: "16px", borderBottom: "1px solid rgba(55, 65, 81, 0.6)" }}>
                                    <p style={{ color: "#F6255A", fontWeight: "700", marginBottom: "10px", margin: 0, fontSize: "14px" }}>
                                      Phase 1: Authority
                                    </p>
                                    {marketingStrategyData.enterprise_intelligence_report.marketing_recommendations.phase_1_authority.target_segments?.length > 0 && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                          <Users style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                          <span style={{ color: "#9ca3af", fontWeight: "600" }}>Target Segments: </span>
                                        </div>
                                        <div style={{ marginTop: "8px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                                          {marketingStrategyData.enterprise_intelligence_report.marketing_recommendations.phase_1_authority.target_segments.map((segment, idx) => (
                                            <span
                                              key={idx}
                                              style={{
                                                fontSize: "12px",
                                                backgroundColor: "rgba(246, 37, 90, 0.2)",
                                                color: "#F6255A",
                                                padding: "6px 12px",
                                                borderRadius: "8px",
                                                fontWeight: "600",
                                                border: "1px solid rgba(246, 37, 90, 0.3)",
                                              }}
                                            >
                                              {segment}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {marketingStrategyData.enterprise_intelligence_report.marketing_recommendations.phase_1_authority.primary_channels?.length > 0 && (
                                      <div>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                          <Rss style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                          <span style={{ color: "#9ca3af", fontWeight: "600" }}>Primary Channels: </span>
                                        </div>
                                        <div style={{ marginTop: "8px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                                          {marketingStrategyData.enterprise_intelligence_report.marketing_recommendations.phase_1_authority.primary_channels.map((channel, idx) => (
                                            <span
                                              key={idx}
                                              style={{
                                                fontSize: "12px",
                                                backgroundColor: "rgba(55, 65, 81, 0.5)",
                                                color: "#ffffff",
                                                padding: "6px 12px",
                                                borderRadius: "8px",
                                                fontWeight: "600",
                                                border: "1px solid rgba(55, 65, 81, 0.6)",
                                              }}
                                            >
                                              {channel}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {marketingStrategyData.enterprise_intelligence_report.marketing_recommendations.phase_2_acquisition && (
                                  <div>
                                    <p style={{ color: "#F6255A", fontWeight: "700", marginBottom: "10px", margin: 0, fontSize: "14px" }}>
                                      Phase 2: Acquisition
                                    </p>
                                    {marketingStrategyData.enterprise_intelligence_report.marketing_recommendations.phase_2_acquisition.tactics?.length > 0 && (
                                      <div>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                          <Target style={{ width: "14px", height: "14px", color: "#10b981" }} />
                                          <span style={{ color: "#9ca3af", fontWeight: "600" }}>Tactics: </span>
                                        </div>
                                        {marketingStrategyData.enterprise_intelligence_report.marketing_recommendations.phase_2_acquisition.tactics.map((tactic, idx) => (
                                          <div className="pdf-no-break" key={idx}
                                            style={{
                                              marginTop: "6px",
                                              fontSize: "12px",
                                              color: "#ffffff",
                                              paddingLeft: "12px",
                                              borderLeft: "2px solid #10b981",
                                              backgroundColor: "rgba(16, 185, 129, 0.05)",
                                              padding: "10px",
                                              borderRadius: "4px",
                                            }}
                                          >
                                            {tactic}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {marketingStrategyData.enterprise_intelligence_report.ai_campaign_suggestions?.length > 0 && (
                            <div style={{ marginBottom: "16px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Bot style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <h6
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    marginBottom: 0,
                                    color: "#ffffff",
                                  }}
                                >
                                  AI Campaign Suggestions
                                </h6>
                              </div>
                              <div className="pdf-no-break" style={{ background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                  borderRadius: "12px",
                                  padding: "18px",
                                  border: "1px solid rgba(55, 65, 81, 0.6)",
                                }}
                              >
                                {marketingStrategyData.enterprise_intelligence_report.ai_campaign_suggestions.map((suggestion, idx) => (
                                  <div className="pdf-no-break" key={idx}
                                    style={{
                                      padding: "14px",
                                      background: "linear-gradient(135deg, rgba(55, 65, 81, 0.4) 0%, rgba(55, 65, 81, 0.2) 100%)",
                                      borderRadius: "10px",
                                      marginBottom: idx < marketingStrategyData.enterprise_intelligence_report.ai_campaign_suggestions.length - 1 ? "10px" : "0",
                                      border: "1px solid rgba(55, 65, 81, 0.5)",
                                      transition: "all 0.2s ease",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: "10px",
                                      }}
                                    >
                                      <div className="d-flex align-items-center gap-2">
                                        <Target style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                        <span
                                          style={{
                                            fontSize: "15px",
                                            fontWeight: "700",
                                            color: "#ffffff",
                                          }}
                                        >
                                          {suggestion.name}
                                        </span>
                                      </div>
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          padding: "6px 12px",
                                          borderRadius: "8px",
                                          backgroundColor:
                                            suggestion.priority === "High"
                                              ? "rgba(239, 68, 68, 0.2)"
                                              : "rgba(245, 158, 11, 0.2)",
                                          color: suggestion.priority === "High" ? "#ef4444" : "#f59e0b",
                                          fontWeight: "700",
                                          border:
                                            suggestion.priority === "High"
                                              ? "1px solid rgba(239, 68, 68, 0.3)"
                                              : "1px solid rgba(245, 158, 11, 0.3)",
                                        }}
                                      >
                                        {suggestion.priority}
                                      </span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <Flag style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                      <p
                                        style={{
                                          fontSize: "13px",
                                          color: "#9ca3af",
                                          marginBottom: "0",
                                          margin: 0,
                                          fontWeight: "500",
                                        }}
                                      >
                                        {suggestion.goal}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {marketingStrategyData.enterprise_intelligence_report.sentiment_analysis && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Heart style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <h6
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    marginBottom: 0,
                                    color: "#ffffff",
                                  }}
                                >
                                  Sentiment Analysis
                                </h6>
                              </div>
                              <div className="pdf-no-break" style={{ background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                  borderRadius: "12px",
                                  padding: "18px",
                                  border: "1px solid rgba(55, 65, 81, 0.6)",
                                  fontSize: "13px",
                                }}
                              >
                                {marketingStrategyData.enterprise_intelligence_report.sentiment_analysis.positive_trends?.length > 0 && (
                                  <div style={{ marginBottom: "16px" }}>
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                      <TrendingUp style={{ width: "14px", height: "14px", color: "#10b981" }} />
                                      <p style={{ color: "#10b981", fontWeight: "700", marginBottom: 0, fontSize: "14px" }}>
                                        Positive Trends
                                      </p>
                                    </div>
                                    {marketingStrategyData.enterprise_intelligence_report.sentiment_analysis.positive_trends.map((trend, idx) => (
                                      <div className="pdf-no-break" key={idx}
                                        style={{
                                          marginTop: "6px",
                                          fontSize: "12px",
                                          color: "#ffffff",
                                          paddingLeft: "12px",
                                          borderLeft: "2px solid #10b981",
                                          backgroundColor: "rgba(16, 185, 129, 0.05)",
                                          padding: "10px",
                                          borderRadius: "4px",
                                        }}
                                      >
                                        {trend}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {marketingStrategyData.enterprise_intelligence_report.sentiment_analysis.negative_trends?.length > 0 && (
                                  <div>
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                      <TrendingDown style={{ width: "14px", height: "14px", color: "#ef4444" }} />
                                      <p style={{ color: "#ef4444", fontWeight: "700", marginBottom: 0, fontSize: "14px" }}>
                                        Negative Trends
                                      </p>
                                    </div>
                                    {marketingStrategyData.enterprise_intelligence_report.sentiment_analysis.negative_trends.map((trend, idx) => (
                                      <div className="pdf-no-break" key={idx}
                                        style={{
                                          marginTop: "6px",
                                          fontSize: "12px",
                                          color: "#ffffff",
                                          paddingLeft: "12px",
                                          borderLeft: "2px solid #ef4444",
                                          backgroundColor: "rgba(239, 68, 68, 0.05)",
                                          padding: "10px",
                                          borderRadius: "4px",
                                        }}
                                      >
                                        {trend}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {marketingStrategyData.enterprise_intelligence_report.ai_action_feed && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Zap style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <h6
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    marginBottom: 0,
                                    color: "#ffffff",
                                  }}
                                >
                                  AI Action Feed
                                </h6>
                              </div>
                              <div className="pdf-no-break" style={{ background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                  borderRadius: "12px",
                                  padding: "18px",
                                  border: "1px solid rgba(55, 65, 81, 0.6)",
                                  fontSize: "13px",
                                }}
                              >
                                {marketingStrategyData.enterprise_intelligence_report.ai_action_feed.high_priority?.length > 0 && (
                                  <div style={{ marginBottom: "16px" }}>
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                      <AlertTriangle style={{ width: "14px", height: "14px", color: "#ef4444" }} />
                                      <p style={{ color: "#ef4444", fontWeight: "700", marginBottom: 0, fontSize: "14px" }}>
                                        High Priority
                                      </p>
                                    </div>
                                    {marketingStrategyData.enterprise_intelligence_report.ai_action_feed.high_priority.map((action, idx) => (
                                      <div className="pdf-no-break" key={idx}
                                        style={{
                                          marginTop: "6px",
                                          fontSize: "12px",
                                          color: "#ffffff",
                                          paddingLeft: "12px",
                                          borderLeft: "2px solid #ef4444",
                                          backgroundColor: "rgba(239, 68, 68, 0.05)",
                                          padding: "10px",
                                          borderRadius: "4px",
                                        }}
                                      >
                                        {action}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {marketingStrategyData.enterprise_intelligence_report.ai_action_feed.medium_priority?.length > 0 && (
                                  <div>
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                      <AlertCircle style={{ width: "14px", height: "14px", color: "#f59e0b" }} />
                                      <p style={{ color: "#f59e0b", fontWeight: "700", marginBottom: 0, fontSize: "14px" }}>
                                        Medium Priority
                                      </p>
                                    </div>
                                    {marketingStrategyData.enterprise_intelligence_report.ai_action_feed.medium_priority.map((action, idx) => (
                                      <div className="pdf-no-break" key={idx}
                                        style={{
                                          marginTop: "6px",
                                          fontSize: "12px",
                                          color: "#ffffff",
                                          paddingLeft: "12px",
                                          borderLeft: "2px solid #f59e0b",
                                          backgroundColor: "rgba(245, 158, 11, 0.05)",
                                          padding: "10px",
                                          borderRadius: "4px",
                                        }}
                                      >
                                        {action}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {marketingStrategyData.enterprise_intelligence_report.ai_signals_used?.length > 0 && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Sparkles style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <h6
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    marginBottom: 0,
                                    color: "#ffffff",
                                  }}
                                >
                                  AI Signals Used
                                </h6>
                              </div>
                              <div className="pdf-no-break" style={{ background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                  borderRadius: "12px",
                                  padding: "18px",
                                  border: "1px solid rgba(55, 65, 81, 0.6)",
                                }}
                              >
                                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                                  {marketingStrategyData.enterprise_intelligence_report.ai_signals_used.map((signal, idx) => (
                                    <span
                                      key={idx}
                                      style={{
                                        fontSize: "12px",
                                        backgroundColor: "rgba(246, 37, 90, 0.2)",
                                        color: "#F6255A",
                                        padding: "6px 12px",
                                        borderRadius: "8px",
                                        fontWeight: "600",
                                        border: "1px solid rgba(246, 37, 90, 0.3)",
                                      }}
                                    >
                                      {signal}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}

                          {marketingStrategyData.enterprise_intelligence_report.source_reference_matrix?.length > 0 && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Database style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <h6
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    marginBottom: 0,
                                    color: "#ffffff",
                                  }}
                                >
                                  Source Reference Matrix
                                </h6>
                              </div>
                              <div className="pdf-no-break" style={{ background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                  borderRadius: "12px",
                                  padding: "18px",
                                  border: "1px solid rgba(55, 65, 81, 0.6)",
                                  fontSize: "13px",
                                }}
                              >
                                {marketingStrategyData.enterprise_intelligence_report.source_reference_matrix.map((source, idx) => (
                                  <div className="pdf-no-break" key={idx}
                                    style={{
                                      padding: "14px",
                                      background: "linear-gradient(135deg, rgba(55, 65, 81, 0.4) 0%, rgba(55, 65, 81, 0.2) 100%)",
                                      borderRadius: "10px",
                                      marginBottom: idx < marketingStrategyData.enterprise_intelligence_report.source_reference_matrix.length - 1 ? "10px" : "0",
                                      border: "1px solid rgba(55, 65, 81, 0.5)",
                                      transition: "all 0.2s ease",
                                    }}
                                  >
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                      <FileText style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                      <span style={{ color: "#F6255A", fontWeight: "700", fontSize: "15px" }}>
                                        {source.source_type}
                                      </span>
                                    </div>
                                    <div style={{ color: "#9ca3af", fontWeight: "500" }}>
                                      {source.examples}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {marketingStrategyData.enterprise_intelligence_report.final_ai_evaluation && (
                            <div style={{ marginBottom: "20px" }}>
                              <div className="d-flex align-items-center gap-2 mb-3">
                                <Brain style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                <h6
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    marginBottom: 0,
                                    color: "#ffffff",
                                  }}
                                >
                                  Final AI Evaluation
                                </h6>
                              </div>
                              <div className="pdf-no-break" style={{ background: "linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(55, 65, 81, 0.3) 100%)",
                                  borderRadius: "12px",
                                  padding: "18px",
                                  border: "1px solid rgba(55, 65, 81, 0.6)",
                                  fontSize: "13px",
                                }}
                              >
                                <div style={{ marginBottom: "12px", display: "flex", gap: "16px" }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <TrendingUp style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                    <span style={{ color: "#9ca3af" }}>Enterprise Potential: </span>
                                    <span style={{ color: "#ffffff", fontWeight: "600" }}>
                                      {marketingStrategyData.enterprise_intelligence_report.final_ai_evaluation.enterprise_potential}
                                    </span>
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    <Target style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                    <span style={{ color: "#9ca3af" }}>Market Opportunity: </span>
                                    <span style={{ color: "#ffffff", fontWeight: "600" }}>
                                      {marketingStrategyData.enterprise_intelligence_report.final_ai_evaluation.market_opportunity}
                                    </span>
                                  </div>
                                </div>
                                <div style={{ marginBottom: "12px", display: "flex", gap: "16px" }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <Users style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                    <span style={{ color: "#9ca3af" }}>Competition Density: </span>
                                    <span style={{ color: "#ffffff", fontWeight: "600" }}>
                                      {marketingStrategyData.enterprise_intelligence_report.final_ai_evaluation.competition_density}
                                    </span>
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    <Search style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                    <span style={{ color: "#9ca3af" }}>SEO Opportunity: </span>
                                    <span style={{ color: "#ffffff", fontWeight: "600" }}>
                                      {marketingStrategyData.enterprise_intelligence_report.final_ai_evaluation.seo_opportunity}
                                    </span>
                                  </div>
                                </div>
                                <div style={{ marginBottom: "12px", display: "flex", gap: "16px" }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <Layers style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                    <span style={{ color: "#9ca3af" }}>Branding Scalability: </span>
                                    <span style={{ color: "#ffffff", fontWeight: "600" }}>
                                      {marketingStrategyData.enterprise_intelligence_report.final_ai_evaluation.branding_scalability}
                                    </span>
                                  </div>
                                </div>
                                {marketingStrategyData.enterprise_intelligence_report.final_ai_evaluation.investor_narrative && (
                                  <div style={{ marginBottom: "12px" }}>
                                    <div className="d-flex align-items-center gap-2">
                                      <MessageSquare style={{ width: "14px", height: "14px", color: "#9ca3af" }} />
                                      <span style={{ color: "#9ca3af", fontWeight: "600" }}>Investor Narrative: </span>
                                      <span style={{ color: "#ffffff", fontWeight: "600" }}>
                                        {marketingStrategyData.enterprise_intelligence_report.final_ai_evaluation.investor_narrative}
                                      </span>
                                    </div>
                                  </div>
                                )}
                                {marketingStrategyData.enterprise_intelligence_report.final_ai_evaluation.rationale && (
                                  <div>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <Lightbulb style={{ width: "14px", height: "14px", color: "#F6255A" }} />
                                      <span style={{ color: "#9ca3af", fontWeight: "600" }}>Rationale: </span>
                                    </div>
                                    <div style={{
                                      padding: "12px",
                                      background: "linear-gradient(135deg, rgba(246, 37, 90, 0.1) 0%, rgba(246, 37, 90, 0.05) 100%)",
                                      borderRadius: "8px",
                                      border: "1px solid rgba(246, 37, 90, 0.2)",
                                      marginTop: "8px",
                                      fontSize: "13px",
                                      color: "#ffffff",
                                      fontWeight: "500"
                                    }}>
                                      {marketingStrategyData.enterprise_intelligence_report.final_ai_evaluation.rationale}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Ads Fetch Report */}
                      {/* {marketingStrategyData?.ads_fetch_report?.length > 0 && (
                        <div style={{ marginBottom: "24px" }}>
                          <h6
                            style={{
                              fontSize: "16px",
                              fontWeight: "600",
                              marginBottom: "16px",
                              color: "#F6255A",
                            }}
                          >
                            Ads Data Collection Report
                          </h6>
                          <div className="pdf-no-break" style={{ backgroundColor: "rgba(55, 65, 81, 0.5)",
                              borderRadius: "8px",
                              padding: "16px",
                            }}
                          >
                            {marketingStrategyData.ads_fetch_report.map((report, idx) => (
                              <div className="pdf-no-break" key={idx}
                                style={{
                                  padding: "12px",
                                  backgroundColor: "rgba(55, 65, 81, 0.3)",
                                  borderRadius: "6px",
                                  marginBottom: idx < marketingStrategyData.ads_fetch_report.length - 1 ? "8px" : "0",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    marginBottom: "8px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: "600",
                                      color: "#ffffff",
                                    }}
                                  >
                                    {report.app_name}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      padding: "2px 8px",
                                      borderRadius: "4px",
                                      backgroundColor:
                                        report.has_error
                                          ? "rgba(239, 68, 68, 0.2)"
                                          : "rgba(16, 185, 129, 0.2)",
                                      color: report.has_error ? "#ef4444" : "#10b981",
                                      fontWeight: "600",
                                    }}
                                  >
                                    {report.has_error ? "Error" : "Success"}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                                    gap: "8px",
                                    fontSize: "12px",
                                  }}
                                >
                                  <div>
                                    <span style={{ color: "#9ca3af" }}>Ads: </span>
                                    <span style={{ color: "#ffffff" }}>{report.ad_count}</span>
                                  </div>
                                  <div>
                                    <span style={{ color: "#9ca3af" }}>Active: </span>
                                    <span style={{ color: "#ffffff" }}>{report.active_ad_count}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )} */}
                    </div>
                  ) : (
                    <div className="d-flex flex-column align-items-center justify-content-center py-8" style={{ minHeight: "300px" }}>
                      <div
                        style={{
                          width: "80px",
                          height: "80px",
                          borderRadius: "50%",
                          backgroundColor: "rgba(246, 37, 90, 0.1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: "20px",
                        }}
                      >
                        <Target
                          style={{
                            width: "40px",
                            height: "40px",
                            color: "#F6255A",
                          }}
                        />
                      </div>
                      <h6
                        style={{
                          fontSize: "18px",
                          color: "#ffffff",
                          fontWeight: "600",
                          marginBottom: "12px",
                        }}
                      >
                        Generate Marketing Strategy
                      </h6>
                      <p
                        style={{
                          fontSize: "14px",
                          color: "#9ca3af",
                          marginBottom: "24px",
                          textAlign: "center",
                          maxWidth: "350px",
                        }}
                      >
                        Create a comprehensive marketing strategy and campaign recommendations for your app
                      </p>
                      <button
                        className="btn btn-primary"
                        style={{
                          backgroundColor: isGeneratingMarketingStrategy === appData?.app_id ? "rgba(246, 37, 90, 0.5)" : "#F6255A",
                          border: isGeneratingMarketingStrategy === appData?.app_id ? "1px solid rgba(246, 37, 90, 0.5)" : "1px solid #F6255A",
                          color: "#ffffff",
                          padding: "12px 28px",
                          fontSize: "14px",
                          fontWeight: "500",
                          borderRadius: "8px",
                          transition: "all 0.2s ease",
                          opacity: isGeneratingMarketingStrategy === appData?.app_id ? 0.6 : 1,
                          cursor: isGeneratingMarketingStrategy === appData?.app_id ? "not-allowed" : "pointer",
                        }}
                        onClick={() => {
                          if (isGeneratingMarketingStrategy === appData?.app_id) {
                            toastr.warning("Marketing strategy is being prepared. Please wait...", "Data in Progress")
                          } else {
                            handleMarketingStrategy()
                          }
                        }}
                        onMouseEnter={e => {
                          if (isGeneratingMarketingStrategy !== appData?.app_id) {
                            e.target.style.backgroundColor = "#e01e4a"
                            e.target.style.borderColor = "#e01e4a"
                          }
                        }}
                        onMouseLeave={e => {
                          if (isGeneratingMarketingStrategy !== appData?.app_id) {
                            e.target.style.backgroundColor = "#F6255A"
                            e.target.style.borderColor = "#F6255A"
                          }
                        }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <Target style={{ width: "16px", height: "16px" }} />
                          Generate Strategy
                        </div>
                      </button>
                    </div>
                  )}
                </div>
                {marketingStrategyData && (
                  <div
                    className="modal-footer p-4"
                    style={{
                      borderTop: "1px solid #374151",
                      background: "linear-gradient(180deg, rgba(31, 41, 55, 0.5) 0%, rgba(17, 24, 39, 0.8) 100%)",
                    }}
                  >
                    <div className="d-flex gap-3 w-100">
                      <button
                        className="btn btn-secondary"
                        style={{
                          backgroundColor: "rgba(55, 65, 81, 0.5)",
                          border: "1px solid rgba(55, 65, 81, 0.8)",
                          color: "#9ca3af",
                          padding: "10px 20px",
                          fontSize: "14px",
                          fontWeight: "500",
                          borderRadius: "10px",
                          transition: "all 0.2s ease",
                          flex: 1,
                        }}
                        onClick={() => {
                          const contentToCopy = typeof marketingStrategyData === "string"
                            ? marketingStrategyData
                            : formatStrategyAsMarkdown(marketingStrategyData)
                          navigator.clipboard.writeText(contentToCopy)
                          trackEvent(ANALYTICS_EVENTS.FEATURE.AI_CMO_MARKETING_STRATEGY_COPIED, {
                            app_id: appData?.app_id,
                          })
                          setShowCopyToast(true)
                          setTimeout(() => setShowCopyToast(false), 2000)
                        }}
                        onMouseEnter={e => {
                          e.target.style.backgroundColor = "rgba(55, 65, 81, 0.8)"
                          e.target.style.borderColor = "#6b7280"
                          e.target.style.color = "#ffffff"
                        }}
                        onMouseLeave={e => {
                          e.target.style.backgroundColor = "rgba(55, 65, 81, 0.5)"
                          e.target.style.borderColor = "rgba(55, 65, 81, 0.8)"
                          e.target.style.color = "#9ca3af"
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <FileText style={{ width: "16px", height: "16px" }} />
                          Copy Strategy
                        </div>
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{
                          backgroundColor: "rgba(55, 65, 81, 0.5)",
                          border: "1px solid rgba(55, 65, 81, 0.8)",
                          color: "#9ca3af",
                          padding: "10px 20px",
                          fontSize: "14px",
                          fontWeight: "500",
                          borderRadius: "10px",
                          transition: "all 0.2s ease",
                          cursor: isGeneratingPDF ? "not-allowed" : "pointer",
                          opacity: isGeneratingPDF ? 0.6 : 1,
                          flex: 1,
                        }}
                        onClick={() => {
                          if (!isGeneratingPDF) {
                            downloadStrategyAsPDF(
                              marketingStrategyData,
                              appData?.game_info?.name || appData?.name,
                              setIsGeneratingPDF,
                            )
                            trackEvent(ANALYTICS_EVENTS.FEATURE.AI_CMO_MARKETING_STRATEGY_DOWNLOADED, {
                              app_id: appData?.app_id,
                            })
                          }
                        }}
                        onMouseEnter={e => {
                          if (!isGeneratingPDF) {
                            e.target.style.backgroundColor = "rgba(55, 65, 81, 0.8)"
                            e.target.style.borderColor = "#6b7280"
                            e.target.style.color = "#ffffff"
                          }
                        }}
                        onMouseLeave={e => {
                          e.target.style.backgroundColor = "rgba(55, 65, 81, 0.5)"
                          e.target.style.borderColor = "rgba(55, 65, 81, 0.8)"
                          e.target.style.color = "#9ca3af"
                        }}
                        disabled={isGeneratingPDF}
                      >
                        {isGeneratingPDF ? (
                          <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span
                              style={{
                                display: "inline-block",
                                width: "14px",
                                height: "14px",
                                border: "2px solid #9ca3af",
                                borderTop: "2px solid #F6255A",
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite",
                                WebkitAnimation: "spin 1s linear infinite",
                              }}
                            />
                            Generating...
                          </span>
                        ) : (
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <Download style={{ width: "16px", height: "16px" }} />
                            Download PDF
                          </div>
                        )}
                      </button>
                      <style>
                        {`
                          @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                          }
                          @keyframes pulse {
                            0%, 100% { opacity: 1; transform: scale(1); }
                            50% { opacity: 0.8; transform: scale(1.05); }
                          }
                          @keyframes skeleton-pulse {
                            0%, 100% { opacity: 0.4; }
                            50% { opacity: 0.7; }
                          }
                        `}
                      </style>
                      <button
                        className="btn btn-primary"
                        style={{
                          background: "linear-gradient(135deg, #F6255A 0%, #e01e4a 100%)",
                          border: "none",
                          color: "#ffffff",
                          padding: "10px 20px",
                          fontSize: "14px",
                          fontWeight: "600",
                          borderRadius: "10px",
                          transition: "all 0.2s ease",
                          flex: 1,
                          boxShadow: "0 4px 12px rgba(246, 37, 90, 0.3)",
                        }}
                        onClick={() => setShowMarketingStrategyModal(false)}
                        onMouseEnter={e => {
                          e.target.style.background = "linear-gradient(135deg, #e01e4a 0%, #c4183b 100%)"
                          e.target.style.transform = "translateY(-2px)"
                          e.target.style.boxShadow = "0 6px 16px rgba(246, 37, 90, 0.4)"
                        }}
                        onMouseLeave={e => {
                          e.target.style.background = "linear-gradient(135deg, #F6255A 0%, #e01e4a 100%)"
                          e.target.style.transform = "translateY(0)"
                          e.target.style.boxShadow = "0 4px 12px rgba(246, 37, 90, 0.3)"
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <X style={{ width: "16px", height: "16px" }} />
                          Close
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            </>
          )}

          {showComingSoonModal && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.8)",
                  zIndex: 1002,
                }}
                onClick={() => setShowComingSoonModal(false)}
              />
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                style={{
                  position: "fixed",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: "600px",
                  maxWidth: "90vw",
                  backgroundColor: "#1f2937",
                  border: "1px solid #374151",
                  borderLeft: "none",
                  zIndex: 1003,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
                onClick={e => e.stopPropagation()}
              >
                <div
                  className="modal-header p-4"
                  style={{
                    borderBottom: "1px solid #374151",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h5
                    className="modal-title mb-0"
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#ffffff",
                    }}
                  >
                    {comingSoonFeature}
                  </h5>
                  <button
                    className="btn btn-sm btn-link p-0"
                    style={{
                      color: "#9ca3af",
                      padding: "4px",
                    }}
                    onClick={() => setShowComingSoonModal(false)}
                  >
                    <X style={{ width: "20px", height: "20px" }} />
                  </button>
                </div>
                <div
                  className="modal-body p-4"
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      backgroundColor: "rgba(246, 37, 90, 0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "24px",
                    }}
                  >
                    <Zap
                      style={{
                        width: "40px",
                        height: "40px",
                        color: "#F6255A",
                      }}
                    />
                  </div>
                  <h3
                    style={{
                      fontSize: "24px",
                      fontWeight: "600",
                      color: "#ffffff",
                      marginBottom: "12px",
                    }}
                  >
                    Coming Soon
                  </h3>
                  <p
                    style={{
                      fontSize: "15px",
                      color: "#9ca3af",
                      lineHeight: "1.6",
                      marginBottom: "32px",
                      maxWidth: "400px",
                    }}
                  >
                    {comingSoonFeature} is currently under development. We're working hard to bring you this feature soon!
                  </p>
                  <button
                    style={{
                      backgroundColor: "#F6255A",
                      border: "1px solid #F6255A",
                      color: "#ffffff",
                      padding: "12px 32px",
                      fontSize: "15px",
                      fontWeight: "500",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => setShowComingSoonModal(false)}
                    onMouseEnter={e => {
                      e.target.style.backgroundColor = "#e01e4a"
                      e.target.style.borderColor = "#e01e4a"
                    }}
                    onMouseLeave={e => {
                      e.target.style.backgroundColor = "#F6255A"
                      e.target.style.borderColor = "#F6255A"
                    }}
                  >
                    Got it
                  </button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Copy Toast Notification */}
        {showCopyToast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            style={{
              position: "fixed",
              bottom: "20px",
              right: "20px",
              backgroundColor: "#10b981",
              color: "#ffffff",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "500",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M13.5 3.5H6.5C6.22386 3.5 6 3.72386 6 4V11C6 11.2761 6.22386 11.5 6.5 11.5H13.5C13.7761 11.5 14 11.2761 14 11V4C14 3.72386 13.7761 3.5 13.5 3.5Z"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 6.5H2.5C2.22386 6.5 2 6.72386 2 7V12.5C2 12.7761 2.22386 13 2.5 13H8C8.27614 13 8.5 12.7761 8.5 12.5V11"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Copied to clipboard!
          </motion.div>
        )}
      </div>

      {/* Scan upgrade modal */}
      {showScanUpgradeModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowScanUpgradeModal(false)}
        >
          <div
            style={{
              background: "#21222D",
              border: "1px solid #353748",
              borderRadius: "16px",
              padding: "36px 32px",
              maxWidth: "420px",
              width: "90%",
              textAlign: "center",
              position: "relative",
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setShowScanUpgradeModal(false)}
              style={{
                position: "absolute",
                top: "14px",
                right: "16px",
                background: "none",
                border: "none",
                color: "#878a99",
                fontSize: "20px",
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              ×
            </button>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>🔒</div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: "18px", marginBottom: "8px" }}>
              Scan limit reached
            </div>
            <div style={{ color: "#878a99", fontSize: "14px", lineHeight: 1.6, marginBottom: "24px" }}>
              You've used all your app scans on your current plan. Upgrade to scan more apps and unlock full AI CMO features.
            </div>
            <a
              href="/profile/plans"
              onClick={() => {
                trackEvent(ANALYTICS_EVENTS.CONVERSION.PLAN_UPGRADE_STARTED, {
                  trigger: "app_scan_limit",
                })
              }}
              style={{
                display: "inline-block",
                background: "#F6255A",
                color: "#fff",
                borderRadius: "8px",
                padding: "11px 28px",
                fontSize: "14px",
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              Upgrade Plan
            </a>
          </div>
        </div>
      )}

      {/* Name search — same shared modal + platform tabs Keyword Suggestions, UGC
          Explorer, and Influencer Explorer all use, instead of the bespoke
          multi-platform fan-out dropdown this used to have. Selecting an app
          reconstructs a scan URL and feeds it straight into the existing scan
          pipeline (handleSearchResultClick / handleScanWithUrl), same as before. */}
      <KeywordSuggestionsDetailsModal
        show={showSearchModal}
        handleClose={() => setShowSearchModal(false)}
        title="Search Apps"
        searchTerm={url}
        onSearchChange={e => setUrl(e.target.value)}
        onSearchClick={() => {}}
        onSelectApp={handleSearchResultClick}
        onUpgrade={() => { setShowSearchModal(false); setShowSearchUpgradeModal(true) }}
        selectButtonLabel="Scan This App"
      />

      {/* Search upgrade modal — same visual pattern as the scan upgrade modal above,
          kept separate since it's a different limit (search vs. scan) with its own copy. */}
      {showSearchUpgradeModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowSearchUpgradeModal(false)}
        >
          <div
            style={{
              background: "#21222D",
              border: "1px solid #353748",
              borderRadius: "16px",
              padding: "36px 32px",
              maxWidth: "420px",
              width: "90%",
              textAlign: "center",
              position: "relative",
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setShowSearchUpgradeModal(false)}
              style={{
                position: "absolute",
                top: "14px",
                right: "16px",
                background: "none",
                border: "none",
                color: "#878a99",
                fontSize: "20px",
                cursor: "pointer",
                lineHeight: 1,
              }}
            >
              ×
            </button>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>🔒</div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: "18px", marginBottom: "8px" }}>
              Search limit reached
            </div>
            <div style={{ color: "#878a99", fontSize: "14px", lineHeight: 1.6, marginBottom: "24px" }}>
              Upgrade your plan to search and scan more apps.
            </div>
            <a
              href="/profile/plans"
              onClick={() => {
                trackEvent(ANALYTICS_EVENTS.CONVERSION.PLAN_UPGRADE_STARTED, {
                  trigger: "app_search_limit",
                })
              }}
              style={{
                display: "inline-block",
                background: "#F6255A",
                color: "#fff",
                borderRadius: "8px",
                padding: "11px 28px",
                fontSize: "14px",
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              Upgrade Plan
            </a>
          </div>
        </div>
      )}
    </BlurUpgradeContext.Provider>
  )
}

export default AppScanner
